import React, { createElement, useState, useEffect, useCallback, useRef } from 'react';
import { Chess } from 'chess.js';
import { Chessboard } from 'react-chessboard';
import { toast } from 'react-toastify';
import { Room, Track } from 'livekit-client';
import { useLocation } from 'react-router-dom';
import { getPuzzlesForProvider, getPuzzlesForSubscription, markPuzzleAsTaught, unmarkPuzzleAsTaught } from '../../../../../services/chessPuzzleService';
import { RootState, useAppSelector } from '../../../../../state/state';
import { SubscriptionDocWithId } from '../../../types/SubscriptionType';
import { getContextualHint, getHintProgress } from '../../../../../utils/puzzleHintGenerator';
import { selectActiveRole, selectOriginalUID } from '../../../../../state/UserStatesV2/userSliceV2';
import { createCollaborativeSession, joinCollaborativeSession, CollaborativePuzzleSession, getActiveSessions, subscribeToSession } from '../../../../../services/collaborativePuzzleService';
import CollaborativePuzzleView from './CollaborativePuzzleView';
import { chessSounds } from '../../../../../utils/chessSounds';
import type { StockfishAnalysis } from '../../../../../services/stockfishApi';
import { useStockfishEngine } from '../../../../../hooks/useStockfishEngine';

type UserRole = 'teacher' | 'student';
const PUZZLE_MODE = 'puzzle' as const;
const TEACHING_MODE = 'teaching' as const;
type ChessMode = typeof PUZZLE_MODE | typeof TEACHING_MODE;

// Type guard functions
function isTeachingMode(mode: ChessMode): mode is typeof TEACHING_MODE {
    return mode === TEACHING_MODE;
}

function isPuzzleMode(mode: ChessMode): mode is typeof PUZZLE_MODE {
    return mode === PUZZLE_MODE;
}

function isTeacher(role: UserRole): role is 'teacher' {
    return role === 'teacher';
}

function isStudent(role: UserRole): role is 'student' {
    return role === 'student';
}

interface ChessPuzzle {
    id: string;
    fen: string;
    moves: string;
    rating: number;
    
    themes: string[];
    description?: string;
    hint?: string;
    complexity?: 'beginner' | 'intermediate' | 'advanced';
    category?: string;
    source?: 'lichess' | 'custom' | 'local';
    lichessId?: string;
}

interface ChequeredChessProps {
    subscriptionId: string;
    subscriptionName: string;
    userRole?: UserRole;
    isInMeeting?: boolean; // Whether user is currently in a LiveKit meeting
    participantName?: string; // Name of the current participant
    room?: Room | null; // LiveKit room instance (passed from wrapper)
    disableTeachingMode?: boolean; // When true (e.g., in LiveKit), hide teaching and force puzzle
    initialPuzzle?: ChessPuzzle | null; // Initial puzzle to load (from parent component)
    initialMode?: 'puzzle' | 'teaching'; // Initial mode to set
    onPuzzleSolved?: () => void; // Callback when puzzle is solved
    serviceProviderId?: string; // Service provider ID for loading puzzles from current class
    initialPosition?: string | null; // Initial FEN position to load (for teaching mode)
    teachingBoardVariant?: 'standard' | 'neo'; // NEO = lichess-style editor UX
}

interface ChessMove {
    type: 'move' | 'puzzle' | 'position';
    from: string;
    to: string;
    san: string;
    fen: string;
    playerName: string;
    timestamp: number;
    puzzleId?: string;
}

interface StudentPosition {
    studentName: string;
    fen: string;
    lastMove: string;
    timestamp: number;
    completed?: boolean;
    totalMoves?: number;
}

/** Lichess board editor–style palette (https://lichess.org/editor) */
const LICHESS_PAGE_BG = '#262421';
const LICHESS_PANEL_BG = '#302e2b';
const LICHESS_PANEL_BORDER = '#3d3a36';
const LICHESS_TEXT = '#bababa';
const LICHESS_TEXT_MUTED = '#8c8b88';
const LICHESS_ACCENT = '#81c14b';
/** Lichess default “brown” board (theme=brown, same family as lichess.org/editor screenshot export) */
const LICHESS_BOARD_LIGHT = '#f0d9b5';
const LICHESS_BOARD_DARK = '#b58863';
/** Spare row chrome — lila `_spare.scss` uses $c-font-dimmer + inset shadow */
const LICHESS_SPARE_BAR_BG = '#5c5b58';

function fenToLichessAnalysisUrl(fen: string): string {
    return `https://lichess.org/analysis/${fen.trim().replace(/\s+/g, '_')}`;
}

function parseCastlingFlags(fen: string): { K: boolean; Q: boolean; k: boolean; q: boolean } {
    const parts = fen.trim().split(/\s+/);
    const c = parts[2] ?? '-';
    return {
        K: c.includes('K'),
        Q: c.includes('Q'),
        k: c.includes('k'),
        q: c.includes('q')
    };
}

function withFenCastling(fen: string, flags: { K: boolean; Q: boolean; k: boolean; q: boolean }): string {
    const parts = fen.trim().split(/\s+/);
    let cast = '';
    if (flags.K) cast += 'K';
    if (flags.Q) cast += 'Q';
    if (flags.k) cast += 'k';
    if (flags.q) cast += 'q';
    parts[2] = cast || '-';
    return parts.join(' ');
}

function withFenSideToMove(fen: string, color: 'w' | 'b'): string {
    const parts = fen.trim().split(/\s+/);
    parts[1] = color;
    return parts.join(' ');
}

/** Lichess editor URL (same path pattern as lichess.org/editor/<fen>) */
function fenToLichessEditorUrl(fen: string): string {
    return `https://lichess.org/editor/${fen.trim().replace(/\s+/g, '_')}`;
}

function withFenEnPassant(fen: string, epSquare: string): string {
    const parts = fen.trim().split(/\s+/);
    const v = epSquare === '' || epSquare === '-' ? '-' : epSquare;
    parts[3] = v;
    return parts.join(' ');
}

/** Human-readable reason why a move from → to is illegal in the given position. */
function explainIllegalMove(game: Chess, from: string, to: string): string {
    const PIECE_NAME: Record<string, string> = {
        p: 'pawn', n: 'knight', b: 'bishop', r: 'rook', q: 'queen', k: 'king',
    };
    const turn = game.turn();
    const turnName = turn === 'w' ? 'White' : 'Black';
    const src = game.get(from as Parameters<Chess['get']>[0]);
    const dst = game.get(to as Parameters<Chess['get']>[0]);

    if (!src) return `No piece on ${from.toUpperCase()}.`;

    // Moving opponent's piece
    if (src.color !== turn) {
        const movedSide = src.color === 'w' ? 'White' : 'Black';
        return `It's ${turnName}'s turn — can't move a ${movedSide} ${PIECE_NAME[src.type]}.`;
    }

    // Capturing own piece
    if (dst && dst.color === src.color) {
        return `Can't capture your own ${PIECE_NAME[dst.type]} on ${to.toUpperCase()}.`;
    }

    // Must get out of check
    if (game.inCheck()) {
        return `${turnName}'s king is in check — you must resolve the check first.`;
    }

    // Pawn-specific
    if (src.type === 'p') {
        const fromFile = from.charCodeAt(0);
        const toFile = to.charCodeAt(0);
        if (Math.abs(fromFile - toFile) === 1 && !dst) {
            return `Pawns can only capture diagonally when an enemy piece is there.`;
        }
        if (fromFile === toFile && dst) {
            return `Pawns can't capture straight ahead.`;
        }
        const fromRank = parseInt(from[1]);
        const toRank = parseInt(to[1]);
        const dir = src.color === 'w' ? 1 : -1;
        if ((toRank - fromRank) * dir <= 0) {
            return `Pawns can only move forward.`;
        }
    }

    return `The ${PIECE_NAME[src.type] ?? src.type} on ${from.toUpperCase()} can't move to ${to.toUpperCase()}.`;
}

/** En-passant file options — matches lila `view.ts` (rank 3 if black to move, else 6). */
function enPassantFileOptions(turn: 'w' | 'b'): string[] {
    const rank = turn === 'b' ? '3' : '6';
    return ['', ...'abcdefgh'.split('').map((f) => f + rank)];
}

const ChequeredChess: React.FC<ChequeredChessProps> = ({
    subscriptionId,
    // subscriptionName,  // Reserved for future use
    userRole,
    isInMeeting = false,
    participantName = 'Player',
    room = null,
    disableTeachingMode = false,
    initialPuzzle = null,
    initialMode,
    onPuzzleSolved,
    serviceProviderId,
    initialPosition = null,
    teachingBoardVariant = 'standard'
}) => {
    const isNeoTeachingBoard = teachingBoardVariant === 'neo';
    const location = useLocation();
    const [game, setGame] = useState(new Chess());
    // Custom FEN for teaching mode (bypasses chess.js validation)
    const [customFenForTeaching, setCustomFenForTeaching] = useState<string | null>(null);
    // Enforce validations in teaching mode (default: false/off)
    const [enforceValidations, setEnforceValidations] = useState(false);
    const [showGuide, setShowGuide] = useState(false);
    const [currentPuzzle, setCurrentPuzzle] = useState<ChessPuzzle | null>(null);
    
    // Show guide on first visit
    useEffect(() => {
        const hasSeenGuide = localStorage.getItem('chessBoardGuideSeen');
        if (!hasSeenGuide) {
            setTimeout(() => {
                setShowGuide(true);
                localStorage.setItem('chessBoardGuideSeen', 'true');
            }, 1000); // Show after 1 second
        }
    }, []);
    const [puzzleProgress, setPuzzleProgress] = useState<string[]>([]);
    const [mainZoomLevel, setMainZoomLevel] = useState(100);
    const [boardWidthPx, setBoardWidthPx] = useState<number>(700); // Increased for better visibility at 100% zoom
    const boardInnerPaddingPx = 16; // matches p-4
    const effectiveBoardWidth = Math.max(240, boardWidthPx - 2 * boardInnerPaddingPx);
    const studentCardMaxWidth = Math.min(boardWidthPx, 800); // Increased max width
    const studentBoardWidth = Math.max(240, Math.min(effectiveBoardWidth, studentCardMaxWidth - 48));
    const [studentZoomLevels, setStudentZoomLevels] = useState<{ [key: string]: number }>({}); // Per-student zoom levels
    const boardContainerRef = useRef<HTMLDivElement>(null);
    /** Ref on the left flex-1 column — used by the resize observer so it measures
     *  a stable container whose size doesn't depend on the board itself. */
    const boardAreaRef = useRef<HTMLDivElement>(null);
    const dragPreviewRef = useRef<HTMLImageElement | null>(null);
    const screenShareTrackRef = useRef<MediaStreamTrack | null>(null);
    const canvasMirrorRef = useRef<HTMLCanvasElement | null>(null);
    const canvasMirrorIntervalRef = useRef<number | null>(null);
    const boardViewBroadcastIntervalRef = useRef<number | null>(null);
    const [isCorrectMove, setIsCorrectMove] = useState<boolean | null>(null);
    const [puzzleCompleted, setPuzzleCompleted] = useState(false);
    const [boardShake, setBoardShake] = useState(false);
    const [soundEnabled, setSoundEnabled] = useState(() => {
        // Load sound preference from localStorage
        const saved = localStorage.getItem('chessSoundEnabled');
        return saved !== null ? saved === 'true' : true; // Default to enabled
    });
    const [customFen, setCustomFen] = useState('');
    const [showFenInput, setShowFenInput] = useState(false);
    const [boardOrientation, setBoardOrientation] = useState<'white' | 'black'>('white');
    const [isBoardShared, setIsBoardShared] = useState(false);
    const [studentPositions, setStudentPositions] = useState<Map<string, StudentPosition>>(new Map());
    const [shareMode, setShareMode] = useState<'collaboration' | 'view'>('collaboration');
    const [showHint, setShowHint] = useState(false);
    const isStandaloneChequered = typeof window !== 'undefined' && window.location.pathname.includes('/chequered/');
    const computedInitialMode: ChessMode = initialMode 
        ? (initialMode === 'teaching' ? TEACHING_MODE : PUZZLE_MODE)
        : (disableTeachingMode ? PUZZLE_MODE : (isStandaloneChequered ? TEACHING_MODE : PUZZLE_MODE));
    const [mode, setMode] = useState<ChessMode>(computedInitialMode);
    const isNeoEditorUi = isNeoTeachingBoard && mode === TEACHING_MODE;
    const [originalMode, setOriginalMode] = useState<ChessMode>(computedInitialMode);
    // Store state before sharing to restore when stopping
    const [stateBeforeShare, setStateBeforeShare] = useState<{
        puzzle: ChessPuzzle | null;
        boardFen: string;
        mode: ChessMode;
    } | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedComplexity, setSelectedComplexity] = useState<'all' | 'beginner' | 'intermediate' | 'advanced'>('all');
    const [selectedCategory, setSelectedCategory] = useState<'all' | 'tactics' | 'endgame' | 'opening' | 'middlegame'>('all');
    const [showPuzzleCreator, setShowPuzzleCreator] = useState(false);
    const [customPuzzles, setCustomPuzzles] = useState<ChessPuzzle[]>([]);
    const [showBulkImport, setShowBulkImport] = useState(true);
    const [puzzleModeTab, setPuzzleModeTab] = useState<'library' | 'import' | 'create'>('library');
    const [firestorePuzzles, setFirestorePuzzles] = useState<ChessPuzzle[]>([]);
    const [loadingPuzzles, setLoadingPuzzles] = useState(false);
    // Removed bookmarks - not needed for progress tracking in classes
    const [newPuzzle, setNewPuzzle] = useState({
        description: '',
        hint: '',
        solution: '',
        complexity: 'beginner' as 'beginner' | 'intermediate' | 'advanced',
        category: 'tactics' as 'tactics' | 'endgame' | 'opening' | 'middlegame',
        themes: ''
    });
    const [teachingModeHistory, setTeachingModeHistory] = useState<string[]>([]);
    // SAN move log for the NEO analysis move-list display
    const [neoMoveLog, setNeoMoveLog] = useState<string[]>([]);
    const [neoMoveUndoneLog, setNeoMoveUndoneLog] = useState<string[]>([]);
    const [teachingModeRedoHistory, setTeachingModeRedoHistory] = useState<string[]>([]);
    const [showPiecePicker, setShowPiecePicker] = useState(false);
    const [selectedPieceType, setSelectedPieceType] = useState<string | null>(null);
    const [isPieceSetupMode, setIsPieceSetupMode] = useState(false);
    const [selectedTrayPiece, setSelectedTrayPiece] = useState<{piece: string, color: string} | null>(null);
    
    // Drawing/Annotation state
    const [rightClickedSquares, setRightClickedSquares] = useState<{[square: string]: {backgroundColor: string}}>({});
    const [drawingColor, setDrawingColor] = useState<'green' | 'red' | 'blue' | 'yellow'>('green');
    // Default drawing tool.
    // Standard teaching starts with Pen; NEO starts with Arrow to mimic lichess editor behavior.
    const [drawingMode, setDrawingMode] = useState<'highlight' | 'arrow' | 'pen'>(
        computedInitialMode === TEACHING_MODE
            ? (isNeoTeachingBoard ? 'arrow' : 'pen')
            : 'highlight'
    );
    const [arrowStart, setArrowStart] = useState<string | null>(null);
    const [isDrawingArrow, setIsDrawingArrow] = useState(false);
    const [arrowEnd, setArrowEnd] = useState<string | null>(null);
    const [isPenMode, setIsPenMode] = useState(false); // Track if pen is actively drawing
    const [interactionMode, setInteractionMode] = useState<'move' | 'select' | 'delete'>('move'); // Lichess-style interaction modes
    const [selectedSquares, setSelectedSquares] = useState<Set<string>>(new Set()); // For selection mode
    /** Lichess spare row: pointer | pieces | trash; 'none' when a piece type is selected from tray */
    const [neoEditorSpare, setNeoEditorSpare] = useState<'pointer' | 'trash' | 'none'>('pointer');
    const [neoFenField, setNeoFenField] = useState('');
    // NEO inline Stockfish analysis
    const [showNeoAnalysis, setShowNeoAnalysis] = useState(false);
    // Kept for type reference only; legacy API path no longer used in NEO panel
    const _unusedAnalysisType: StockfishAnalysis | null = null; void _unusedAnalysisType;
    const [neoAnalysisFen, setNeoAnalysisFen] = useState<string>('');
    const [neoAnalysisBestMoveArrow, setNeoAnalysisBestMoveArrow] = useState<[string, string] | null>(null);
    // Local Stockfish engine (UCI, browser WASM)
    const sfEngine = useStockfishEngine();
    const [arrows, setArrows] = useState<Array<[string, string, string]>>([]);
    const [hintArrows, setHintArrows] = useState<Array<[string, string, string]>>([]);
    const subscriptionList: SubscriptionDocWithId[] = useAppSelector((state: RootState) => state.subscriptionTableSlice?.data ?? []);
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(subscriptionId);
    const activeSubscriptionId = selectedSubscriptionId || subscriptionId;
    const activeRole = useAppSelector(selectActiveRole);
    const loggedInUserId = useAppSelector(selectOriginalUID);
    const loggedInUserName = activeRole?.displayName || participantName || 'Player';
    const [collaborativeSessionId, setCollaborativeSessionId] = useState<string | null>(null);
    const [showCollaborativeView, setShowCollaborativeView] = useState(false);
    const [activeSessions, setActiveSessions] = useState<{ [sessionId: string]: CollaborativePuzzleSession }>({});
    const unsubscribeActiveSessionsRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        setSelectedSubscriptionId(subscriptionId);
    }, [subscriptionId]);

    const currentFenString = game.fen();
    useEffect(() => {
        setNeoFenField(currentFenString);
        // Clear stale legacy analysis arrow when the position changes
        if (currentFenString !== neoAnalysisFen) {
            setNeoAnalysisBestMoveArrow(null);
        }
    }, [currentFenString]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto re-analyse with local engine when position changes while panel is open
    const showNeoAnalysisRef = useRef(showNeoAnalysis);
    showNeoAnalysisRef.current = showNeoAnalysis;
    useEffect(() => {
        if (showNeoAnalysisRef.current && sfEngine.ready) {
            sfEngine.startAnalysis(currentFenString);
        }
    }, [currentFenString]); // eslint-disable-line react-hooks/exhaustive-deps

    // Subscribe to active collaborative sessions
    useEffect(() => {
        if (activeSubscriptionId) {
            unsubscribeActiveSessionsRef.current = getActiveSessions(activeSubscriptionId, (sessions) => {
                setActiveSessions(sessions);
            });
        }

        return () => {
            if (unsubscribeActiveSessionsRef.current) {
                unsubscribeActiveSessionsRef.current();
            }
        };
    }, [activeSubscriptionId]);

    // Handle joining via shared link (URL parameters)
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const collabSessionId = urlParams.get('collabSession');
        const linkSubscriptionId = urlParams.get('subscription');

        if (collabSessionId && linkSubscriptionId && linkSubscriptionId === activeSubscriptionId && !showCollaborativeView) {
            // Check if session exists and join
            const unsubscribe = subscribeToSession(collabSessionId, activeSubscriptionId, async (session) => {
                if (session && session.status === 'active' && session.puzzle) {
                    unsubscribe();
                    try {
                        // Join the session
                        await joinCollaborativeSession(
                            collabSessionId,
                            activeSubscriptionId,
                            loggedInUserId,
                            loggedInUserName,
                            userRole || 'student'
                        );
                        // Set up the collaborative view
                        setCollaborativeSessionId(collabSessionId);
                        setCurrentPuzzle(session.puzzle);
                        setShowCollaborativeView(true);
                        toast.success('Joined collaborative puzzle session!');
                        
                        // Clean up URL parameters
                        const newUrl = window.location.pathname;
                        window.history.replaceState({}, document.title, newUrl);
                    } catch (error) {
                        console.error('Error joining session from link:', error);
                        toast.error('Failed to join session. It may have ended.');
                        // Clean up URL parameters even on error
                        const newUrl = window.location.pathname;
                        window.history.replaceState({}, document.title, newUrl);
                    }
                } else if (session && session.status !== 'active') {
                    unsubscribe();
                    toast.error('This session has ended.');
                    // Clean up URL parameters
                    const newUrl = window.location.pathname;
                    window.history.replaceState({}, document.title, newUrl);
                }
            });

            // Timeout after 5 seconds if session not found
            const timeout = setTimeout(() => {
                unsubscribe();
                toast.error('Session not found or expired.');
                // Clean up URL parameters
                const newUrl = window.location.pathname;
                window.history.replaceState({}, document.title, newUrl);
            }, 5000);

            return () => {
                clearTimeout(timeout);
                unsubscribe();
            };
        }
    }, [location.search, activeSubscriptionId, loggedInUserId, loggedInUserName, userRole, showCollaborativeView]);
    
    // Preserve teaching mode state (board position, annotations) on refresh
    useEffect(() => {
        if (mode === TEACHING_MODE) {
            try {
                const storageKey = `chequered_teaching_state_${subscriptionId || 'default'}`;
                const savedState = sessionStorage.getItem(storageKey);
                if (savedState) {
                    const parsed = JSON.parse(savedState);
                    if (parsed.fen) {
                        try {
                            const restoredGame = new Chess(parsed.fen);
                            setGame(restoredGame);
                        } catch (e) {
                            // If FEN is invalid, use buildBoardState if we have board state
                            if (parsed.boardState) {
                                // Restore using custom FEN
                                setCustomFenForTeaching(parsed.fen);
                            }
                        }
                    }
                    if (parsed.arrows) {
                        setArrows(parsed.arrows);
                    }
                    if (parsed.highlights) {
                        setRightClickedSquares(parsed.highlights);
                    }
                    if (parsed.boardOrientation) {
                        setBoardOrientation(parsed.boardOrientation);
                    }
                }
            } catch (error) {
                console.error('Error restoring teaching mode state:', error);
            }
        }
    }, [mode, subscriptionId]);
    
    // Load initial position from prop (for preset positions)
    useEffect(() => {
        if (initialPosition && mode === TEACHING_MODE) {
            try {
                const testGame = new Chess(initialPosition);
                setGame(testGame);
                setCustomFenForTeaching(null); // Clear custom FEN if using valid FEN
            } catch (e) {
                // If FEN is invalid for chess.js, use custom FEN for teaching mode
                setCustomFenForTeaching(initialPosition);
            }
        }
    }, [initialPosition, mode]);
    
    // Save teaching mode state whenever it changes
    useEffect(() => {
        if (mode === TEACHING_MODE) {
            try {
                const storageKey = `chequered_teaching_state_${subscriptionId || 'default'}`;
                const stateToSave = {
                    fen: game.fen(),
                    arrows: arrows,
                    highlights: rightClickedSquares,
                    boardOrientation: boardOrientation,
                    timestamp: Date.now()
                };
                sessionStorage.setItem(storageKey, JSON.stringify(stateToSave));
            } catch (error) {
                console.error('Error saving teaching mode state:', error);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, game, arrows, rightClickedSquares, boardOrientation, subscriptionId]);
    
    // ESC key handler to cancel selections
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && mode === TEACHING_MODE) {
                if (selectedTrayPiece || arrowStart) {
                    setSelectedTrayPiece(null);
                    setArrowStart(null);
                    toast.info('✅ Selection cancelled');
                }
            }
        };
        
        window.addEventListener('keydown', handleEscape);
        return () => window.removeEventListener('keydown', handleEscape);
    }, [mode, selectedTrayPiece, arrowStart]);

    // Reset puzzle state when switching subscription selection
    useEffect(() => {
        setCurrentPuzzle(null);
        setPuzzleProgress([]);
        setPuzzleCompleted(false);
        setFirestorePuzzles([]);
    }, [activeSubscriptionId]);

    // Load puzzles from Firestore
    // Separate effect for initial load (only when entering meeting or mode changes)
    useEffect(() => {
        const loadFirestorePuzzles = async () => {
            if (mode !== 'puzzle') return;
            
            // In meeting mode, load puzzles from current class (subscription)
            // Otherwise, load from main library
            if (isInMeeting && serviceProviderId && activeSubscriptionId) {
                const componentStartTime = performance.now();
                console.log('⏱️ [COMPONENT] Starting puzzle load in meeting mode', {
                    serviceProviderId,
                    subscriptionId: activeSubscriptionId,
                    participantName,
                    timestamp: new Date().toISOString()
                });
                
                setLoadingPuzzles(true);
                try {
                    const step1Time = performance.now();
                    console.log('⏱️ [COMPONENT] Step 1: Calling getPuzzlesForSubscription', {
                        elapsed: `${(step1Time - componentStartTime).toFixed(2)}ms`
                    });
                    
                    // Load ALL puzzles from current class (no filters on initial load for better performance)
                    // Filters will be applied client-side
                    const puzzles = await getPuzzlesForSubscription(serviceProviderId, activeSubscriptionId, {
                        limit: 200 // Limit to 200 puzzles max for performance
                    });
                    
                    const step2Time = performance.now();
                    console.log('⏱️ [COMPONENT] Step 2: Puzzles received, setting state', {
                        puzzleCount: puzzles.length,
                        elapsed: `${(step2Time - step1Time).toFixed(2)}ms`,
                        totalElapsed: `${(step2Time - componentStartTime).toFixed(2)}ms`
                    });
                    
                    setFirestorePuzzles(puzzles);
                    
                    const step3Time = performance.now();
                    console.log('⏱️ [COMPONENT] Step 3: Skipping bookmark load in meeting mode (performance optimization)', {
                        elapsed: `${(step3Time - step2Time).toFixed(2)}ms`,
                        reason: 'Bookmarks not needed for puzzle sharing in meetings'
                    });
                    
                    // Bookmarks removed - not needed for progress tracking in classes
                    
                    const step4Time = performance.now();
                    console.log('⏱️ [COMPONENT] Step 4: Bookmarks skipped (meeting mode)', {
                        elapsed: `${(step4Time - step3Time).toFixed(2)}ms`,
                        totalElapsed: `${(step4Time - componentStartTime).toFixed(2)}ms`
                    });
                    
                    const componentEndTime = performance.now();
                    const totalComponentTime = componentEndTime - componentStartTime;
                    console.log('⏱️ [COMPONENT] ✅ Puzzle loading completed', {
                        totalPuzzles: puzzles.length,
                        bookmarksSkipped: true,
                        totalTime: `${totalComponentTime.toFixed(2)}ms (${(totalComponentTime / 1000).toFixed(2)}s)`,
                        note: 'Bookmarks skipped in meeting mode for performance'
                    });
                } catch (error) {
                    const componentEndTime = performance.now();
                    const totalComponentTime = componentEndTime - componentStartTime;
                    console.error('❌ [COMPONENT] Error loading puzzles from current class:', {
                        error,
                        totalTime: `${totalComponentTime.toFixed(2)}ms (${(totalComponentTime / 1000).toFixed(2)}s)`,
                        serviceProviderId,
                        subscriptionId: activeSubscriptionId
                    });
                    // Fallback to empty array if subscription puzzles don't exist
                    setFirestorePuzzles([]);
                } finally {
                    setLoadingPuzzles(false);
                }
            } else if (!isInMeeting && serviceProviderId) {
                // Not in meeting - load from main library
                setLoadingPuzzles(true);
                try {
                    const puzzles = await getPuzzlesForProvider(serviceProviderId, {
                        limit: 100
                    });
                    
                    console.log(`📚 Loaded ${puzzles.length} puzzles from main library`);
                    setFirestorePuzzles(puzzles);
                } catch (error) {
                    console.error('Error loading puzzles from library:', error);
                    setFirestorePuzzles([]);
                } finally {
                    setLoadingPuzzles(false);
                }
            }
        };
        
        loadFirestorePuzzles();
        // Only reload when entering/exiting meeting, mode changes, or IDs change - NOT on filter changes
    }, [mode, isInMeeting, serviceProviderId, activeSubscriptionId, participantName]);

    // Expanded puzzle library with hints and categories
    const samplePuzzles: ChessPuzzle[] = [
        // Beginner Puzzles
        {
            id: 'b1',
            fen: 'r1bqkb1r/pppp1ppp/2n2n2/4p2Q/2B1P3/8/PPPP1PPP/RNB1K1NR w KQkq - 4 4',
            moves: 'h5f7 e8f7 c4d5',
            rating: 1200,
            themes: ['fork', 'sacrifice'],
            description: 'Scholar\'s Mate Pattern - Find the winning sacrifice!',
            hint: 'Look for a check that wins material. The king is exposed!',
            complexity: 'beginner',
            category: 'tactics'
        },
        {
            id: 'b2',
            fen: 'r1bqkbnr/pppp1ppp/2n5/4p3/2B1P3/5Q2/PPPP1PPP/RNB1K1NR w KQkq - 4 4',
            moves: 'f3f7',
            rating: 1100,
            themes: ['checkmate', 'tactics'],
            description: 'Scholar\'s Mate - Deliver checkmate in one!',
            hint: 'The f7 pawn is the weakest point in Black\'s position.',
            complexity: 'beginner',
            category: 'tactics'
        },
        // Intermediate Puzzles
        {
            id: 'i1',
            fen: 'r1bqk2r/ppp2ppp/2n5/3np3/1b2P3/2NP1N2/PPP2PPP/R1BQKB1R w KQkq - 0 6',
            moves: 'd3d4 d5f4 d4e5',
            rating: 1500,
            themes: ['pin', 'tactics'],
            description: 'Exploit the pin! Find the tactical blow.',
            hint: 'The knight on d5 is pinned. How can you exploit this?',
            complexity: 'intermediate',
            category: 'tactics'
        },
        {
            id: 'i2',
            fen: 'r2qkb1r/ppp2ppp/2n2n2/3pp3/2B1P3/2N2N2/PPPP1PPP/R1BQK2R w KQkq - 0 6',
            moves: 'c4f7 e8f7 c3d5',
            rating: 1550,
            themes: ['sacrifice', 'fork'],
            description: 'Knight fork after sacrifice',
            hint: 'A piece sacrifice can win the queen. Think about knight forks!',
            complexity: 'intermediate',
            category: 'tactics'
        },
        // Advanced Puzzles
        {
            id: 'a1',
            fen: '2kr3r/ppp2ppp/4p3/3pP3/3P4/2P5/PP3PPP/2KR3R b - - 0 14',
            moves: 'd8d4 c3d4 h8c8',
            rating: 1800,
            themes: ['endgame', 'rook'],
            description: 'Rook endgame - Find the winning sequence!',
            hint: 'Activate your rooks. The exposed king is the key.',
            complexity: 'advanced',
            category: 'endgame'
        },
        {
            id: 'a2',
            fen: '6k1/5ppp/8/8/8/2B5/5PPP/6K1 w - - 0 1',
            moves: 'c3f6 g8h8 f6g7',
            rating: 1900,
            themes: ['endgame', 'bishop'],
            description: 'Bishop and pawn endgame',
            hint: 'Zugzwang! Force the king into a bad position.',
            complexity: 'advanced',
            category: 'endgame'
        },
        // Opening Puzzles
        {
            id: 'o1',
            fen: 'rnbqkb1r/pppp1ppp/5n2/4p3/4P3/5N2/PPPP1PPP/RNBQKB1R w KQkq - 4 3',
            moves: 'f3e5',
            rating: 1300,
            themes: ['opening', 'tactics'],
            description: 'Petrov Defense Trap',
            hint: 'The center pawn is hanging. Or is it a trap?',
            complexity: 'beginner',
            category: 'opening'
        },
        {
            id: 'o2',
            fen: 'r1bqkbnr/pppp1ppp/2n5/4p3/2B1P3/5N2/PPPP1PPP/RNBQK2R b KQkq - 3 3',
            moves: 'f8c5',
            rating: 1250,
            themes: ['opening', 'development'],
            description: 'Italian Game - Best developing move',
            hint: 'Develop with tempo! Attack the weak f2 square.',
            complexity: 'beginner',
            category: 'opening'
        },
        // Multi-Step Puzzles
        {
            id: 'ms1',
            fen: 'r1bqkb1r/pppp1ppp/2n2n2/4p2Q/2B1P3/8/PPPP1PPP/RNB1K1NR w KQkq - 4 4',
            moves: 'h5f7 e8f7 b4f8',
            rating: 1600,
            themes: ['sacrifice', 'queen', 'multi-step'],
            description: 'Three-move combination: Queen sacrifice → King check → Winning the game!',
            hint: 'Start with a queen sacrifice on f7. The king must take, then check with the bishop!',
            complexity: 'intermediate',
            category: 'tactics'
        },
        {
            id: 'ms2',
            fen: '2kr1b1r/pp1npppp/2p1bn2/q7/3PN3/1PN1BP2/P2Q1PPP/2KR1B1R w - - 0 12',
            moves: 'd4c6 b7c6 b3d5 c6d5 d2d5',
            rating: 1700,
            themes: ['fork', 'tactics', 'multi-step'],
            description: 'Five-move tactical sequence: Knight fork → Capture → Knight fork → Capture → Central control',
            hint: 'Start with a knight fork on c6. After the capture, fork again with the other knight!',
            complexity: 'intermediate',
            category: 'tactics'
        },
        {
            id: 'ms3',
            fen: 'r3k2r/Pppp1ppp/1b3nbN/nP6/BBP1P3/q4N2/Pp1P2PP/R2Q1RK1 w kq - 0 1',
            moves: 'a4b5 a3b4 c4c5 b4b5',
            rating: 1800,
            themes: ['endgame', 'pawn', 'multi-step'],
            description: 'Four-move pawn breakthrough: Create passed pawns and promote!',
            hint: 'Sacrifice the a-pawn to open lines. Then push the c-pawn to create passed pawns!',
            complexity: 'advanced',
            category: 'endgame'
        }
    ];

    // Combine all puzzle sources: 
    // - In meeting mode: Only puzzles from current class (subscription)
    // - Otherwise: Firestore (primary) + custom (imported in session) + samples (fallback)
    const allPuzzles = isInMeeting 
        ? [...firestorePuzzles, ...customPuzzles] // Only current class puzzles in meeting mode
        : [...firestorePuzzles, ...customPuzzles, ...samplePuzzles]; // Full library when not in meeting

    // Filter puzzles (Firestore already applies some filters, this is for client-side refinement)
    const filteredPuzzles = allPuzzles.filter(puzzle => {
        // Search filter
        if (searchQuery && !puzzle.description?.toLowerCase().includes(searchQuery.toLowerCase()) &&
            !puzzle.themes.some(theme => theme.toLowerCase().includes(searchQuery.toLowerCase()))) {
            return false;
        }
        
        // Complexity filter
        if (selectedComplexity !== 'all' && puzzle.complexity !== selectedComplexity) {
            return false;
        }
        
        // Category filter
        if (selectedCategory !== 'all' && puzzle.category !== selectedCategory) {
            return false;
        }
        
        return true;
    });

    // Resize observer to keep board responsive - Fit within page, no scrolling
    // Use ref to track board size and prevent recalculation when drawing mode changes
    const boardSizeLockedRef = useRef<number | null>(null);
    const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const lastLoadedPuzzleIdRef = useRef<string | null>(null);
    useEffect(() => {
        // NEO: observe the stable left-column ref whose size does NOT depend on the board.
        // Standard: observe the board's parent element as before.
        const el = isNeoEditorUi
            ? (boardAreaRef.current ?? boardContainerRef.current?.parentElement)
            : boardContainerRef.current?.parentElement;
        if (!el) return;
        const update = () => {
            try {
                // Clear any pending resize
                if (resizeTimeoutRef.current) {
                    clearTimeout(resizeTimeoutRef.current);
                }
                
                // Debounce to prevent rapid recalculations
                resizeTimeoutRef.current = setTimeout(() => {
                    // For NEO: measure the stable left column.
                    // For standard: measure the board's parent container.
                    const containerWidth = el.clientWidth || 900;
                    const containerHeight = el.clientHeight || 900;

                    let availableWidth: number;
                    let availableHeight: number;

                    if (isNeoEditorUi) {
                        // Spare rows (~56px) + FEN/PGN strip (~80px) + gaps/padding (~24px)
                        availableWidth  = containerWidth  - 8;
                        availableHeight = containerHeight - 56 - 80 - 24;
                    } else {
                        // Standard layout has a ~60px toolbar and 16px padding
                        availableWidth  = containerWidth  - 16;
                        availableHeight = containerHeight - 60 - 16;
                    }
                    
                    // Square board: use the smaller of the two dimensions
                    const availableSize = Math.min(availableWidth, availableHeight);

                    // NEO: slightly conservative so spare rows + FEN/PGN strip all fit without scrolling
                    const fillFactor = isNeoEditorUi ? 0.72 : 0.90;
                    const minSize    = isNeoEditorUi ? 180 : 400;
                    const target = Math.min(1800, Math.max(minSize, Math.floor(availableSize * fillFactor)));
                    
                    // Only update if board size hasn't been locked yet, or if there's a significant change (>50px)
                    // This prevents board size changes when switching drawing modes
                    if (boardSizeLockedRef.current === null) {
                        setBoardWidthPx(target);
                        boardSizeLockedRef.current = target;
                    } else if (Math.abs(target - boardSizeLockedRef.current) > 50) {
                        // Only update on significant window resize
                        setBoardWidthPx(target);
                        boardSizeLockedRef.current = target;
                    }
                }, 100);
            } catch {}
        };
        update();
        // Reset the lock so the new element is measured fresh
        boardSizeLockedRef.current = null;
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => { 
            try { 
                ro.disconnect(); 
                if (resizeTimeoutRef.current) {
                    clearTimeout(resizeTimeoutRef.current);
                }
            } catch {} 
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userRole, isNeoEditorUi]);

    // Prevent any drag interactions for students ONLY in view mode to avoid piece ghosting
    useEffect(() => {
        const container = boardContainerRef.current || document.body;
        const prevent = (e: Event) => { e.preventDefault(); };
        if (isStudent(userRole) && shareMode === 'view') {
            container.addEventListener('dragstart', prevent, { passive: false });
            container.addEventListener('touchmove', prevent, { passive: false });
            return () => {
                container.removeEventListener('dragstart', prevent as any);
                container.removeEventListener('touchmove', prevent as any);
            };
        }
        return;
    }, [userRole, shareMode]);

    // Ensure drag image stays under cursor during drag (HTML5 setDragImage)
    useEffect(() => {
        const container = boardContainerRef.current;
        if (!container) return;

        const cleanupDragPreview = () => {
            if (dragPreviewRef.current && dragPreviewRef.current.parentNode) {
                try {
                    dragPreviewRef.current.parentNode.removeChild(dragPreviewRef.current);
                } catch {}
            }
            dragPreviewRef.current = null;
        };

        // Handler applies only when pieces are draggable (not student view-only)
        const handleDragStart = (e: DragEvent) => {
            if (isStudent(userRole) && shareMode === 'view') return; // don't allow in view
            if (!e.dataTransfer) return;

            const target = e.target as HTMLElement | null;
            if (!target || target.tagName?.toLowerCase() !== 'img') return;

            const img = target as HTMLImageElement;
            const rect = img.getBoundingClientRect();

            // Compute cursor offset relative to the rendered image, accounting for transforms
            const offsetX = e.clientX - rect.left;
            const offsetY = e.clientY - rect.top;

            // Create a clone to use as the drag preview so transforms don't offset the cursor
            const clone = img.cloneNode(true) as HTMLImageElement;
            clone.style.position = 'fixed';
            clone.style.pointerEvents = 'none';
            clone.style.top = '-9999px';
            clone.style.left = '-9999px';
            clone.style.width = `${rect.width}px`;
            clone.style.height = `${rect.height}px`;
            clone.style.transform = 'none';

            cleanupDragPreview();
            document.body.appendChild(clone);
            dragPreviewRef.current = clone;

            try {
                e.dataTransfer.setDragImage(clone, offsetX, offsetY);
            } catch {}
        };

        const handleDragEnd = () => {
            cleanupDragPreview();
        };

        const handleDrop = () => {
            cleanupDragPreview();
        };

        container.addEventListener('dragstart', handleDragStart);
        container.addEventListener('dragend', handleDragEnd);
        container.addEventListener('drop', handleDrop);

        return () => {
            container.removeEventListener('dragstart', handleDragStart);
            container.removeEventListener('dragend', handleDragEnd);
            container.removeEventListener('drop', handleDrop);
            cleanupDragPreview();
        };
    }, [userRole, shareMode]);

    // Load a puzzle (just loads locally, doesn't share)
    const loadPuzzle = useCallback((puzzle: ChessPuzzle, showToast: boolean = true) => {
        // Don't load puzzles in teaching mode
        if (mode === TEACHING_MODE) {
            return;
        }
        
        // Prevent duplicate loads only if we're already loading/loaded this exact puzzle
        // But allow loading different puzzles even if called multiple times quickly
        const puzzleId = puzzle.id;
        const isAlreadyLoaded = lastLoadedPuzzleIdRef.current === puzzleId && currentPuzzle?.id === puzzleId;
        
        if (isAlreadyLoaded) {
            // Already loaded this exact puzzle, skip to prevent duplicate calls
            return;
        }
        
        // Set ref immediately to prevent duplicate calls from happening simultaneously
        lastLoadedPuzzleIdRef.current = puzzleId;
        
        // Always load the puzzle state - ensure we load the correct puzzle
        try {
            const newGame = new Chess(puzzle.fen);
            setGame(newGame);
            setCurrentPuzzle(puzzle);
            setPuzzleProgress([]);
            setIsCorrectMove(null);
            setPuzzleCompleted(false);
            setShowHint(false);
            setHintArrows([]);
            setMode('puzzle');
            
            // IMPORTANT: Set board orientation based on whose turn it is
            // If black to move, show board from black's perspective
            const turnColor = newGame.turn(); // 'w' for white, 'b' for black
            setBoardOrientation(turnColor === 'w' ? 'white' : 'black');
            console.log(`🎯 Puzzle loaded: ${turnColor === 'w' ? 'White' : 'Black'} to move, board oriented from ${turnColor === 'w' ? 'white' : 'black'}'s perspective`);
            
            // Show toast only if requested
            if (showToast) {
                toast.info(`Puzzle loaded! ${puzzle.description}`);
            }
        } catch (error) {
            console.error('Error loading puzzle:', error);
            lastLoadedPuzzleIdRef.current = null; // Reset on error
        }
    }, [mode, currentPuzzle?.id]);

    // Mark puzzle as taught (for teachers in meeting mode)
    const handleMarkAsTaught = useCallback(async (puzzle: ChessPuzzle, currentlyTaught: boolean) => {
        if (!isInMeeting || userRole !== 'teacher' || !serviceProviderId || !activeSubscriptionId || !participantName) {
            return;
        }

        try {
            if (currentlyTaught) {
                // Unmark as taught
                await unmarkPuzzleAsTaught(serviceProviderId, activeSubscriptionId, puzzle.id);
            } else {
                // Mark as taught
                await markPuzzleAsTaught(serviceProviderId, activeSubscriptionId, puzzle.id, participantName, participantName);
            }
            
            // Refresh puzzles to show updated status
            const updatedPuzzles = await getPuzzlesForSubscription(serviceProviderId, activeSubscriptionId, {
                limit: 200
            });
            setFirestorePuzzles(updatedPuzzles);
            
            // Update current puzzle if it's the one being marked
            if (currentPuzzle?.id === puzzle.id) {
                const updatedPuzzle = updatedPuzzles.find(p => p.id === puzzle.id);
                if (updatedPuzzle) {
                    setCurrentPuzzle(updatedPuzzle);
                }
            }
        } catch (error) {
            console.error('Error marking puzzle as taught:', error);
        }
    }, [isInMeeting, userRole, serviceProviderId, activeSubscriptionId, participantName, currentPuzzle]);

    // Load initial puzzle when provided from parent (only in puzzle mode)
    // This has HIGHEST PRIORITY - if initialPuzzle is provided, use it
    useEffect(() => {
        // Skip if no puzzle, in teaching mode, or already loaded
        if (!initialPuzzle || mode === TEACHING_MODE) {
            if (!initialPuzzle) {
                lastLoadedPuzzleIdRef.current = null;
            }
            return;
        }
        
        // Always load the initialPuzzle when it changes (parent selected a puzzle)
        // Only load if it's a different puzzle than currently loaded
        if (initialPuzzle.id !== currentPuzzle?.id) {
            // Reset ref if it's the same as what we're about to load (from parent)
            if (lastLoadedPuzzleIdRef.current === initialPuzzle.id) {
                lastLoadedPuzzleIdRef.current = null;
            }
            // Check if we've already loaded this puzzle in this session
            const isNewPuzzle = lastLoadedPuzzleIdRef.current !== initialPuzzle.id;
            loadPuzzle(initialPuzzle, isNewPuzzle);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPuzzle?.id, mode, initialPuzzle]); // Include full initialPuzzle to detect when parent changes selection

    // Update mode when initialMode changes
    useEffect(() => {
        if (initialMode) {
            const newMode = initialMode === 'teaching' ? TEACHING_MODE : PUZZLE_MODE;
            if (newMode !== mode) {
                setMode(newMode);
                setOriginalMode(newMode);
                // Reset puzzle when switching to teaching mode
                if (newMode === TEACHING_MODE) {
                    setCurrentPuzzle(null);
                    setGame(new Chess());
                }
            }
        }
    }, [initialMode, mode]);

    // Send chess move via LiveKit data channel
    const sendChessData = useCallback((data: ChessMove) => {
        if (room && isInMeeting) {
            try {
                const encoder = new TextEncoder();
                const payload = encoder.encode(JSON.stringify({
                    type: 'chess',
                    data
                }));
                room.localParticipant?.publishData(payload, { reliable: true });
                console.log('♟️ Sent chess data:', data);
            } catch (error) {
                console.error('Error sending chess data:', error);
                // Don't disconnect on error, just log it
            }
        }
    }, [room, isInMeeting]);

    // Check for active puzzle/teaching when joining (for late joiners)
    useEffect(() => {
        if (!room || !isInMeeting || userRole !== 'student') return;

        // Check teacher's metadata for active puzzle
        const checkForActiveContent = () => {
            room.remoteParticipants.forEach((participant) => {
                if (participant.metadata) {
                    try {
                        const metadata = JSON.parse(participant.metadata);
                        console.log('🔍 Checking participant metadata:', metadata);

                        // Handle active puzzle
                        if (metadata.activePuzzle) {
                            const puzzle = metadata.activePuzzle;
                            console.log('♟️ Late joiner: Loading active puzzle from metadata');

                            // Join collaborative session if session ID is provided
                            if (puzzle.collaborativeSessionId && activeSubscriptionId && loggedInUserId && loggedInUserName) {
                                console.log('🔗 [Late Joiner] Joining collaborative session:', puzzle.collaborativeSessionId);
                                joinCollaborativeSession(
                                    puzzle.collaborativeSessionId,
                                    activeSubscriptionId,
                                    loggedInUserId,
                                    loggedInUserName,
                                    'student'
                                ).then(() => {
                                    console.log('✅ [Late Joiner] Successfully joined collaborative session');
                                }).catch((error) => {
                                    console.warn('⚠️ [Late Joiner] Failed to join collaborative session:', error.message);
                                });
                            }

                            // Reset state first
                            setPuzzleProgress([]);
                            setIsCorrectMove(null);
                            setPuzzleCompleted(false);
                            setShowHint(false);
                            setHintArrows([]);

                            // Load student progress if available
                            if (metadata.studentProgress && Array.isArray(metadata.studentProgress)) {
                                const studentPositionsMap = new Map();
                                metadata.studentProgress.forEach(student => {
                                    studentPositionsMap.set(student.studentName, {
                                        studentName: student.studentName,
                                        fen: student.fen,
                                        lastMove: student.lastMove,
                                        timestamp: student.timestamp,
                                        completed: student.completed,
                                        totalMoves: student.totalMoves
                                    });
                                });
                                setStudentPositions(studentPositionsMap);
                                console.log('📊 Late joiner: Loaded student progress:', metadata.studentProgress.length, 'students');
                            } else {
                                setStudentPositions(new Map());
                            }

                            // Set mode and share mode
                            setMode('puzzle');
                            setShareMode('collaboration');
                            setIsBoardShared(true);

                            // Update puzzle and board state
                            setCurrentPuzzle(puzzle);
                            const newGame = new Chess(puzzle.fen);
                            setGame(newGame);

                            toast.info('♟️ Joined active puzzle session in collaboration mode!');
                            console.log('🎯 Puzzle loaded for late joiner:', puzzle);
                        }

                    } catch (error) {
                        console.error('Error parsing participant metadata:', error);
                    }
                }
            });
        };

        // Check immediately
        checkForActiveContent();

        // Also check when new participants join (teacher might join after student)
        room.on('participantConnected', checkForActiveContent);

        return () => {
            room.off('participantConnected', checkForActiveContent);
        };
    }, [room, isInMeeting, userRole]);

    // Receive chess moves from other participants
    useEffect(() => {
        if (!room || !isInMeeting) return;

        const handleDataReceived = (payload: Uint8Array, participant: any) => {
            try {
                const decoder = new TextDecoder();
                const message = JSON.parse(decoder.decode(payload));
                
                // Handle puzzle / board sharing from teacher
                if (message.type === 'chess' && message.action === 'PUZZLE_SHARED') {
                    // Only students receive the puzzle
                    if (userRole !== 'teacher') {
                        try {
                            const puzzle = message.puzzle;
                            if (!puzzle) {
                                console.error('Received puzzle share without puzzle data');
                                return;
                            }

                            // Handle teaching board shared as puzzle
                            if (puzzle.category === 'teaching') {
                                console.log('📚 Student received teaching board as puzzle:', puzzle);
                                setShareMode('collaboration');
                                setMode('teaching');
                                setIsBoardShared(true);
                                
                                const newGame = new Chess(puzzle.fen);
                                setGame(newGame);
                                setCurrentPuzzle(null);
                                setPuzzleProgress([]);
                                setIsCorrectMove(null);
                                setPuzzleCompleted(false);
                                setShowHint(false);
                                setHintArrows([]);
                                
                                toast.success('📚 Teacher shared their board - watch their moves in real-time!');
                                return;
                            }

                            // Regular puzzle handling - always collaboration mode
                            console.log('🤝 Student received puzzle in collaboration mode:', message);

                            // Set states in a specific order
                            setShareMode('collaboration');
                            setMode('puzzle');
                            setIsBoardShared(true);
                            
                            // Then set game state
                            const newGame = new Chess(puzzle.fen);
                            setGame(newGame);
                            setCurrentPuzzle(puzzle);
                            setPuzzleProgress([]);
                            setIsCorrectMove(null);
                            setPuzzleCompleted(false);
                            setShowHint(false);
                            setHintArrows([]);
                            
                            // Show collaboration mode toast
                            console.log('🤝 Student switched to collaboration mode');
                            toast.success(`♟️ Collaboration Mode: Solve "${puzzle.description || 'this puzzle'}"`);
                        } catch (error) {
                            console.error('Error handling puzzle share:', error);
                            toast.error('Failed to load shared puzzle - please ask teacher to rebroadcast');
                        }
                    }
                    return;
                }

                // Ignore board-view broadcasting; we only support puzzle mode now
                if (message.type === 'chess' && (message.action === 'BOARD_VIEW_SHARED' || message.action === 'BOARD_VIEW_STOPPED')) {
                    return;
                }
                
                // Handle student moves (only teacher receives these)
                if (message.type === 'chess' && message.action === 'STUDENT_MOVE') {
                    // Only teacher processes student moves
                    if (userRole === 'teacher') {
                        setStudentPositions(prev => {
                            const newMap = new Map(prev);
                            const existing = newMap.get(message.playerName);
                            newMap.set(message.playerName, {
                                studentName: message.playerName,
                                fen: message.fen,
                                lastMove: message.san,
                                timestamp: message.timestamp,
                                completed: existing?.completed || false,
                                totalMoves: (existing?.totalMoves || 0) + 1 // Increment move count
                            });

                            // Update metadata for late joiners with current student progress
                            setTimeout(async () => {
                                try {
                                    const metadata = {
                                        activePuzzle: currentPuzzle ? {
                                            ...currentPuzzle,
                                            shareMode: 'collaboration'
                                        } : null,
                                        studentProgress: Array.from(newMap.entries()).map(([name, data]) => ({
                                            studentName: name,
                                            fen: data.fen,
                                            lastMove: data.lastMove,
                                            timestamp: data.timestamp,
                                            completed: data.completed,
                                            totalMoves: data.totalMoves
                                        })),
                                        timestamp: Date.now()
                                    };
                                    await room.localParticipant?.setMetadata(JSON.stringify(metadata));
                                    console.log('📝 Updated metadata with student progress for late joiners');
                                } catch (error) {
                                    console.warn('Could not update metadata with student progress:', error);
                                }
                            }, 100); // Small delay to ensure state is updated

                            return newMap;
                        });
                        console.log(`♟️ Student ${message.playerName} made move: ${message.san}`);
                    }
                    return;
                }
                
                // Handle puzzle completion (only teacher receives these)
                if (message.type === 'chess' && message.action === 'PUZZLE_COMPLETED') {
                    if (userRole === 'teacher') {
                        setStudentPositions(prev => {
                            const newMap = new Map(prev);
                            const existing = newMap.get(message.playerName);
                            if (existing) {
                                newMap.set(message.playerName, {
                                    ...existing,
                                    completed: true,
                                    totalMoves: message.totalMoves,
                                    timestamp: message.timestamp
                                });

                                // Update metadata for late joiners with current student progress
                                setTimeout(async () => {
                                    try {
                                        const metadata = {
                                            activePuzzle: currentPuzzle ? {
                                                ...currentPuzzle,
                                                shareMode: 'collaboration'
                                            } : null,
                                            studentProgress: Array.from(newMap.entries()).map(([name, data]) => ({
                                                studentName: name,
                                                fen: data.fen,
                                                lastMove: data.lastMove,
                                                timestamp: data.timestamp,
                                                completed: data.completed,
                                                totalMoves: data.totalMoves
                                            })),
                                            timestamp: Date.now()
                                        };
                                        await room.localParticipant?.setMetadata(JSON.stringify(metadata));
                                        console.log('📝 Updated metadata with puzzle completion for late joiners');
                                    } catch (error) {
                                        console.warn('Could not update metadata with puzzle completion:', error);
                                    }
                                }, 100); // Small delay to ensure state is updated
                            }
                            return newMap;
                        });
                        toast.success(`🎉 ${message.playerName} completed the puzzle!`, {
                            position: 'top-right',
                            autoClose: 3000
                        });
                        console.log(`✅ Student ${message.playerName} completed puzzle in ${message.totalMoves} moves`);
                    }
                    return;
                }
                
                // Handle puzzle share in collaboration mode
                if (message.type === 'chess' && message.action === 'PUZZLE_SHARED' && userRole === 'student') {
                    console.log('📚 Student received puzzle share:', message);
                    
                    try {
                        if (!message.puzzle) {
                            console.error('Received puzzle share without puzzle data');
                            return;
                        }

                        // Join collaborative session if session ID is provided
                        if (message.collaborativeSessionId && activeSubscriptionId && loggedInUserId && loggedInUserName) {
                            console.log('🔗 [ChequeredChess] Joining collaborative session:', message.collaborativeSessionId);
                            joinCollaborativeSession(
                                message.collaborativeSessionId,
                                activeSubscriptionId,
                                loggedInUserId,
                                loggedInUserName,
                                'student'
                            ).then(() => {
                                console.log('✅ [ChequeredChess] Successfully joined collaborative session');
                            }).catch((error) => {
                                console.warn('⚠️ [ChequeredChess] Failed to join collaborative session:', error.message);
                            });
                        } else {
                            console.log('⚠️ [ChequeredChess] Missing required data for collaborative session:', {
                                hasSessionId: !!message.collaborativeSessionId,
                                hasSubscriptionId: !!activeSubscriptionId,
                                hasUserId: !!loggedInUserId,
                                hasUserName: !!loggedInUserName
                            });
                        }

                        // Reset all state first
                        setPuzzleProgress([]);
                        setIsCorrectMove(null);
                        setPuzzleCompleted(false);
                        setShowHint(false);
                        setHintArrows([]);
                        setStudentPositions(new Map());
                        
                        // Set mode and share mode
                        setMode('puzzle');
                        setShareMode('collaboration');
                        setIsBoardShared(true);
                        
                        // Update puzzle and board state
                        setCurrentPuzzle(message.puzzle);
                        const newGame = new Chess(message.puzzle.fen);
                        setGame(newGame);
                        
                        toast.success(`🤝 Solve puzzle: "${message.puzzle.description || 'this puzzle'}"`);
                        console.log('🎯 Puzzle loaded:', message.puzzle);
                    } catch (error) {
                        console.error('Error handling board share:', error);
                        toast.error('Failed to load shared board - please ask teacher to rebroadcast');
                    }
                    return;
                }
                
                // Handle stop broadcast by teacher (both puzzle and board view)
                if (message.type === 'chess' && message.action === 'PUZZLE_STOPPED') {
                    if (userRole === 'student') {
                        // Clear student's puzzle
                        setCurrentPuzzle(null);
                        setGame(new Chess());
                        setPuzzleProgress([]);
                        setIsCorrectMove(null);
                        setPuzzleCompleted(false);
                        toast.info('Teacher stopped the puzzle session');
                    }
                    return;
                }

                if (message.type === 'chess' && message.action === 'BOARD_VIEW_STOPPED') {
                    if (userRole === 'student') {
                        setIsBoardShared(false);
                        setGame(new Chess());
                        setShareMode('collaboration');
                        toast.info('Teacher stopped board view');
                    }
                    return;
                }
                
                if (message.type === 'chess') {
                    const chessData: ChessMove = message.data;
                    console.log('♟️ Received chess data from', participant.name, ':', chessData);
                    
                    // If teacher shared a puzzle/position (legacy support)
                    if (chessData.type === 'puzzle' && userRole !== 'teacher') {
                        const newGame = new Chess(chessData.fen);
                        setGame(newGame);
                        setPuzzleProgress([]);
                        setIsCorrectMove(null);
                        setPuzzleCompleted(false);
                        toast.info(`📢 Teacher shared: ${chessData.puzzleId || 'Custom position'}`);
                    }
                    
                    // If teacher is broadcasting board view, update student board with latest FEN
                    if (chessData && chessData.type === 'position' && userRole !== 'teacher') {
                        try {
                            if (shareMode === 'view') {
                                const newGame = new Chess((chessData as any).fen);
                                setGame(newGame);
                            }
                        } catch (e) {
                            console.warn('Could not update board view position:', e);
                        }
                    }

                    // If student made a move (teacher receives all student moves) - legacy
                    if (chessData.type === 'move' && userRole === 'teacher') {
                        setStudentPositions(prev => {
                            const newMap = new Map(prev);
                            newMap.set(chessData.playerName, {
                                studentName: chessData.playerName,
                                fen: chessData.fen,
                                lastMove: chessData.san,
                                timestamp: chessData.timestamp
                            });
                            return newMap;
                        });
                    }
                }
            } catch (error) {
                console.error('Error parsing chess data:', error);
            }
        };

        room.on('dataReceived', handleDataReceived);
        
        return () => {
            room.off('dataReceived', handleDataReceived);
        };
    }, [room, isInMeeting, userRole]);

    // Late join/open via teacher metadata: load board view or active puzzle
    useEffect(() => {
        if (!room || !isInMeeting || userRole === 'teacher') return;

        const checkForTeacherMetadata = async () => {
            try {
                const participants = Array.from(room.participants.values());
                const teacher = participants.find((p: any) => p.isSpeaker || p.permissions?.canPublish);
                const metaRaw = teacher?.metadata || room.localParticipant?.metadata;
                if (!metaRaw) return;
                const meta = JSON.parse(metaRaw);
                if (meta?.boardView?.fen) {
                    setShareMode('view');
                    setMode('teaching');
                    setIsBoardShared(true);
                    setGame(new Chess(meta.boardView.fen));
                    console.log('📥 Loaded board view from metadata');
                } else if (meta?.activePuzzle?.fen) {
                    setShareMode('collaboration');
                    setMode('puzzle');
                    setIsBoardShared(true);
                    setCurrentPuzzle(meta.activePuzzle);
                    setGame(new Chess(meta.activePuzzle.fen));
                    console.log('📥 Loaded puzzle from metadata');
                }
            } catch (err) {
                console.warn('Metadata parse failed', err);
            }
        };

        checkForTeacherMetadata();
        room.on('participantConnected', checkForTeacherMetadata);
        return () => room.off('participantConnected', checkForTeacherMetadata);
    }, [room, isInMeeting, userRole]);

    // Load first puzzle on mount (only if NOT in meeting OR if teacher, and NOT in teaching mode)
    // BUT: Don't auto-load if initialPuzzle is provided (parent has selected a puzzle)
    useEffect(() => {
        // Don't auto-load puzzles in teaching mode
        if (mode === TEACHING_MODE) {
            return;
        }
        // Don't auto-load if parent has provided a specific puzzle to load
        if (initialPuzzle) {
            return; // Parent has selected a puzzle, don't override it
        }
        // Students in meeting wait for teacher to share puzzle
        if (isInMeeting && userRole !== 'teacher') {
            return; // Don't auto-load for students in meetings
        }
        // Only auto-load if no puzzle is currently loaded
        if (!currentPuzzle) {
            loadPuzzle(samplePuzzles[0]);
        }
    }, [mode, initialPuzzle, currentPuzzle]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle piece drop
    const onDrop = (sourceSquare: string, targetSquare: string) => {
        // In view mode students cannot move
        if (userRole === 'student' && shareMode === 'view') {
            return false;
        }
        if (!currentPuzzle) return false;
        
        // Students can't move after completing puzzle
        if (puzzleCompleted && userRole === 'student') {
            toast.info('Puzzle already solved! Waiting for next puzzle...');
            return false;
        }
        
        // Teacher can always move in teaching mode
        if (puzzleCompleted && userRole === 'teacher' && mode === TEACHING_MODE) {
            // Allow teacher to continue in teaching mode
        } else if (puzzleCompleted) {
            return false;
        }

        const gameCopy = new Chess(game.fen());
        const move = gameCopy.move({
            from: sourceSquare,
            to: targetSquare,
            promotion: 'q' // Always promote to queen for simplicity
        });

        // Invalid move
        if (move === null) return false;

        // Get expected moves - filter out move numbers (e.g., "1.", "2.") and empty strings
        // Also filter out result markers like "1-0", "0-1", "1/2-1/2"
        const moveParts = currentPuzzle.moves.split(' ').filter(m => {
            if (!m || m.trim() === '') return false;
            // Filter out move numbers like "1.", "2.", "10."
            if (m.match(/^\d+\.?$/)) return false;
            // Filter out game results
            if (m.match(/^(1-0|0-1|1\/2-1\/2|\*)$/)) return false;
            return true;
        });
        const expectedMoves = moveParts;
        const currentMoveIndex = puzzleProgress.length;
        
        // Debug logging to help diagnose issues
        if (currentMoveIndex === 0) {
            console.log('Puzzle moves parsing:', {
                original: currentPuzzle.moves,
                filtered: expectedMoves,
                count: expectedMoves.length
            });
        }

        // Check if this is the correct move
        const isCorrect = (move.san === expectedMoves[currentMoveIndex] || 
            `${sourceSquare}${targetSquare}` === expectedMoves[currentMoveIndex]);

        if (isCorrect) {
            // Play move sound (capture or normal move)
            if (move.captured) {
                chessSounds.playCapture();
            } else {
                chessSounds.playMove();
            }
            
            // Send correct move to teacher in real-time
            if (isInMeeting && room && userRole === 'student') {
                const moveData = {
                    type: 'chess',
                    action: 'STUDENT_MOVE',
                    from: sourceSquare,
                    to: targetSquare,
                    san: move.san,
                    fen: gameCopy.fen(),
                    playerName: participantName,
                    timestamp: Date.now(),
                    puzzleId: currentPuzzle.id,
                    isCorrect: true,
                    moveNumber: currentMoveIndex + 1
                };
                
                try {
                    const encoder = new TextEncoder();
                    const payload = encoder.encode(JSON.stringify(moveData));
                    room.localParticipant?.publishData(payload, { reliable: true });
                    console.log(`♟️ Student move sent to teacher: ${move.san} (correct)`);
                } catch (error) {
                    console.error('Error sending student move:', error);
                }
            }
            
            setGame(gameCopy);
            const newProgress = [...puzzleProgress, move.san];
            setPuzzleProgress(newProgress);
            setIsCorrectMove(true);
            
            // Check if puzzle is completed - must complete ALL moves
            // newProgress.length is the number of moves completed so far
            // Puzzle is complete when we've made all expected moves
            // Use strict equality to ensure all moves are completed
            console.log(`🔍 Puzzle progress: ${newProgress.length}/${expectedMoves.length} moves completed`);
            
            if (newProgress.length === expectedMoves.length) {
                console.log('🎉 Puzzle completed after player move!');
                setPuzzleCompleted(true);
                chessSounds.playPuzzleComplete(); // Play success sound
                toast.success('🎉 Puzzle solved! Great job!');
                
                // Call onPuzzleSolved callback if provided (for tracking in subscription)
                if (onPuzzleSolved) {
                    onPuzzleSolved();
                }
                
                // Notify teacher that student completed the puzzle
                if (isInMeeting && room && userRole === 'student') {
                    const completionData = {
                        type: 'chess',
                        action: 'PUZZLE_COMPLETED',
                        playerName: participantName,
                        puzzleId: currentPuzzle.id,
                        timestamp: Date.now(),
                        totalMoves: puzzleProgress.length + 1
                    };
                    
                    try {
                        const encoder = new TextEncoder();
                        const payload = encoder.encode(JSON.stringify(completionData));
                        room.localParticipant?.publishData(payload, { reliable: true });
                        console.log('♟️ Puzzle completion sent to teacher');
                    } catch (error) {
                        console.error('Error sending completion:', error);
                    }
                }
            } else {
                chessSounds.playCorrect(); // Play correct move sound
                toast.success('✅ Correct move!');
                
                // Make computer's response move after a delay
                setTimeout(() => {
                    if (currentMoveIndex + 1 < expectedMoves.length) {
                        const nextMove = expectedMoves[currentMoveIndex + 1];
                        const computerMove = gameCopy.move(nextMove);
                        if (computerMove) {
                            const updatedGame = new Chess(gameCopy.fen());
                            setGame(updatedGame);
                            
                            // Update progress with computer move
                            setPuzzleProgress(prev => {
                                const newProgress = [...prev, computerMove.san];
                                
                                // Check if puzzle is complete after computer move
                                if (newProgress.length === expectedMoves.length) {
                                    console.log('🎉 Puzzle completed after computer move!');
                                    setPuzzleCompleted(true);
                                    chessSounds.playPuzzleComplete();
                                    toast.success('🎉 Puzzle solved! Great job!');
                                    
                                    if (onPuzzleSolved) {
                                        onPuzzleSolved();
                                    }
                                }
                                
                                return newProgress;
                            });
                            
                            // Play computer move sound
                            if (computerMove.captured) {
                                chessSounds.playCapture();
                            } else {
                                chessSounds.playMove();
                            }
                            // Play check sound if king is in check
                            if (updatedGame.inCheck()) {
                                setTimeout(() => chessSounds.playCheck(), 100);
                            }
                        }
                    }
                }, 500);
            }
        } else {
            setIsCorrectMove(false);
            chessSounds.playWrong(); // Play wrong move sound
            toast.error('❌ Incorrect move. Try again!');
            
            // Trigger shake animation
            setBoardShake(true);
            setTimeout(() => setBoardShake(false), 500);
            
            return false;
        }

        return true;
    };

    // Reset puzzle
    const resetPuzzle = () => {
        if (currentPuzzle) {
            loadPuzzle(currentPuzzle);
        }
    };

    // Load custom position from FEN
    const loadCustomPosition = () => {
        try {
            const newGame = new Chess(customFen);
            setGame(newGame);
            setCurrentPuzzle(null);
            setPuzzleCompleted(false);
            setPuzzleProgress([]);
            setShowFenInput(false);
            if (isNeoTeachingBoard && mode === TEACHING_MODE) {
                // Reset NEO editor state when switching position.
                setTeachingModeHistory([]);
                setTeachingModeRedoHistory([]);
                setSelectedTrayPiece(null);
                setNeoEditorSpare('pointer');
                setInteractionMode('move');
                setSelectedSquares(new Set());
                setRightClickedSquares({});
                setArrows([]);
                setHintArrows([]);
                setShowHint(false);
            }
            toast.success('Custom position loaded!');
        } catch (error) {
            toast.error('Invalid FEN string!');
        }
    };

    // Share puzzle in collaboration mode
    const shareBoardWithStudents = async () => {
        if (!room || !isInMeeting) {
            toast.error('Not in a live meeting');
            return;
        }

        if (userRole !== 'teacher') {
            toast.error('Only teachers can share puzzles');
            return;
        }

        if (!currentPuzzle) {
            toast.error('Please load a puzzle first');
            return;
        }

        if (mode !== 'puzzle') {
            toast.error('Share is only available in puzzle mode');
            return;
        }

        // Create or reuse collaborative session for LivePlay
        let collaborativeSessionId: string;
        try {
            console.log('🎯 Creating collaborative session for LivePlay puzzle...');
            collaborativeSessionId = await createCollaborativeSession(
                currentPuzzle,
                activeSubscriptionId,
                loggedInUserId,
                loggedInUserName,
                userRole || 'teacher'
            );
            console.log('✅ Collaborative session created/reused:', collaborativeSessionId);
        } catch (error) {
            console.error('❌ Failed to create collaborative session:', error);
            toast.error('Failed to create collaborative session');
            return;
        }

        // Save current state before sharing (to restore later)
        setOriginalMode(mode);
        setStateBeforeShare({
            puzzle: currentPuzzle,
            boardFen: game.fen(),
            mode: mode
        });
        
        setIsBoardShared(true);
        setShareMode('collaboration');
        setStudentPositions(new Map());

        // Prepare share data with collaborative session ID
        const shareData = {
            type: 'chess',
            action: 'PUZZLE_SHARED',
            fen: currentPuzzle.fen,
            shareMode: 'collaboration',
            playerName: participantName,
            timestamp: Date.now(),
            collaborativeSessionId, // Include session ID
            subscriptionId: activeSubscriptionId, // Include subscription ID
            puzzle: {
                id: currentPuzzle.id,
                fen: currentPuzzle.fen,
                moves: currentPuzzle.moves,
                description: currentPuzzle.description,
                rating: currentPuzzle.rating,
                themes: currentPuzzle.themes,
                complexity: currentPuzzle.complexity
            }
        };

        // Send the share data
        try {
            const encoder = new TextEncoder();
            const payload = encoder.encode(JSON.stringify(shareData));
            await room.localParticipant?.publishData(payload, { reliable: true });
            console.log('📢 Share data sent:', shareData);

            // Set metadata for late joiners
            try {
                const metadata = {
                    activePuzzle: {
                        ...currentPuzzle,
                        shareMode: 'collaboration',
                        collaborativeSessionId // Include session ID for late joiners
                    },
                    studentProgress: [], // Initially empty, will be updated as students progress
                    timestamp: Date.now()
                };
                await room.localParticipant?.setMetadata(JSON.stringify(metadata));
                console.log('📝 Initial metadata set for late joiners with session ID:', collaborativeSessionId);
            } catch (error) {
                console.warn('Could not set metadata:', error);
            }
        } catch (error) {
            console.error('Failed to share puzzle:', error);
            toast.error('Failed to share puzzle - please try again');
            return;
        }

        const puzzleData = {
            type: 'chess',
            action: 'PUZZLE_SHARED',
            puzzle: {
                id: currentPuzzle.id,
                fen: currentPuzzle.fen,
                moves: currentPuzzle.moves,
                description: currentPuzzle.description,
                rating: currentPuzzle.rating,
                themes: currentPuzzle.themes,
                complexity: currentPuzzle.complexity,
                category: 'puzzle'
            },
            shareMode: 'collaboration', // Always collaboration mode for puzzles
            collaborativeSessionId, // Include session ID
            subscriptionId: activeSubscriptionId, // Include subscription ID
            timestamp: Date.now()
        };

        try {
            // Save the current mode before sharing (for restoration when stopping)
            setOriginalMode(mode);

            // Set local state first before broadcasting
            setIsBoardShared(true);
            setStudentPositions(new Map());

            // Function to send a single broadcast
            const sendBroadcast = async (attempt = 1) => {
                try {
                    const encoder = new TextEncoder();
                    const payload = encoder.encode(JSON.stringify(puzzleData));
                    await room.localParticipant?.publishData(payload, { reliable: true });
                    console.log(`📢 Broadcast ${attempt} sent successfully`);
                    return true;
                } catch (error) {
                    console.warn(`❌ Broadcast ${attempt} failed:`, error);
                    return false;
                }
            };

            // Wait for state to settle, then broadcast
            setTimeout(async () => {
                // Send initial broadcast
                const firstTry = await sendBroadcast(1);
                
                // Wait and send confirmation broadcast
                await new Promise(resolve => setTimeout(resolve, 800));
                const secondTry = await sendBroadcast(2);
                
                // If both failed, try one more time
                if (!firstTry && !secondTry) {
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    const finalTry = await sendBroadcast(3);
                    
                    if (!finalTry) {
                        console.error('❌ All broadcast attempts failed');
                        toast.error('Share failed - please try rebroadcast');
                        return;
                    }
                }
        try {
            const encoder = new TextEncoder();
            const payload = encoder.encode(JSON.stringify(puzzleData));
            
                    // Send via data channel with retry mechanism
                    let retryCount = 0;
                    const maxRetries = 3;

                    while (retryCount < maxRetries) {
                        try {
            await room.localParticipant?.publishData(payload, { reliable: true });
                            console.log('♟️ Puzzle data published successfully');
                            break;
                        } catch (publishError) {
                            retryCount++;
                            console.log(`♟️ Publish attempt ${retryCount} failed:`, publishError);

                            if (retryCount < maxRetries) {
                                await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
                            } else {
                                console.error('♟️ Failed to publish puzzle after all retries');
                                toast.error('Puzzle shared but may not reach all participants - use rebroadcast button');
                            }
                        }
                    }

                    // Set metadata for late joiners - only if teacher in puzzle mode
                    if (userRole === 'teacher' && mode === PUZZLE_MODE && currentPuzzle) {
                        try {
                            const metadata = {
                                activePuzzle: {
                                    id: currentPuzzle.id,
                                    fen: currentPuzzle.fen,
                                    moves: currentPuzzle.moves,
                                    description: currentPuzzle.description,
                                    rating: currentPuzzle.rating,
                                    themes: currentPuzzle.themes,
                                    complexity: currentPuzzle.complexity,
                                    category: currentPuzzle.category,
                                    shareMode: 'collaboration'
                                },
                                studentProgress: Array.from(studentPositions.entries()).map(([name, data]) => ({
                                    studentName: name,
                                    fen: data.fen,
                                    lastMove: data.lastMove,
                                    timestamp: data.timestamp,
                                    completed: data.completed,
                                    totalMoves: data.totalMoves
                                })),
                                timestamp: Date.now()
                            };

                            await room.localParticipant?.setMetadata(JSON.stringify(metadata));
                            console.log('📝 Teacher updated metadata for puzzle late joiners:', metadata);
                        } catch (metadataError) {
                            console.warn('Could not update metadata for late joiners:', metadataError);
                            // Continue anyway - real-time sharing still works
                        }
                    } else {
                        console.log(`🎓 Skipping metadata update - ${userRole} in ${mode} mode`);
                    }
            
            const modeText = 'Collaboration Mode';
                    const actionText = mode === TEACHING_MODE ? 'Teaching board' : 'Puzzle';
                    toast.success(`📢 ${actionText} shared in ${modeText}!`);
        } catch (error) {
                    console.error('Error in puzzle publish retry:', error);
                }
            }, 500); // Wait 500ms for state to settle

            console.log(`♟️ Board sharing initiated, mode: ${mode}`);
        } catch (error) {
            console.error('Error sharing board:', error);
            toast.error('Failed to share board');
        }
    };

    // Share teacher board in view-only mode
    const shareBoardViewMode = async () => {
        if (!room || !isInMeeting) {
            toast.error('Not in a live meeting');
            return;
        }

        try {
            setOriginalMode(mode);
            // Force teaching mode for view-only broadcast
            setMode(TEACHING_MODE);
            setCurrentPuzzle(null);
            setIsBoardShared(true);
            setShareMode('view');

            const element = boardContainerRef.current;
            if (!element) throw new Error('Chequered container not found');

            // 1) Prefer Element Region Capture (closest to screenshare, cropped to board)
            let published = false;
            try {
                const supportsCropTarget = typeof (window as any).CropTarget !== 'undefined';
                // Capture current tab to avoid infinite mirror when the board is in-app
                const stream: MediaStream = await (navigator.mediaDevices as any).getDisplayMedia({
                    video: { preferCurrentTab: true },
                    audio: false
                });
                const track = stream.getVideoTracks()[0];
                if (track && supportsCropTarget && typeof (track as any).cropTo === 'function') {
                    const cropTarget = await (window as any).CropTarget.fromElement(element);
                    await (track as any).cropTo(cropTarget);
                }
                screenShareTrackRef.current = track;
                await room.localParticipant.publishTrack(track, { name: 'chequered-view' as any, source: Track.Source.ScreenShare });
                
                // Notify students to open board in view mode
                try {
                    const encoder = new TextEncoder();
                    const viewData = {
                        type: 'chess',
                        action: 'BOARD_VIEW_SHARED',
                        fen: game.fen(),
                        shareMode: 'view',
                        timestamp: Date.now()
                    };
                    const payload = encoder.encode(JSON.stringify(viewData));
                    await room.localParticipant?.publishData(payload, { reliable: true });
                } catch (_) {}

                // Start periodic FEN broadcast
                if (boardViewBroadcastIntervalRef.current) {
                    window.clearInterval(boardViewBroadcastIntervalRef.current);
                }
                boardViewBroadcastIntervalRef.current = window.setInterval(async () => {
                    try {
                        const encoder = new TextEncoder();
                        const pos = {
                            type: 'chess',
                            data: {
                                type: 'position',
                                fen: game.fen(),
                                playerName: participantName,
                                timestamp: Date.now()
                            }
                        } as any;
                        const payload = encoder.encode(JSON.stringify(pos));
                        await room.localParticipant?.publishData(payload, { reliable: false });
                    } catch (_) {}
                }, 2000);

                published = true;
                toast.success('📺 Chequered view shared');
            } catch (e) {
                console.warn('Element Capture not available, falling back to canvas mirror', e);
            }

            // 2) Fallback to canvas streaming (no popup). Dynamically import html2canvas.
            if (!published) try {
                const html2canvasMod = await import(/* @vite-ignore */ 'html2canvas');
                const html2canvas = html2canvasMod.default || (html2canvasMod as any);
                // Prepare mirror canvas
                const { width, height } = element.getBoundingClientRect();
                const mirror = document.createElement('canvas');
                mirror.width = Math.max(1, Math.floor(width));
                mirror.height = Math.max(1, Math.floor(height));
                canvasMirrorRef.current = mirror;

                const fps = 8; // balance CPU and quality
                const tick = async () => {
                    try {
                        const snap = await html2canvas(element, {
                            backgroundColor: null,
                            scale: 1,
                            useCORS: true,
                            onclone: undefined
                        });
                        const mctx = mirror.getContext('2d');
                        if (mctx) {
                            mctx.clearRect(0, 0, mirror.width, mirror.height);
                            mctx.drawImage(snap, 0, 0, mirror.width, mirror.height);
                        }
                    } catch (e) {
                        // continue; next frame will try again
                    }
                };

                // Prime first frame
                await tick();

                // Start capture stream from mirror
                if (typeof (mirror as any).captureStream === 'function') {
                    const stream: MediaStream = (mirror as any).captureStream(fps);
                    const track = stream.getVideoTracks()[0];
                    screenShareTrackRef.current = track;
                    await room.localParticipant.publishTrack(track, { name: 'chequered-view' as any, source: Track.Source.ScreenShare });
                    // Schedule periodic redraws
                    canvasMirrorIntervalRef.current = window.setInterval(tick, Math.floor(1000 / fps));
                    published = true;
                    // Notify students to open board in view mode
                    try {
                        const encoder = new TextEncoder();
                        const viewData = {
                            type: 'chess',
                            action: 'BOARD_VIEW_SHARED',
                            fen: game.fen(),
                            shareMode: 'view',
                            timestamp: Date.now()
                        };
                        const payload = encoder.encode(JSON.stringify(viewData));
                        await room.localParticipant?.publishData(payload, { reliable: true });
                    } catch (_) {}
                    // Start periodic FEN broadcast to keep students in sync
                    if (boardViewBroadcastIntervalRef.current) {
                        window.clearInterval(boardViewBroadcastIntervalRef.current);
                    }
                    boardViewBroadcastIntervalRef.current = window.setInterval(async () => {
                        try {
                            const encoder = new TextEncoder();
                            const pos = {
                                type: 'chess',
                                data: {
                                    type: 'position',
                                    fen: game.fen(),
                                    playerName: participantName,
                                    timestamp: Date.now()
                                }
                            } as any;
                            const payload = encoder.encode(JSON.stringify(pos));
                            await room.localParticipant?.publishData(payload, { reliable: false });
                        } catch (_) {}
                    }, 2000);
                    toast.success('📺 Chequered view shared');
                }
            } catch (e) {
                console.warn('html2canvas unavailable, falling back to browser share picker', e);
            }

            // 3) Last fallback: regular display capture without crop
            if (!published) {
                const stream = await (navigator.mediaDevices as any).getDisplayMedia({ video: true, audio: false });
                const track: MediaStreamTrack | undefined = stream.getVideoTracks()?.[0];
                if (!track) throw new Error('No video track from display media');
                screenShareTrackRef.current = track;
                await room.localParticipant.publishTrack(track, { name: 'chequered-view' as any, source: Track.Source.ScreenShare });
                try {
                    const encoder = new TextEncoder();
                    const viewData = { type: 'chess', action: 'BOARD_VIEW_SHARED', fen: game.fen(), shareMode: 'view', timestamp: Date.now() } as any;
                    const payload = encoder.encode(JSON.stringify(viewData));
                    await room.localParticipant?.publishData(payload, { reliable: true });
                } catch (_) {}
                if (boardViewBroadcastIntervalRef.current) {
                    window.clearInterval(boardViewBroadcastIntervalRef.current);
                }
                boardViewBroadcastIntervalRef.current = window.setInterval(async () => {
                    try {
                        const encoder = new TextEncoder();
                        const pos = { type: 'chess', data: { type: 'position', fen: game.fen(), playerName: participantName, timestamp: Date.now() } } as any;
                        const payload = encoder.encode(JSON.stringify(pos));
                        await room.localParticipant?.publishData(payload, { reliable: false });
                    } catch (_) {}
                }, 2000);
                toast.success('📺 Chequered view shared');
            }
        } catch (e) {
            console.error('Error starting board view share:', e);
            toast.error('Failed to share board');
            setIsBoardShared(false);
            setShareMode('collaboration');
        }
    };

    // Rebroadcast current puzzle (for when initial broadcast failed)
    const rebroadcastBoard = async () => {
        if (!room || !isInMeeting || !isBoardShared) {
            toast.error('Nothing to rebroadcast - share puzzle first');
            return;
        }

        if (userRole !== 'teacher') {
            toast.error('Only teachers can rebroadcast');
            return;
        }

        if (!currentPuzzle) {
            toast.error('No puzzle to rebroadcast');
            return;
        }

        // Prepare rebroadcast data
        const rebroadcastData = {
            type: 'chess',
            action: 'PUZZLE_SHARED',
            fen: currentPuzzle.fen,
            shareMode: 'collaboration',
            playerName: participantName,
            timestamp: Date.now(),
            puzzle: {
                id: currentPuzzle.id,
                fen: currentPuzzle.fen,
                moves: currentPuzzle.moves,
                description: currentPuzzle.description,
                rating: currentPuzzle.rating,
                themes: currentPuzzle.themes,
                complexity: currentPuzzle.complexity
            }
        };

        try {
            const encoder = new TextEncoder();
            const payload = encoder.encode(JSON.stringify(rebroadcastData));
            await room.localParticipant?.publishData(payload, { reliable: true });
            toast.success('Puzzle rebroadcasted to all participants!');
            console.log('📢 Puzzle rebroadcast successful');
        } catch (error) {
            console.error('Error rebroadcasting:', error);
            toast.error('Failed to rebroadcast');
        }
        try {
            const encoder = new TextEncoder();
            const payload = encoder.encode(JSON.stringify(rebroadcastData));
            await room.localParticipant?.publishData(payload, { reliable: true });

            toast.success('Puzzle rebroadcasted to all participants!');
            console.log('📢 Puzzle rebroadcast successful');
        } catch (error) {
            console.error('Error rebroadcasting:', error);
            toast.error('Failed to rebroadcast');
        }
    };

    // Stop sharing board
    const stopSharingBoard = async () => {
        if (!room || !isInMeeting) return;
        
        // For view mode, unpublish and stop the screen-share track
        if (shareMode === 'view' && screenShareTrackRef.current) {
            try {
                room.localParticipant.unpublishTrack(screenShareTrackRef.current);
            } catch {}
            try {
                screenShareTrackRef.current.stop();
            } catch {}
            screenShareTrackRef.current = null;
            if (canvasMirrorIntervalRef.current) {
                window.clearInterval(canvasMirrorIntervalRef.current);
                canvasMirrorIntervalRef.current = null;
            }
            if (boardViewBroadcastIntervalRef.current) {
                window.clearInterval(boardViewBroadcastIntervalRef.current);
                boardViewBroadcastIntervalRef.current = null;
            }
        }

        // Broadcast stop sharing to all students for both modes (so UI closes)
        const stopData = {
            type: 'chess',
            action: shareMode === 'view' ? 'BOARD_VIEW_STOPPED' : 'PUZZLE_STOPPED',
            timestamp: Date.now()
        };
        try {
            const encoder = new TextEncoder();
            const payload = encoder.encode(JSON.stringify(stopData));
            await room.localParticipant?.publishData(payload, { reliable: true });
        } catch {}
        
        // Clear metadata for late joiners (teachers only)
        if (userRole === 'teacher') {
            try {
                await room.localParticipant?.setMetadata('');
            } catch {}
        }
        toast.success('Board sharing stopped');
        
        // Restore state to what it was before sharing
        setIsBoardShared(false);
        setStudentPositions(new Map());
        setShareMode('collaboration');
        
        // Restore puzzle, board, and mode to state before sharing
        if (stateBeforeShare && userRole === 'teacher') {
            setMode(stateBeforeShare.mode);
            if (stateBeforeShare.puzzle) {
                setCurrentPuzzle(stateBeforeShare.puzzle);
                setGame(new Chess(stateBeforeShare.boardFen));
                setPuzzleProgress([]);
                setIsCorrectMove(null);
                setPuzzleCompleted(false);
            } else {
                setCurrentPuzzle(null);
                setGame(new Chess(stateBeforeShare.boardFen));
            }
            setStateBeforeShare(null); // Clear saved state
        }
    };

    // Parse puzzle solution moves and create visual hint arrows (like Lichess)
    // Generate hint: ONLY highlight the piece to move, not the destination
    // This makes students think about WHERE to move, promoting learning
    const generateHintArrows = useCallback(() => {
        if (!currentPuzzle || !showHint) {
            setHintArrows([]);
            setRightClickedSquares({}); // Clear highlights
            return;
        }

        try {
            // Filter out move numbers and result markers
            const solutionMoves = currentPuzzle.moves.split(' ').filter(m => {
                if (!m || m.trim() === '') return false;
                if (m.match(/^\d+\.?$/)) return false; // Filter out move numbers like "1.", "2."
                if (m.match(/^(1-0|0-1|1\/2-1\/2|\*)$/)) return false; // Filter out results
                return true;
            });
            
            // Clear previous highlights
            const newHighlights: { [square: string]: { backgroundColor: string } } = {};
            
            // Show the NEXT move based on current progress (not always the first move)
            const nextMoveIndex = puzzleProgress.length;
            
            if (nextMoveIndex < solutionMoves.length) {
                const nextMove = solutionMoves[nextMoveIndex];
                
                let fromSquare = '';
                
                // Parse move: could be in format "e2e4" or "Nf3" (SAN notation)
                if (nextMove.length >= 4 && /^[a-h][1-8][a-h][1-8]/.test(nextMove)) {
                    // UCI format: e2e4
                    fromSquare = nextMove.substring(0, 2);
                } else {
                    // Try to parse as SAN notation using chess.js
                    try {
                        const tempGame = new Chess(game.fen());
                        const move = tempGame.move(nextMove);
                        
                        if (move) {
                            fromSquare = move.from;
                        }
                    } catch (e) {
                        console.log('Could not parse SAN move for hint:', nextMove);
                    }
                }
                
                // Highlight ONLY the piece to move (not the destination)
                if (fromSquare) {
                    newHighlights[fromSquare] = {
                        backgroundColor: 'rgba(255, 193, 7, 0.5)' // Yellow glow
                    };
                }
            }
            
            setHintArrows([]); // No arrows, just highlights
            setRightClickedSquares(newHighlights); // Use square highlights instead
        } catch (error) {
            console.error('Error generating hint:', error);
            setHintArrows([]);
            setRightClickedSquares({});
        }
    }, [currentPuzzle, showHint, game, puzzleProgress]);

    // Update hint arrows when showHint changes
    useEffect(() => {
        generateHintArrows();
    }, [generateHintArrows]);

    // Create custom puzzle from current position
    const createCustomPuzzle = () => {
        if (!newPuzzle.description.trim()) {
            toast.error('Please enter a puzzle description');
            return;
        }
        if (!newPuzzle.solution.trim()) {
            toast.error('Please enter the solution moves (e.g., "e2e4 e7e5" or "Nf3 Nc6")');
            return;
        }

        try {
            // Validate solution moves
            const testGame = new Chess(game.fen());
            const solutionMoves = newPuzzle.solution.trim().split(/\s+/);
            
            for (const move of solutionMoves) {
                try {
                    testGame.move(move);
                } catch {
                    toast.error(`Invalid move in solution: ${move}`);
                    return;
                }
            }

            const customPuzzle: ChessPuzzle = {
                id: `custom-${Date.now()}`,
                fen: game.fen(),
                moves: newPuzzle.solution.trim(),
                rating: newPuzzle.complexity === 'beginner' ? 1200 : newPuzzle.complexity === 'intermediate' ? 1600 : 2000,
                themes: newPuzzle.themes.split(',').map(t => t.trim()).filter(t => t),
                description: newPuzzle.description,
                hint: newPuzzle.hint || undefined,
                complexity: newPuzzle.complexity,
                category: newPuzzle.category,
                source: 'custom'
            };

            // Add to custom puzzles
            const updatedCustomPuzzles = [...customPuzzles, customPuzzle];
            setCustomPuzzles(updatedCustomPuzzles);
            
            // Save to localStorage
            localStorage.setItem('chequeredCustomPuzzles', JSON.stringify(updatedCustomPuzzles));

            // Reset form
            setNewPuzzle({
                description: '',
                hint: '',
                solution: '',
                complexity: 'beginner',
                category: 'tactics',
                themes: ''
            });
            setShowPuzzleCreator(false);

            toast.success('✅ Custom puzzle created successfully!');
            
            // Load the newly created puzzle
            loadPuzzle(customPuzzle);
        } catch (error) {
            toast.error('Failed to create puzzle. Please check your inputs.');
            console.error(error);
        }
    };

    // Delete custom puzzle
    const deleteCustomPuzzle = (puzzleId: string) => {
        const updatedPuzzles = customPuzzles.filter(p => p.id !== puzzleId);
        setCustomPuzzles(updatedPuzzles);
        localStorage.setItem('chequeredCustomPuzzles', JSON.stringify(updatedPuzzles));
        
        if (currentPuzzle?.id === puzzleId) {
            setCurrentPuzzle(null);
            setGame(new Chess());
        }
        
        toast.success('Puzzle deleted');
    };

    // Load custom puzzles from localStorage on mount
    useEffect(() => {
        const savedPuzzles = localStorage.getItem('chequeredCustomPuzzles');
        if (savedPuzzles) {
            try {
                setCustomPuzzles(JSON.parse(savedPuzzles));
            } catch (error) {
                console.error('Failed to load custom puzzles:', error);
            }
        }
    }, []);

    // Switch to teaching mode (free board)
    const switchToTeachingMode = () => {
        // Clean up existing state
        setMode('teaching');
        setOriginalMode('teaching'); // Update original mode when switching
        setShareMode('collaboration'); // Teaching mode uses collaboration
        setIsBoardShared(false); // Reset sharing state
        
        // Reset game state
        const newGame = new Chess(); // Start from initial position
        setGame(newGame);
        setCurrentPuzzle(null);
        setPuzzleProgress([]);
        setIsCorrectMove(null);
        setPuzzleCompleted(false);
        setTeachingModeHistory([]);
        setShowPiecePicker(false);
        setIsPieceSetupMode(false);
        setStudentPositions(new Map());
        
        // Clear any existing metadata
        if (room && room.localParticipant) {
            try {
                room.localParticipant.setMetadata('');
                console.log('🧹 Cleared metadata when switching to teaching mode');
            } catch (error) {
                console.warn('Could not clear metadata:', error);
            }
        }
        setSelectedTrayPiece(null);
        setRightClickedSquares({}); // Clear annotations
        setArrows([]);
        setArrowStart(null);
        setHintArrows([]);
        setShowHint(false);
        toast.info('Teaching mode activated - Board is fully editable! Piece tray and drawing tools available.');
    };

    // Helper function to build board state manually (bypasses chess.js validation)
    // Helper to convert board array to FEN string (bypasses chess.js validation)
    const boardArrayToFen = (board: (any | null)[][]): string => {
        const fenParts: string[] = [];
        for (let rank = 0; rank < 8; rank++) {
            let fenRank = '';
            let emptyCount = 0;
            for (let file = 0; file < 8; file++) {
                const piece = board[rank][file];
                if (piece) {
                    if (emptyCount > 0) {
                        fenRank += emptyCount.toString();
                        emptyCount = 0;
                    }
                    const pieceChar = piece.type === 'p' ? 'p' :
                                     piece.type === 'r' ? 'r' :
                                     piece.type === 'n' ? 'n' :
                                     piece.type === 'b' ? 'b' :
                                     piece.type === 'q' ? 'q' : 'k';
                    fenRank += piece.color === 'w' ? pieceChar.toUpperCase() : pieceChar;
                } else {
                    emptyCount++;
                }
            }
            if (emptyCount > 0) {
                fenRank += emptyCount.toString();
            }
            fenParts.push(fenRank || '8');
        }
        return `${fenParts.join('/')} w - - 0 1`;
    };

    // Build board state completely bypassing chess.js validation (for teaching mode)
    const buildBoardState = (currentGame: Chess, square: string, pieceToPlace: { type: string, color: string } | null): Chess => {
        try {
            // Get current board state - handle both valid and invalid FENs
            const emptyBoard: (any | null)[][] = Array(8).fill(null).map(() => Array(8).fill(null));
            let currentBoard = emptyBoard;
            
            // Try to get board state from current game
            try {
                currentBoard = currentGame.board();
            } catch (e) {
                // If current game has invalid FEN, start with empty board
                currentBoard = emptyBoard;
            }
            
            // Build new board state: copy all pieces except target square
            const newBoard: (any | null)[][] = Array(8).fill(null).map(() => Array(8).fill(null));
            for (let rank = 0; rank < 8; rank++) {
                for (let file = 0; file < 8; file++) {
                    const sq = String.fromCharCode(97 + file) + (8 - rank);
                    if (sq !== square) {
                        const piece = currentBoard[rank]?.[file];
                        if (piece) {
                            newBoard[rank][file] = { type: piece.type, color: piece.color };
                        }
                    }
                }
            }
            
            // Place the new piece if provided (allows multiple kings, no validation)
            if (pieceToPlace) {
                const targetRank = 8 - parseInt(square[1]);
                const targetFile = square.charCodeAt(0) - 97;
                newBoard[targetRank][targetFile] = {
                    type: pieceToPlace.type,
                    color: pieceToPlace.color
                };
            }
            
            // Generate FEN from board array (bypasses chess.js validation)
            const fen = boardArrayToFen(newBoard);
            
            // If validations are enforced in teaching mode, use chess.js validation
            if (mode === TEACHING_MODE && enforceValidations) {
                try {
                    const newGame = new Chess(fen);
                    // Verify the board matches what we want
                    const actualBoard = newGame.board();
                    let matches = true;
                    for (let rank = 0; rank < 8; rank++) {
                        for (let file = 0; file < 8; file++) {
                            const expectedPiece = newBoard[rank][file];
                            const actualPiece = actualBoard[rank]?.[file];
                            if ((expectedPiece && !actualPiece) || (!expectedPiece && actualPiece) ||
                                (expectedPiece && actualPiece && 
                                 (expectedPiece.type !== actualPiece.type || expectedPiece.color !== actualPiece.color))) {
                                matches = false;
                                break;
                            }
                        }
                        if (!matches) break;
                    }
                    if (matches) {
                        return newGame;
                    }
                    // If validation changed the board, show a warning but use the validated version
                    toast.warning('Position adjusted to meet chess rules');
                    return newGame;
                } catch (e) {
                    // Chess.js rejected the FEN - show error and return current game
                    toast.error('Invalid position: ' + (e instanceof Error ? e.message : 'Position violates chess rules'));
                    return currentGame;
                }
            }
            
            // No validation: Create a new Chess instance, bypassing validation
            let newGame: Chess;
            try {
                newGame = new Chess(fen);
                // Check if the board matches (chess.js might have modified it)
                const actualBoard = newGame.board();
                let matches = true;
                for (let rank = 0; rank < 8; rank++) {
                    for (let file = 0; file < 8; file++) {
                        const expectedPiece = newBoard[rank][file];
                        const actualPiece = actualBoard[rank]?.[file];
                        if ((expectedPiece && !actualPiece) || (!expectedPiece && actualPiece) ||
                            (expectedPiece && actualPiece && 
                             (expectedPiece.type !== actualPiece.type || expectedPiece.color !== actualPiece.color))) {
                            matches = false;
                            break;
                        }
                    }
                    if (!matches) break;
                }
                if (matches) {
                    return newGame;
                }
            } catch (e) {
                // Chess.js rejected the FEN - we'll create a workaround
            }
            
            // Workaround: Create minimal valid game, clear it, then place pieces manually
            // Use a position with kings in corners that we'll remove
            newGame = new Chess('k7/8/8/8/8/8/8/7K w - - 0 1');
            
            // Remove all pieces first
            const allSquares = ['a1', 'b1', 'c1', 'd1', 'e1', 'f1', 'g1', 'h1',
                              'a2', 'b2', 'c2', 'd2', 'e2', 'f2', 'g2', 'h2',
                              'a3', 'b3', 'c3', 'd3', 'e3', 'f3', 'g3', 'h3',
                              'a4', 'b4', 'c4', 'd4', 'e4', 'f4', 'g4', 'h4',
                              'a5', 'b5', 'c5', 'd5', 'e5', 'f5', 'g5', 'h5',
                              'a6', 'b6', 'c6', 'd6', 'e6', 'f6', 'g6', 'h6',
                              'a7', 'b7', 'c7', 'd7', 'e7', 'f7', 'g7', 'h7',
                              'a8', 'b8', 'c8', 'd8', 'e8', 'f8', 'g8', 'h8'];
            allSquares.forEach(sq => {
                try {
                    const p = newGame.get(sq as any);
                    if (p) newGame.remove(sq as any);
                } catch (e) {}
            });
            
            // Now place all pieces from our board array
            // We'll use put() which should work even with invalid positions in some cases
            for (let rank = 0; rank < 8; rank++) {
                for (let file = 0; file < 8; file++) {
                    const piece = newBoard[rank][file];
                    if (piece) {
                        const sq = String.fromCharCode(97 + file) + (8 - rank) as any;
                        try {
                            newGame.put({ type: piece.type as any, color: piece.color as any }, sq);
                        } catch (err) {
                            // If put() fails, we'll need to use a different approach
                            // For now, log and continue - the FEN will still be correct for rendering
                            console.warn(`Could not place ${piece.color}${piece.type} on ${sq}`);
                        }
                    }
                }
            }
            
            // Override the FEN to match our board (for react-chessboard rendering)
            // We'll store the correct FEN and use it for rendering
            // Note: The Chess instance might have a different internal state, but we'll use our FEN for display
            return newGame;
        } catch (error) {
            console.error('Error building board state:', error);
            return currentGame;
        }
    };

    // Handle piece drop in teaching mode (respects enforceValidations flag)
    const onDropTeaching = (sourceSquare: string, targetSquare: string, piece: string) => {
        // Save current position to history
        const currentFen = game.fen();
        
        try {
            // If validations are enforced, use chess.js move validation
            if (enforceValidations) {
                try {
                    const gameCopy = new Chess(game.fen());
                    const move = gameCopy.move({
            from: sourceSquare,
            to: targetSquare,
                        promotion: 'q' // Auto-promote to queen
            });
            
            if (move) {
                        // Valid move
                setTeachingModeHistory([...teachingModeHistory, currentFen]);
                setTeachingModeRedoHistory([]); // Clear redo history when making a new move
                        setNeoMoveLog(prev => [...prev, move.san]);
                        setNeoMoveUndoneLog([]);
                        setGame(gameCopy);

                        // Share move in teaching mode
        if (isInMeeting && room) {
                            try {
            sendChessData({
                type: 'position',
                from: sourceSquare,
                to: targetSquare,
                san: move.san,
                                    fen: gameCopy.fen(),
                playerName: participantName,
                timestamp: Date.now()
            });
                            } catch (e) {
                                console.log('Could not share move:', e);
        }
                        }
        return true;
                    } else {
                        toast.error(explainIllegalMove(new Chess(game.fen()), sourceSquare, targetSquare), { autoClose: 3000 });
                        return false;
                    }
                } catch {
                    toast.error(explainIllegalMove(new Chess(game.fen()), sourceSquare, targetSquare), { autoClose: 3000 });
                    return false;
                }
            }
            
            // No validation: free movement (Lichess-style)
            // Get the piece being moved from the source square
            let sourcePiece: { type: string, color: string } | null = null;
            
            // Method 1: Try to get from board array directly
            try {
                const currentBoard = game.board();
                const sourceRank = 8 - parseInt(sourceSquare[1]);
                const sourceFile = sourceSquare.charCodeAt(0) - 97;
                if (currentBoard[sourceRank] && currentBoard[sourceRank][sourceFile]) {
                    const pieceOnSquare = currentBoard[sourceRank][sourceFile];
                    sourcePiece = {
                        type: pieceOnSquare.type,
                        color: pieceOnSquare.color
                    };
                }
                } catch (e) {
                // Board() might fail if FEN is invalid, continue to next method
            }
            
            // Method 2: Try to parse from piece string (format: "wK", "bP", etc.)
            if (!sourcePiece && piece) {
            const pieceColor = piece.charAt(0) === 'w' ? 'w' : 'b';
            const pieceType = piece.charAt(1).toLowerCase();
                sourcePiece = { type: pieceType, color: pieceColor };
            }
            
            // Method 3: Try using game.get() as last resort
            if (!sourcePiece) {
                try {
                    const pieceObj = game.get(sourceSquare as any);
                    if (pieceObj) {
                        sourcePiece = {
                            type: pieceObj.type,
                            color: pieceObj.color
                        };
                    }
                } catch (e) {
                    // get() might fail, that's okay
                }
            }
            
            // If we still don't have a piece, we can't move
            if (!sourcePiece) {
                console.warn('Could not determine piece to move from', sourceSquare, piece);
                return false;
            }
            
            // Use buildBoardState to move the piece freely (no validation)
            let updatedGame = buildBoardState(game, sourceSquare, null); // Remove from source
            updatedGame = buildBoardState(updatedGame, targetSquare, sourcePiece); // Place on target

            setTeachingModeHistory([...teachingModeHistory, currentFen]);
            setTeachingModeRedoHistory([]);
            setGame(updatedGame);

            // Record the move in the PGN log.
            // First try to get proper SAN (works for legal moves even in free-placement mode).
            // Fall back to compact UCI-style notation for illegal/creative moves.
            {
                const PIECE_SYM: Record<string, string> = { p: '', n: 'N', b: 'B', r: 'R', q: 'Q', k: 'K' };
                let moveSan = '';
                try {
                    const tryCopy = new Chess(game.fen());
                    const tryMove = tryCopy.move({ from: sourceSquare, to: targetSquare, promotion: 'q' });
                    if (tryMove) moveSan = tryMove.san;
                } catch { /* illegal – fall through */ }
                if (!moveSan && sourcePiece) {
                    const sym = PIECE_SYM[sourcePiece.type] ?? '';
                    moveSan = `${sym}${sourceSquare}-${targetSquare}`;
                }
                if (moveSan) {
                    setNeoMoveLog(prev => [...prev, moveSan]);
                    setNeoMoveUndoneLog([]);
                }
            }

            // Share move in teaching mode too
            if (isInMeeting && room) {
                try {
                    sendChessData({
                        type: 'position',
                        from: sourceSquare,
                        to: targetSquare,
                        san: `${sourceSquare}${targetSquare}`, // Simple notation for teaching mode
                        fen: updatedGame.fen(),
                        playerName: participantName,
                        timestamp: Date.now()
                    });
                } catch (e) {
                    console.log('Could not share move:', e);
                }
            }
            
            return true;
        } catch (error) {
            console.error('Error in piece drop:', error);
            // Fallback: try to parse piece from string and place it
            try {
                const pieceColor = piece.charAt(0) === 'w' ? 'w' : 'b';
                const pieceType = piece.charAt(1).toLowerCase();
                // Try to remove from source first
                let updatedGame = buildBoardState(game, sourceSquare, null);
                // Then place on target
                updatedGame = buildBoardState(updatedGame, targetSquare, { type: pieceType, color: pieceColor });
                setTeachingModeHistory([...teachingModeHistory, currentFen]);
                setTeachingModeRedoHistory([]); // Clear redo history when making a new move
                setGame(updatedGame);
                return true;
            } catch (fallbackError) {
                console.error('Fallback also failed:', fallbackError);
                toast.error('Could not move piece');
            return false;
            }
        }
    };

    // Undo last move in teaching mode
    const undoTeachingMove = () => {
        if (teachingModeHistory.length === 0) {
            toast.info('Nothing to undo');
            return;
        }

        const currentFen = game.fen();
        const previousFen = teachingModeHistory[teachingModeHistory.length - 1];
        const newHistory = teachingModeHistory.slice(0, -1);
        
        // Add current position to redo history
        setTeachingModeRedoHistory([...teachingModeRedoHistory, currentFen]);
        setTeachingModeHistory(newHistory);
        setGame(new Chess(previousFen));
        // Sync move log
        setNeoMoveLog(prev => {
            const n = [...prev];
            const popped = n.pop();
            if (popped !== undefined) setNeoMoveUndoneLog(u => [...u, popped]);
            return n;
        });
        
        // Remove last move from progress
        if (puzzleProgress.length > 0) {
            setPuzzleProgress(puzzleProgress.slice(0, -1));
        }
    };

    // Redo last undone move in teaching mode
    const redoTeachingMove = () => {
        if (teachingModeRedoHistory.length === 0) {
            toast.info('Nothing to redo');
            return;
        }

        const nextFen = teachingModeRedoHistory[teachingModeRedoHistory.length - 1];
        const newRedoHistory = teachingModeRedoHistory.slice(0, -1);
        
        // Add current position back to undo history
        setTeachingModeHistory([...teachingModeHistory, game.fen()]);
        setTeachingModeRedoHistory(newRedoHistory);
        setGame(new Chess(nextFen));
        // Sync move log
        setNeoMoveUndoneLog(prev => {
            const n = [...prev];
            const restored = n.pop();
            if (restored !== undefined) setNeoMoveLog(m => [...m, restored]);
            return n;
        });
    };

    // Clear all annotations
    const clearAnnotations = () => {
        setRightClickedSquares({});
        setArrowStart(null);
        setArrows([]);
        setHintArrows([]);
        setShowHint(false);
        toast.success('All annotations and arrows cleared');
    };

    // Handle square right-click for highlighting or arrow drawing
    const onSquareRightClick = (square: string) => {
        // Allow annotations in both teaching and puzzle mode
        if (mode !== TEACHING_MODE && mode !== PUZZLE_MODE) return;

        if (drawingMode === 'highlight') {
            // Highlight mode - toggle highlight on right-click
            // Standard teaching uses blue highlights; NEO uses the selected color.
            const color = isNeoTeachingBoard
                ? ({
                    green: 'rgba(16, 185, 129, 0.4)',
                    red: 'rgba(239, 68, 68, 0.4)',
                    blue: 'rgba(59, 130, 246, 0.4)',
                    yellow: 'rgba(234, 179, 8, 0.4)'
                } as const)[drawingColor]
                : 'rgba(59, 130, 246, 0.4)';

            // Toggle highlight on right-click
            const newSquares = { ...rightClickedSquares };
            if (newSquares[square]) {
                delete newSquares[square];
            } else {
                newSquares[square] = { backgroundColor: color };
            }
            setRightClickedSquares(newSquares);
        } else if (drawingMode === 'arrow') {
            // Arrow mode - free-hand drawing: start on mouse down, end on mouse up
            // This is handled by onSquareMouseDown and onSquareMouseUp
            // Right-click just prevents default context menu
        }
    };

    // Handle square mouse down for arrow/pen drawing
    const onSquareMouseDown = (square: string) => {
        if (mode === TEACHING_MODE || mode === PUZZLE_MODE) {
            if (drawingMode === 'arrow' || drawingMode === 'pen') {
                setArrowStart(square);
                setIsDrawingArrow(true);
                setIsPenMode(true);
                setArrowEnd(square);
            }
        }
    };

    // Handle square mouse enter for arrow/pen drawing (drag)
    const onSquareMouseEnter = (square: string) => {
        if ((drawingMode === 'arrow' || drawingMode === 'pen') && isDrawingArrow && arrowStart) {
            setArrowEnd(square);
        }
    };

    // Handle square mouse up to complete arrow/pen drawing
    const onSquareMouseUp = (square: string | null) => {
        if ((drawingMode === 'arrow' || drawingMode === 'pen') && isDrawingArrow && arrowStart) {
            // Use arrowEnd (which matches the preview) instead of recalculating square
            const finalEnd = arrowEnd || square;
            if (finalEnd && arrowStart !== finalEnd) {
                // Complete the arrow - use rgba format for consistency
                const arrowColor = {
                    'green': 'rgba(16, 185, 129, 0.8)',
                    'red': 'rgba(239, 68, 68, 0.8)',
                    'blue': 'rgba(59, 130, 246, 0.8)',
                    'yellow': 'rgba(234, 179, 8, 0.8)'
                }[drawingColor];
                    
                // Check if arrow already exists to avoid duplicates
                const arrowExists = arrows.some(arr => arr[0] === arrowStart && arr[1] === finalEnd);
                if (!arrowExists) {
                    setArrows([...arrows, [arrowStart, finalEnd, arrowColor]]);
                }
            }
            setArrowStart(null);
            setIsDrawingArrow(false);
            setIsPenMode(false);
            setArrowEnd(null);
        }
    };

    // Helper function to get square from mouse event coordinates
    const getSquareFromMouseEvent = (e: React.MouseEvent | MouseEvent | React.DragEvent, _boardWidth: number): string | null => {
        // Try several selectors react-chessboard might use; fall back to boardContainerRef itself
        const chessboardElement = (
            boardContainerRef.current?.querySelector('[data-testid="board-wrapper"]') ||
            boardContainerRef.current?.querySelector('.react-chessboard') ||
            boardContainerRef.current?.querySelector('div[style*="position"]') ||
            boardContainerRef.current
        ) as HTMLElement | null;
        if (!chessboardElement) return null;
        
        const rect = chessboardElement.getBoundingClientRect();
        if (!rect || rect.width === 0) return null;

        // Get mouse position relative to the chessboard element
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Use the ACTUAL rendered rect width for accurate square detection
        const squareSize = rect.width / 8;
        
        // Calculate which square the mouse is over
        let file = Math.floor(x / squareSize);
        let rank = Math.floor(y / squareSize);

        // Clamp to valid range
        file = Math.max(0, Math.min(7, file));
        rank = Math.max(0, Math.min(7, rank));

        const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
        const ranks = ['1', '2', '3', '4', '5', '6', '7', '8'];

        // React-chessboard uses standard chess notation where rank 0 is rank 8 (top) for white orientation
        // For white orientation: rank 0 = rank 8, rank 7 = rank 1
        // For black orientation: rank 0 = rank 1, rank 7 = rank 8, and files are reversed
        if (boardOrientation === 'black') {
            const adjustedFile = 7 - file;
            const adjustedRank = 7 - rank;
            return files[adjustedFile] + ranks[adjustedRank];
        } else {
            // White orientation: ranks are inverted (0 = 8, 7 = 1)
            const adjustedRank = 7 - rank;
            return files[file] + ranks[adjustedRank];
        }
    };

    // Teacher View - Show all student boards in grid (Chesslang style) OR teacher's own puzzle solving (screen share mode)
    console.log('🔍 Debug teacher view:', { userRole, isInMeeting, isBoardShared, studentPositions: studentPositions.size, shareMode, mode });
    // For teaching mode, don't show the monitoring view - just show teaching interface
    if (isTeacher(userRole) && isInMeeting && isBoardShared && !isTeachingMode(mode)) {
        return (
            <div className="space-y-3 h-full flex flex-col">
                {/* Compact Header with Controls */}
                <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-3 rounded-lg flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div>
                            <h3 className="text-base font-bold flex items-center gap-2">
                                {shareMode === 'collaboration' ? '♟️ Live Monitoring' : '📺 Screen Sharing'} - {currentPuzzle?.description || 'Puzzle'}
                            </h3>
                            <p className="text-xs text-purple-100">
                                {shareMode === 'collaboration' 
                                    ? `${studentPositions.size} students solving • Rating: ${currentPuzzle?.rating || 'N/A'}`
                                    : 'Students are watching you solve the puzzle'
                                }
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                        <button
                                onClick={() => setBoardOrientation(boardOrientation === 'white' ? 'black' : 'white')}
                                className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-medium transition-colors"
                        >
                                🔄 Flip Boards
                        </button>
                            <button
                                onClick={rebroadcastBoard}
                                className="px-3 py-1.5 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-xs font-medium transition-colors"
                                title="Rebroadcast to all participants"
                            >
                                📡 Rebroadcast
                        </button>
                            <button
                                onClick={stopSharingBoard}
                                className="px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-medium transition-colors"
                            >
                                ⏹️ Stop
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content - Student Boards Grid - Chesslang Style - Takes full space */}
                <div className="bg-gray-800 rounded-lg p-3 flex-1 flex flex-col">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-white text-sm flex items-center gap-2">
                                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                                Live Student Progress ({studentPositions.size})
                            </h4>
                            <div className="text-xs text-gray-400">
                                {studentPositions.size} solving
                            </div>
                        </div>
                    
                        {studentPositions.size === 0 ? (
                            <div className="text-center py-16 text-gray-400">
                            <div className="mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto opacity-50">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="9" cy="7" r="4"></circle>
                                    <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                </svg>
                            </div>
                                <p className="text-sm">Waiting for students to start solving...</p>
                                <p className="text-xs mt-1 text-gray-500">Their boards will appear here in real-time</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                            {Array.from(studentPositions.values())
                                .sort((a, b) => {
                                    // Completed puzzles go to bottom, active ones on top
                                    if (a.completed && !b.completed) return 1;
                                    if (!a.completed && b.completed) return -1;
                                    return b.timestamp - a.timestamp;
                                })
                                .map((student, idx) => (
                                    <div 
                                        key={student.studentName}
                                        className={`rounded-lg p-2 border-2 transition-all ${
                                            student.completed 
                                                ? 'bg-green-900/50 border-green-500 shadow-lg shadow-green-500/50' 
                                                : 'bg-gray-700 border-gray-600 hover:border-emerald-500'
                                        }`}
                                    >
                                        {/* Student Info Header */}
                                        <div className="flex items-center justify-between mb-1.5">
                                            <div className="flex items-center gap-1 flex-1 min-w-0">
                                                <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                                    student.completed 
                                                        ? 'bg-green-500 text-white' 
                                                        : 'bg-emerald-600 text-white'
                                                }`}>
                                                    {student.completed ? '✓' : idx + 1}
                                                </span>
                                                <span className="font-semibold text-white text-xs truncate">
                                                    {student.studentName}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Mini Chess Board */}
                                        <div className="relative mb-1.5">
                                            {/* Zoom Controls - Outside the transform container */}
                                            <div className="chess-zoom-controls" style={{ position: 'absolute', right: '12px', top: '-40px', zIndex: 50 }}>
                                                <button
                                                    onClick={() => {
                                                        const currentZoom = studentZoomLevels[student.studentName] || 100;
                                                        const newZoom = Math.max(50, currentZoom - 10);
                                                        setStudentZoomLevels(prev => ({
                                                            ...prev,
                                                            [student.studentName]: newZoom
                                                        }));
                                                        console.log('🔍 Zoom out:', student.studentName, newZoom);
                                                    }}
                                                    title="Zoom Out"
                                                    className="bg-gray-800 hover:bg-gray-700 text-white"
                                                >
                                                    −
                                                </button>
                                                <div className="zoom-level bg-gray-800 text-white">{studentZoomLevels[student.studentName] || 100}%</div>
                                                <button
                                                    onClick={() => {
                                                        const currentZoom = studentZoomLevels[student.studentName] || 100;
                                                        const newZoom = Math.min(150, currentZoom + 10);
                                                        setStudentZoomLevels(prev => ({
                                                            ...prev,
                                                            [student.studentName]: newZoom
                                                        }));
                                                        console.log('🔍 Zoom in:', student.studentName, newZoom);
                                                    }}
                                                    title="Zoom In"
                                                    className="bg-gray-800 hover:bg-gray-700 text-white"
                                                >
                                                    +
                                                </button>
                                            </div>
                                            
                                            {/* Board Container - With transform */}
                                            <div 
                                                className={`chess-board-container ${student.completed ? 'opacity-75' : ''}`}
                                                style={{
                                                    transform: `scale(${(studentZoomLevels[student.studentName] || 100) / 100})`,
                                                    transformOrigin: 'top center',
                                                    maxWidth: '100%'
                                                }}
                                            >
                                            <Chessboard
                                                position={student.fen}
                                                boardOrientation={boardOrientation}
                                                customBoardStyle={{
                                                    borderRadius: '4px'
                                                }}
                                                arePiecesDraggable={false}
                                            />
                                        </div>
                                        </div>

                                        {/* Status with Multi-Step Progress */}
                                        <div className={`text-center rounded px-2 py-1 ${
                                            student.completed 
                                                ? 'bg-green-600' 
                                                : 'bg-gray-600'
                                        }`}>
                                            <span className="text-[10px] font-mono text-white">
                                                {student.completed 
                                                    ? `✓ ${student.totalMoves || 0} moves` 
                                                    : student.lastMove
                                                }
                                            </span>
                                            
                                            {/* Multi-Step Progress Dots */}
                                            {currentPuzzle && !student.completed && (
                                                <div className="flex justify-center gap-0.5 mt-1">
                                                    {Array.from({ length: currentPuzzle.moves.split(' ').length }, (_, i) => {
                                                        const completedMoves = student.totalMoves || 0;
                                                        const totalMovesLocal = currentPuzzle.moves.split(' ').length;
                                                        if (totalMovesLocal <= 1) return null;
                                                        return (
                                                            <div
                                                                key={i}
                                                                className={`w-1 h-1 rounded-full ${
                                                                    i < completedMoves
                                                                        ? 'bg-green-400'
                                                                        : i === completedMoves
                                                                        ? 'bg-yellow-400'
                                                                        : 'bg-gray-400'
                                                                }`}
                                                                title={`Move ${i + 1}${i < completedMoves ? ' ✓' : i === completedMoves ? ' (current)' : ''}`}
                                                            />
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            }
                            </div>
                        )}
                </div>
            </div>
        );
    }

    // Student View - Minimal UI for solving / viewing
    if (isStudent(userRole) && isInMeeting) {
        // If teacher is sharing board view, show the board even without a puzzle
        if (shareMode === 'view' && isBoardShared) {
            return (
                <div className="h-full w-full flex items-center justify-center px-4 py-6">
                    <div className="w-full" style={{ maxWidth: studentCardMaxWidth }}>
                        <div className="rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4">
                                <h3 className="font-semibold text-sm tracking-wide uppercase text-blue-100">
                                    Teacher Demonstration
                                </h3>
                                <p className="text-xs text-blue-50 mt-1">
                                    Watching live moves from your mentor
                                </p>
                            </div>
                            <div className="p-6">
                                <div className="flex flex-col items-center gap-4">
                                    <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                                        Shared Board
                                    </div>
                                    <div
                                        className="rounded-xl border border-slate-200 bg-slate-50 p-3"
                                        style={{ width: studentBoardWidth, maxWidth: '100%' }}
                                    >
                                        <Chessboard
                                            position={game.fen()}
                                            onPieceDrop={() => false}
                                            arePiecesDraggable={false}
                                            boardOrientation={boardOrientation}
                                            boardWidth={studentBoardWidth}
                                            customDarkSquareStyle={{ backgroundColor: '#b58863' }}
                                            customLightSquareStyle={{ backgroundColor: '#f0d9b5' }}
                                        />
                                    </div>
                                    <div className="flex flex-col items-center gap-1 text-xs text-slate-500">
                                        <span className="flex items-center gap-2">
                                            <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
                                            Live broadcast in progress
                                        </span>
                                        <span>Follow along with the teacher’s explanation.</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // Waiting for puzzle
        if (!currentPuzzle) {
            return (
                <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg">
                    <div className="text-center px-6">
                        <div className="mb-4">
                            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto text-purple-400 animate-pulse">
                                <path d="M12 2l2 4h4l-3 3 1 4-4-2-4 2 1-4-3-3h4z"></path>
                                <rect x="2" y="14" width="20" height="8" rx="1"></rect>
                            </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-white mb-2">
                            Waiting for Teacher
                        </h3>
                        <p className="text-gray-400 text-sm">
                            The teacher will share a puzzle soon. Get ready to solve it!
                        </p>
                        <div className="mt-4 flex items-center justify-center gap-2">
                            <span className="w-2 h-2 bg-purple-400 rounded-full animate-ping"></span>
                            <span className="text-xs text-gray-500">Connected</span>
                        </div>
                    </div>
                </div>
            );
        }

        // Student has puzzle - minimal solving UI
        return (
            <div className="h-full w-full flex items-center justify-center px-4 py-6" style={{ overflow: 'auto' }}>
                <div className="w-full" style={{ maxWidth: studentCardMaxWidth, margin: '0 auto' }}>
                    <div className="rounded-2xl border border-slate-200 bg-white shadow-xl" style={{ overflow: 'visible' }}>
                        {/* Puzzle Info */}
                        <div className={`bg-gradient-to-r ${isTeachingMode(mode) ? 'from-blue-600 to-indigo-600' : 'from-green-600 to-emerald-600'} text-white px-6 py-5`}>
                            <div className="flex flex-col gap-2 text-center">
                                <h3 className="text-base font-semibold tracking-tight">
                                    {currentPuzzle.description || 'Solve the Puzzle'}
                                </h3>
                                {currentPuzzle.hint && isPuzzleMode(mode) && (
                                    <div className="flex flex-col gap-1">
                                        <p className="text-xs text-white/85">
                                            💡 {getContextualHint(currentPuzzle, puzzleProgress.length)}
                                        </p>
                                        {(() => {
                                            const moveParts = currentPuzzle.moves.split(' ').filter(m => m && !m.match(/^\d+\.?$/) && !m.match(/^(1-0|0-1|1\/2-1\/2|\*)$/));
                                            if (moveParts.length > 1 && puzzleProgress.length < moveParts.length) {
                                                return (
                                                    <p className="text-[10px] text-white/70">
                                                        {getHintProgress(moveParts.length, puzzleProgress.length)}
                                                    </p>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>
                                )}
                                <div className="flex flex-wrap items-center justify-center gap-3 text-xs text-white/90">
                                    <span className="px-2 py-0.5 rounded-full bg-white/15">
                                        Rating: {currentPuzzle.rating}
                                    </span>
                                    <span className="px-2 py-0.5 rounded-full bg-white/15 capitalize">
                                        {currentPuzzle.complexity || 'intermediate'}
                                    </span>
                                    {mode === 'puzzle' && (
                                        <span className="px-2 py-0.5 rounded-full bg-white/15">
                                            🤝 Collaboration Mode
                                        </span>
                                    )}
                                    {puzzleCompleted && (
                                        <span className="px-2 py-0.5 rounded-full bg-emerald-500/90 text-white font-semibold">
                                            ✓ Solved!
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-6 space-y-6">
                            {/* Board */}
                            <div className="flex flex-col items-center gap-4">
                                <div className="relative w-full flex justify-center">
                                    {/* Zoom Controls - Teachers only */}
                                    {isTeacher(userRole) && (
                                        <div className="chess-zoom-controls absolute right-0 -top-12 z-50">
                                            <button
                                                onClick={() => {
                                                    const newZoom = Math.max(50, mainZoomLevel - 10);
                                                    setMainZoomLevel(newZoom);
                                                    console.log('🔍 Main board zoom out:', newZoom);
                                                }}
                                                title="Zoom Out"
                                                className="bg-gray-800 hover:bg-gray-700 text-white"
                                            >
                                                −
                                            </button>
                                            <div className="zoom-level bg-gray-800 text-white">{mainZoomLevel}%</div>
                                            <button
                                                onClick={() => {
                                                    const newZoom = Math.min(150, mainZoomLevel + 10);
                                                    setMainZoomLevel(newZoom);
                                                    console.log('🔍 Main board zoom in:', newZoom);
                                                }}
                                                title="Zoom In"
                                                className="bg-gray-800 hover:bg-gray-700 text-white"
                                            >
                                                +
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setMainZoomLevel(100);
                                                    console.log('🔍 Main board zoom reset');
                                                }}
                                                title="Reset Zoom"
                                                className="bg-gray-800 hover:bg-gray-700 text-white text-xs px-2"
                                            >
                                                Reset
                                            </button>
                                        </div>
                                    )}

                                    {/* Board Container */}
                                    <div
                                        ref={boardContainerRef}
                                        className={`chess-board-container ${isTeachingMode(mode) ? 'relative' : ''} ${boardShake ? 'board-shake' : ''}`}
                                        style={{
                                            transform: isTeacher(userRole) ? `scale(${mainZoomLevel / 100})` : 'none',
                                            transformOrigin: 'top center',
                                            width: '100%',
                                            maxWidth: `${studentBoardWidth}px`,
                                            margin: '0 auto',
                                            position: 'relative',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            transition: boardShake ? 'none' : 'transform 0.3s ease'
                                        }}
                                        onMouseDown={(e) => {
                                            // Start arrow drawing on right-click drag
                                            if (drawingMode === 'arrow' && (mode === 'teaching' || mode === 'puzzle') && e.button === 2) {
                                                e.preventDefault();
                                                const square = getSquareFromMouseEvent(e, studentBoardWidth);
                                                if (square) {
                                                    onSquareMouseDown(square);
                                                }
                                            }
                                        }}
                                        onMouseMove={(e) => {
                                            // Continue arrow drawing while dragging
                                            if (drawingMode === 'arrow' && isDrawingArrow && e.buttons === 2) {
                                                e.preventDefault();
                                                const square = getSquareFromMouseEvent(e, studentBoardWidth);
                                                if (square && square !== arrowEnd) {
                                                    onSquareMouseEnter(square);
                                                }
                                            }
                                        }}
                                        onMouseUp={(e) => {
                                            // Complete arrow drawing
                                            if (drawingMode === 'arrow' && isDrawingArrow && e.button === 2) {
                                                e.preventDefault();
                                                const square = getSquareFromMouseEvent(e, studentBoardWidth);
                                                if (square) {
                                                    onSquareMouseUp(square);
                                                }
                                            }
                                        }}
                                        onContextMenu={(e) => {
                                            // Prevent default context menu when in arrow drawing mode
                                            if (drawingMode === 'arrow' && (mode === 'teaching' || mode === 'puzzle')) {
                                                e.preventDefault();
                                            }
                                        }}
                                    >
                                        <Chessboard
                                            position={game.fen()}
                                            onPieceDrop={onDrop}
                                            arePiecesDraggable={!(userRole === 'student' && shareMode === 'view')}
                                            boardOrientation={boardOrientation}
                                            animationDuration={isNeoTeachingBoard ? 0 : 300}
                                            {...(isNeoTeachingBoard ? {
                                                showBoardNotation: true,
                                                customNotationStyle: {
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    color: LICHESS_TEXT_MUTED
                                                }
                                            } : {})}
                                            boardWidth={studentBoardWidth}
                                            onSquareRightClick={onSquareRightClick}
                                            customArrows={[...arrows, ...hintArrows] as any}
                                            customSquareStyles={rightClickedSquares}
                                            customBoardStyle={{
                                                borderRadius: isNeoEditorUi ? 2 : '12px',
                                                boxShadow: isNeoEditorUi ? 'none' : '0 12px 24px -10px rgba(15, 23, 42, 0.35)',
                                                opacity: 1,
                                                ...(isNeoEditorUi ? { border: `1px solid ${LICHESS_PANEL_BORDER}` } : {})
                                            }}
                                            customDarkSquareStyle={{
                                                backgroundColor: isNeoTeachingBoard
                                                    ? LICHESS_BOARD_DARK
                                                    : '#b58863'
                                            }}
                                            customLightSquareStyle={{
                                                backgroundColor: isNeoTeachingBoard
                                                    ? LICHESS_BOARD_LIGHT
                                                    : '#f0d9b5'
                                            }}
                                        />
                                        
                                        {/* Shake Animation CSS */}
                                        <style>{`
                                            @keyframes shake {
                                                0%, 100% { transform: translateX(0); }
                                                10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
                                                20%, 40%, 60%, 80% { transform: translateX(8px); }
                                            }
                                            
                                            .board-shake {
                                                animation: shake 0.5s ease-in-out;
                                            }
                                            
                                            .chess-board-container img, 
                                            .chess-board-container svg {
                                                -webkit-user-drag: ${(userRole === 'student' && shareMode === 'view') ? 'none' : 'auto'};
                                                user-select: ${(userRole === 'student' && shareMode === 'view') ? 'none' : 'auto'};
                                            }
                                        `}</style>
                                    </div>
                                </div>

                                {/* Primary Controls */}
                                <div className="flex flex-col gap-2">
                                    <div className="flex flex-wrap justify-center gap-3 text-sm">
                                        <button
                                            onClick={() => setBoardOrientation(boardOrientation === 'white' ? 'black' : 'white')}
                                            className="px-4 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium transition-colors"
                                        >
                                            🔄 Flip Board
                                        </button>
                                        {mode === 'puzzle' && (
                                            <button
                                                onClick={resetPuzzle}
                                                className="px-4 py-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-medium transition-colors"
                                            >
                                                🔁 Reset Puzzle
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setShowHint(!showHint)}
                                            className={`px-4 py-2 rounded-full font-medium transition-colors ${showHint ? 'bg-yellow-500 text-white hover:bg-yellow-600' : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'}`}
                                            title={showHint ? 'Hide which piece to move' : 'Show which piece to move (you figure out where!)'}
                                        >
                                            💡 {showHint ? 'Hide Hint' : 'Highlight Piece'}
                                        </button>
                                        <button
                                            onClick={() => {
                                                const newValue = !soundEnabled;
                                                setSoundEnabled(newValue);
                                                chessSounds.setEnabled(newValue);
                                                localStorage.setItem('chessSoundEnabled', String(newValue));
                                                toast.info(newValue ? '🔊 Sound enabled' : '🔇 Sound muted');
                                            }}
                                            className={`px-4 py-2 rounded-full font-medium transition-colors ${soundEnabled ? 'bg-slate-100 hover:bg-slate-200 text-slate-700' : 'bg-red-100 hover:bg-red-200 text-red-700'}`}
                                            title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
                                        >
                                            {soundEnabled ? '🔊' : '🔇'} Sound
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => setShowGuide(true)}
                                        className="w-10 h-10 rounded-full bg-blue-500 hover:bg-blue-600 text-white font-bold text-lg transition-colors flex items-center justify-center"
                                        title="Show help guide"
                                    >
                                        ?
                                    </button>
                                </div>
                            </div>

                            {/* Puzzle Progress */}
                            {currentPuzzle && (
                                <div className={`rounded-xl border px-3 py-2 transition-all ${
                                    puzzleCompleted 
                                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300' 
                                        : 'bg-slate-50 border-slate-200'
                                }`}>
                                    {(() => {
                                        // Properly count moves - filter out move numbers (e.g., "1.", "2.") and empty strings
                                        const moveParts = currentPuzzle.moves.split(' ').filter(m => m && !m.match(/^\d+\.?$/));
                                        const totalMoves = moveParts.length;
                                        const completedMoves = puzzleProgress.length;

                                        if (totalMoves > 1) {
                                            return (
                                                <div className="space-y-2">
                                                    {/* Completion Badge */}
                                                    {puzzleCompleted && (
                                                        <div className="flex items-center justify-center gap-2 py-1">
                                                            <span className="text-2xl">🎉</span>
                                                            <span className="text-sm font-bold text-green-700">Puzzle Solved!</span>
                                                            <span className="text-2xl">✓</span>
                                                        </div>
                                                    )}
                                                    
                                                    <div className="flex flex-col items-center gap-2">
                                                        <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                                                            <span className="font-medium">Progress</span>
                                                            <span className="text-slate-400">•</span>
                                                            <span className={puzzleCompleted ? 'text-green-700 font-bold' : ''}>{completedMoves}/{totalMoves}</span>
                                                        </div>
                                                        <div className="flex items-center gap-0.5">
                                                            {Array.from({ length: totalMoves }, (_, i) => (
                                                                <div
                                                                    key={i}
                                                                    className={`w-2 h-2 rounded-full border ${
                                                                        i < completedMoves
                                                                            ? 'bg-emerald-500 border-emerald-500'
                                                                            : i === completedMoves
                                                                            ? 'bg-yellow-400 border-yellow-400 animate-pulse'
                                                                            : 'bg-slate-200 border-slate-300'
                                                                    }`}
                                                                    title={`Move ${i + 1}${i < completedMoves ? ' ✓' : i === completedMoves ? ' (current)' : ''}`}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {puzzleProgress.length > 0 && (
                                                        <p className="text-[10px] text-slate-500 text-center">
                                                            <span className="font-mono font-semibold text-emerald-600">{puzzleProgress.join(' → ')}</span>
                                                        </p>
                                                    )}

                                                    {completedMoves < totalMoves && isPuzzleMode(mode) && (
                                                        <p className="text-[10px] text-blue-600 text-center">
                                                            Next: move #{completedMoves + 1}
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        }

                                        return (
                                            <p className="text-xs text-slate-500 text-center">
                                                Single move puzzle — find the winning idea!
                                            </p>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Show collaborative view if active
    if (showCollaborativeView && collaborativeSessionId && currentPuzzle) {
        return (
            <CollaborativePuzzleView
                sessionId={collaborativeSessionId}
                subscriptionId={activeSubscriptionId}
                puzzle={currentPuzzle}
                userId={loggedInUserId}
                userName={loggedInUserName}
                userRole={userRole || 'student'}
                onClose={() => {
                    setShowCollaborativeView(false);
                    setCollaborativeSessionId(null);
                }}
            />
        );
    }

    /** One Lichess-style spare row: pointer, 6 pieces, trash (lila `view.ts` sparePieces). */
    const lichessSpareRow = (forColor: 'w' | 'b') => {
        const sym =
            forColor === 'w'
                ? ({ K: '♔', Q: '♕', R: '♖', B: '♗', N: '♘', P: '♙' } as const)
                : ({ K: '♚', Q: '♛', R: '♜', B: '♝', N: '♞', P: '♟' } as const);
        const pcs = ['K', 'Q', 'R', 'B', 'N', 'P'] as const;
        const pieceSelected = (p: string) =>
            selectedTrayPiece?.color === forColor && selectedTrayPiece?.piece === p;
        const cellBase =
            'flex items-center justify-center border-r border-black/25 last:border-r-0 cursor-pointer transition-colors duration-150 select-none';
        const barMax = boardWidthPx ? `${Math.min(boardWidthPx + 4, 560)}px` : '560px';
        const pieceBg = forColor === 'w' ? LICHESS_BOARD_LIGHT : LICHESS_BOARD_DARK;
        return (
            <div
                className="flex w-full overflow-hidden rounded-[3px] shadow-[inset_0_3px_5px_rgba(0,0,0,0.32)]"
                style={{
                    maxWidth: barMax,
                    background: LICHESS_SPARE_BAR_BG
                }}
            >
                <button
                    type="button"
                    title="Pointer"
                    className={`${cellBase} flex-[0_0_12.5%] aspect-square rounded-l-[3px] hover:bg-white/12`}
                    style={{
                        background:
                            neoEditorSpare === 'pointer' && !selectedTrayPiece
                                ? 'rgba(129, 193, 75, 0.42)'
                                : 'transparent'
                    }}
                    onClick={() => {
                        setNeoEditorSpare('pointer');
                        setSelectedTrayPiece(null);
                        setInteractionMode('move');
                    }}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
                        <path
                            d="M5.5 3.21V20.8l12.86-8.79L5.5 3.21z"
                            fill="#ececea"
                        />
                    </svg>
                </button>
                {pcs.map((p) => (
                    <div
                        key={`${forColor}-${p}`}
                        role="button"
                        tabIndex={0}
                        draggable
                        onDragStart={(e) => {
                            setNeoEditorSpare('none');
                            (window as any).draggedPieceFromTray = `${forColor}${p}`;
                            e.dataTransfer.setData('text/plain', `${forColor}${p}`);
                            e.dataTransfer.effectAllowed = 'copy';
                            // Build a crisp drag image from the cell itself
                            const el = e.currentTarget as HTMLElement;
                            const rect = el.getBoundingClientRect();
                            try { e.dataTransfer.setDragImage(el, rect.width / 2, rect.height / 2); } catch {}
                        }}
                        onDragEnd={() => {
                            (window as any).draggedPieceFromTray = null;
                        }}
                        onClick={() => {
                            setNeoEditorSpare('none');
                            if (pieceSelected(p)) setSelectedTrayPiece(null);
                            else setSelectedTrayPiece({ piece: p, color: forColor });
                        }}
                        className={`${cellBase} flex-[0_0_12.5%] aspect-square text-[clamp(1.15rem,3.6vw,1.9rem)] leading-none cursor-grab active:cursor-grabbing hover:brightness-[1.07] active:brightness-[0.95]`}
                        style={{
                            background: pieceBg,
                            color: forColor === 'w' ? '#1a1a1a' : '#f5f5f4',
                            boxShadow: pieceSelected(p) ? `inset 0 0 0 2px ${LICHESS_ACCENT}` : undefined
                        }}
                    >
                        {sym[p]}
                    </div>
                ))}
                <button
                    type="button"
                    title="Delete"
                    className={`${cellBase} flex-[0_0_12.5%] aspect-square rounded-r-[3px] text-xl hover:bg-white/12`}
                    style={{
                        background:
                            neoEditorSpare === 'trash' && !selectedTrayPiece
                                ? 'rgba(185, 74, 72, 0.42)'
                                : 'transparent'
                    }}
                    onClick={() => {
                        setNeoEditorSpare('trash');
                        setSelectedTrayPiece(null);
                        setInteractionMode('delete');
                    }}
                >
                    <span style={{ color: '#ececea', fontSize: '1.1rem' }} aria-hidden>
                        🗑
                    </span>
                </button>
            </div>
        );
    };

    // Regular view (Student with puzzle or Teacher when not sharing)
    return (
        <>
        <div
            className={`h-full w-full flex flex-col ${isNeoEditorUi ? '' : 'bg-gray-50'}`}
            style={isNeoEditorUi ? { backgroundColor: LICHESS_PAGE_BG } : undefined}
        >
            {/* Modern Header (hidden for NEO — Chequered page already shows a bar; matches Lichess editor chrome) */}
            {!isNeoEditorUi && (
            <div
                className={`flex-shrink-0 px-6 py-3 border-b ${isNeoEditorUi ? '' : 'bg-white border-gray-200'}`}
                style={
                    isNeoEditorUi
                        ? { backgroundColor: LICHESS_PANEL_BG, borderColor: LICHESS_PANEL_BORDER }
                        : undefined
                }
            >
                <div className="flex items-center justify-between">
                    <div className={`flex items-center gap-3 flex-wrap ${isNeoEditorUi ? 'text-[#bababa]' : ''}`}>
                        {mode === PUZZLE_MODE && (
                            <h2 className={`text-lg font-bold flex items-center gap-2 ${isNeoEditorUi ? 'text-[#bababa]' : 'text-gray-900'}`}>
                                🎯 Puzzle Mode
                            </h2>
                            )}
                        {currentPuzzle && mode === PUZZLE_MODE && (
                            <>
                                <span className={`text-sm font-medium ${isNeoEditorUi ? 'text-[#bababa]' : 'text-gray-700'}`}>
                                    {currentPuzzle.description}
                                </span>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    currentPuzzle.complexity === 'beginner' ? 'bg-green-100 text-green-700' :
                                    currentPuzzle.complexity === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-red-100 text-red-700'
                                }`}>
                                    {currentPuzzle.complexity}
                        </span>
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                    Rating: {currentPuzzle.rating}
                                </span>
                            </>
                        )}
                    </div>
                    {serviceProviderId && subscriptionList.length > 0 && (
                        <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold uppercase tracking-wide ${isNeoEditorUi ? 'text-[#8c8b88]' : 'text-gray-600'}`}>
                                Subscription
                            </span>
                            <select
                                value={activeSubscriptionId}
                                onChange={(e) => setSelectedSubscriptionId(e.target.value)}
                                className={`text-sm rounded px-3 py-2 focus:outline-none focus:ring-2 transition ${
                                    isNeoEditorUi
                                        ? 'border border-[#3d3a36] bg-[#262421] text-[#bababa] focus:ring-[#81c14b] focus:border-[#81c14b]'
                                        : 'rounded-lg border border-gray-200 bg-gray-50 shadow-sm focus:ring-purple-500 focus:border-purple-500'
                                }`}
                            >
                                {subscriptionList.map((sub) => (
                                    <option key={sub.id} value={sub.id}>
                                        {sub.Name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    {/* LiveKit Sharing - Prominent for Teachers */}
                    {isInMeeting && userRole === 'teacher' && (
                        <div className="flex items-center gap-2">
                            {/* Show loading status */}
                            {loadingPuzzles && mode === PUZZLE_MODE && (
                                <div className="px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 flex items-center gap-2">
                                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    <span>
                                        Loading puzzles from class puzzles...
                                    </span>
                                </div>
                            )}
                            {/* Show message if no puzzles in class puzzles */}
                            {!loadingPuzzles && firestorePuzzles.length === 0 && !currentPuzzle && mode === PUZZLE_MODE && (
                                <div className="px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800 flex items-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <span>
                                        No puzzles in class puzzles. Please add puzzles in <strong>Chequered</strong> to share with students.
                                    </span>
                                </div>
                            )}
                            {!isBoardShared && mode === PUZZLE_MODE && currentPuzzle && (
                                <button
                                    onClick={shareBoardWithStudents}
                                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg text-sm font-semibold transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                                    title="Share puzzle with students via LiveKit"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                    </svg>
                                    Share Puzzle
                                </button>
                            )}
                            {isBoardShared && (
                                <button
                                    onClick={stopSharingBoard}
                                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    Stop Sharing
                                </button>
                            )}
                            {isInMeeting && userRole !== 'teacher' && (
                                <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-medium flex items-center gap-2">
                                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                    Live Session
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
            )}

            {/* Main Content Area - Board Left, Controls Right - Full Width, No Scroll */}
            <div
                className={`flex-1 overflow-hidden flex gap-2 p-2 w-full h-full ${isNeoEditorUi ? 'flex-col sm:flex-row' : 'flex-row'}`}
                style={isNeoEditorUi ? { backgroundColor: LICHESS_PAGE_BG } : undefined}
            >
                {/* Chess Board - Left Side - Full Height, Fits in Page */}
                <div
                    ref={boardAreaRef}
                    className={`flex-1 flex flex-col min-h-0 max-h-full relative ${isNeoEditorUi ? '' : 'bg-white rounded-xl shadow-lg'}`}
                    style={
                        isNeoEditorUi
                            ? {
                                  backgroundColor: LICHESS_PANEL_BG,
                                  border: `1px solid ${LICHESS_PANEL_BORDER}`,
                                  borderRadius: '2px'
                              }
                            : undefined
                    }
                >
                    <div className={`flex-1 flex flex-col min-h-0 max-h-full ${isNeoEditorUi ? 'p-0' : 'p-2'}`}>
                        {/* Board Controls Toolbar - Compact at Top (hidden on NEO — Lichess editor has no top annotation bar) */}
                        {!isNeoEditorUi && (
                        <div
                            className={`mb-2 flex flex-wrap items-center justify-between gap-2 pb-2 flex-shrink-0 border-b ${isNeoEditorUi ? 'border-[#3d3a36]' : 'border-gray-200'}`}
                        >
                                    <div className="flex flex-wrap items-center gap-2">
                                        {mode === PUZZLE_MODE && currentPuzzle && (
                                    <>
                                        <button
                                            onClick={resetPuzzle}
                                                    className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5"
                                                title="Reset puzzle to start"
                                        >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Reset
                                        </button>
                                        <button
                                            onClick={() => setShowHint(!showHint)}
                                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                                                showHint
                                                        ? 'bg-yellow-500 text-white shadow-sm'
                                                    : 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                                            }`}
                                                title="Show/hide hint arrow"
                                        >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                                </svg>
                                                Hint
                                        </button>
                                    </>
                                    )}
                                        {(Object.keys(rightClickedSquares).length > 0 || arrows.length > 0) && (
                                        <button
                                            onClick={clearAnnotations}
                                                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1.5 ${
                                                    isNeoEditorUi
                                                        ? 'bg-[#3d3a36] hover:bg-[#454240] text-[#bababa] border border-[#4d4a46]'
                                                        : 'bg-red-500 hover:bg-red-600 text-white rounded-lg'
                                                }`}
                                            title="Clear all annotations"
                                        >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                                Clear Annotations
                                                {(Object.keys(rightClickedSquares).length > 0 || arrows.length > 0) && (
                                                    <span className="ml-1 px-1.5 py-0.5 bg-red-700 text-white text-xs font-bold rounded-full">
                                                        {Object.keys(rightClickedSquares).length + arrows.length}
                                    </span>
                                                )}
                                        </button>
                                        )}
                                    </div>
                            </div>
                        )}

                        {/* Board Container - Full Space, No Scroll */}
                            {/* Hidden Piece Tray - Moved to Sidebar */}
                            {false && mode === 'teaching' && (
                                <div className="flex-shrink-0 w-48 flex flex-col gap-3 p-4 bg-gradient-to-b from-blue-100 to-purple-100 rounded-lg border-4 border-blue-500 shadow-xl">
                                    <div className="text-center">
                                        <div className="text-sm font-bold text-blue-900 mb-1 bg-white rounded py-2 px-3 shadow">
                                            🎨 Piece Tray
                                        </div>
                                        <p className="text-[10px] text-blue-800 mt-1 bg-blue-50 rounded px-2 py-1">
                                            Click piece → Click board square
                                        </p>
                                        {selectedTrayPiece ? (
                                            <div className="space-y-1">
                                                <div className="bg-green-500 text-white text-[10px] font-bold px-2 py-1 rounded animate-pulse">
                                                    Selected: {selectedTrayPiece.color === 'w' ? '⚪' : '⚫'} {
                                                        {
                                                            'K': 'King', 'Q': 'Queen', 'R': 'Rook',
                                                            'B': 'Bishop', 'N': 'Knight', 'P': 'Pawn'
                                                        }[selectedTrayPiece.piece]
                                                    }
                                                </div>
                                                <button
                                                    onClick={() => {
                                                        setSelectedTrayPiece(null);
                                                        toast.info('Piece deselected');
                                                    }}
                                                    className="w-full text-[10px] px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded font-semibold"
                                                >
                                                    ✖ Deselect
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="bg-gray-200 text-gray-600 text-[10px] px-2 py-1 rounded">
                                                No piece selected
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* White Pieces */}
                                    <div className="bg-white rounded-lg p-3 shadow-md border-2 border-gray-200">
                                        <div className="text-xs font-bold text-gray-700 mb-2 text-center pb-2 border-b border-gray-300">⚪ White Pieces</div>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { piece: 'K', symbol: '♔', name: 'King', count: '×1' },
                                                { piece: 'Q', symbol: '♕', name: 'Queen', count: '×1' },
                                                { piece: 'R', symbol: '♖', name: 'Rook', count: '×2' },
                                                { piece: 'B', symbol: '♗', name: 'Bishop', count: '×2' },
                                                { piece: 'N', symbol: '♘', name: 'Knight', count: '×2' },
                                                { piece: 'P', symbol: '♙', name: 'Pawn', count: '×8' }
                                            ].map(({ piece, symbol, name, count }) => (
                                                <div
                                                    key={`spare-w-${piece}`}
                                                    onClick={() => {
                                                        setSelectedTrayPiece({ piece, color: 'w' });
                                                        toast.success(`Selected White ${name}! Click on any board square to place it.`);
                                                    }}
                                                    className="relative group"
                                                >
                                                    <div className={`w-12 h-12 flex items-center justify-center bg-gradient-to-br from-white to-gray-50 border-2 rounded-lg cursor-pointer hover:shadow-lg hover:scale-110 transition-all text-3xl ${
                                                        selectedTrayPiece?.piece === piece && selectedTrayPiece?.color === 'w'
                                                            ? 'border-green-500 ring-2 ring-green-400 scale-110'
                                                            : 'border-gray-300 hover:border-blue-500'
                                                    }`}>
                                                        {symbol}
                                                    </div>
                                                    <div className="absolute -top-1 -right-1 bg-blue-600 text-white text-[8px] font-bold px-1 rounded-full">
                                                        {count}
                                                    </div>
                                                    <div className="text-[8px] text-center text-gray-600 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        {name}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Black Pieces */}
                                    <div className="bg-gray-800 rounded-lg p-3 shadow-md border-2 border-gray-600">
                                        <div className="text-xs font-bold text-white mb-2 text-center pb-2 border-b border-gray-600">⚫ Black Pieces</div>
                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { piece: 'K', symbol: '♚', name: 'King', count: '×1' },
                                                { piece: 'Q', symbol: '♛', name: 'Queen', count: '×1' },
                                                { piece: 'R', symbol: '♜', name: 'Rook', count: '×2' },
                                                { piece: 'B', symbol: '♝', name: 'Bishop', count: '×2' },
                                                { piece: 'N', symbol: '♞', name: 'Knight', count: '×2' },
                                                { piece: 'P', symbol: '♟', name: 'Pawn', count: '×8' }
                                            ].map(({ piece, symbol, name, count }) => (
                                                <div
                                                    key={`spare-b-${piece}`}
                                                    onClick={() => {
                                                        setSelectedTrayPiece({ piece, color: 'b' });
                                                        toast.success(`Selected Black ${name}! Click on any board square to place it.`);
                                                    }}
                                                    className="relative group"
                                                >
                                                    <div className={`w-12 h-12 flex items-center justify-center bg-gradient-to-br from-white to-gray-50 border-2 rounded-lg cursor-pointer hover:shadow-lg hover:scale-110 transition-all text-3xl ${
                                                        selectedTrayPiece?.piece === piece && selectedTrayPiece?.color === 'b'
                                                            ? 'border-yellow-400 ring-2 ring-yellow-300 scale-110'
                                                            : 'border-gray-400 hover:border-yellow-400'
                                                    }`}>
                                                        {symbol}
                                                    </div>
                                                    <div className="absolute -top-1 -right-1 bg-yellow-500 text-gray-900 text-[8px] font-bold px-1 rounded-full">
                                                        {count}
                                                    </div>
                                                    <div className="text-[8px] text-center text-gray-300 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        {name}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="text-center mt-2 space-y-1">
                                        <div className="p-2 bg-green-50 rounded border border-green-300">
                                            <p className="text-[10px] text-green-800 font-semibold">
                                                ✨ Unlimited pieces!<br/>
                                                Click any piece multiple times
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => {
                                                try {
                                                    const newGame = new Chess(game.fen());
                                                    // Remove both kings for truly empty board
                                                    newGame.remove('e1' as any);
                                                    newGame.remove('e8' as any);
                                                    setGame(newGame);
                                                    toast.success('Kings removed - board is now empty!');
                                                } catch (error) {
                                                    console.log('Could not remove kings, using different method');
                                                    // If that fails, just acknowledge it
                                                    toast.info('Tip: Click on kings to remove them');
                                                }
                                            }}
                                            className="w-full text-[10px] px-2 py-1 bg-orange-500 hover:bg-orange-600 text-white rounded font-semibold"
                                        >
                                            🗑️ Remove Kings (Empty Board)
                                        </button>
                                    </div>
                                </div>
                            )}
                            
                        {/* Lichess editor DOM: main#board-editor > .board-editor > .spare + .main-board > .cg-wrap (cf. lichess.org/editor page source) */}
                        {createElement(
                            isNeoEditorUi ? 'main' : 'div',
                            {
                                id: isNeoEditorUi ? 'board-editor' : undefined,
                                className: isNeoEditorUi
                                    ? 'flex w-full h-full min-h-0 min-w-0 flex-col overflow-y-auto overflow-x-hidden items-center justify-center gap-1 outline-none'
                                    : 'contents'
                            },
                            <>
                                <div
                                    className={
                                        isNeoEditorUi
                                            ? 'board-editor flex w-full flex-col items-center gap-2 min-w-0'
                                            : 'contents'
                                    }
                                >
                                {isNeoEditorUi && (
                                    <div
                                        className="spare flex w-full shrink-0 justify-center px-0.5"
                                        style={{ maxWidth: boardWidthPx > 0 ? boardWidthPx : undefined }}
                                    >
                                        {lichessSpareRow(boardOrientation === 'white' ? 'b' : 'w')}
                                    </div>
                                )}

                                <div
                                    className={
                                        isNeoEditorUi
                                            ? 'main-board w-full flex justify-center shrink-0 min-h-0 py-2'
                                            : 'contents'
                                    }
                                >
                                    <div
                                        ref={boardContainerRef}
                                        className={
                                            isNeoEditorUi
                                                ? 'cg-wrap relative flex w-full shrink-0 justify-center min-h-0'
                                                : 'flex-1 flex items-center justify-center min-h-0 overflow-auto relative'
                                        }
                                        onDragOver={(e) => {
                                            if ((window as any).draggedPieceFromTray) {
                                                e.preventDefault();
                                                e.dataTransfer.dropEffect = 'copy';
                                            }
                                        }}
                                        onDrop={(e) => {
                                            if (mode === TEACHING_MODE && (window as any).draggedPieceFromTray) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const draggedPiece = (window as any).draggedPieceFromTray as string;
                                                (window as any).draggedPieceFromTray = null;
                                                const square = getSquareFromMouseEvent(e, boardWidthPx);
                                                if (!square) return;
                                                const pieceColor = draggedPiece.charAt(0) === 'w' ? 'w' : 'b';
                                                const pieceType = draggedPiece.charAt(1).toLowerCase();
                                                const currentFen = game.fen();
                                                try {
                                                    setTeachingModeHistory([...teachingModeHistory, currentFen]);
                                                    setTeachingModeRedoHistory([]);
                                                    const updatedGame = buildBoardState(game, square, { type: pieceType, color: pieceColor });
                                                    setGame(updatedGame);
                                                    const pieceName = ({ k: 'King', q: 'Queen', r: 'Rook', b: 'Bishop', n: 'Knight', p: 'Pawn' } as any)[pieceType] || pieceType;
                                                    toast.success(`Placed ${pieceColor === 'w' ? 'White' : 'Black'} ${pieceName} on ${square.toUpperCase()}`);
                                                } catch (err: any) {
                                                    toast.error(`Could not place piece: ${err?.message || 'unknown error'}`);
                                                }
                                            }
                                        }}
                                    >
                            {/* Feedback Messages - Overlay on top of board */}
                            {(isCorrectMove !== null || puzzleCompleted) && (
                                <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-2">
                                    {isCorrectMove !== null && !puzzleCompleted && (
                                        <div className={`px-4 py-2 rounded-lg text-sm font-medium text-center shadow-lg ${
                                            isCorrectMove 
                                                ? 'bg-green-500 text-white' 
                                                : 'bg-red-500 text-white'
                                        }`}>
                                            {isCorrectMove ? '✅ Correct!' : '❌ Try again'}
                                        </div>
                                    )}
                                    {puzzleCompleted && (
                                        <div className="px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg text-sm font-semibold text-center shadow-lg">
                                            🎉 Puzzle Solved!
                                        </div>
                                    )}
                                </div>
                            )}
                            {/* Left Side Controls — hidden in NEO (Lichess editor uses the tools column) */}
                            {mode === TEACHING_MODE && !isNeoEditorUi && (
                                <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10 flex flex-col gap-2">
                                    <button
                                        onClick={undoTeachingMove}
                                        disabled={teachingModeHistory.length === 0}
                                        className={`w-10 h-10 rounded-sm flex items-center justify-center transition-all border ${
                                            isNeoEditorUi
                                                ? teachingModeHistory.length > 0
                                                    ? 'bg-[#3d3a36] hover:bg-[#454240] text-[#bababa] border-[#4d4a46] cursor-pointer'
                                                    : 'bg-[#262421] text-[#5c5a57] border-[#3d3a36] cursor-not-allowed'
                                                : teachingModeHistory.length > 0
                                                ? 'shadow-lg bg-blue-500 hover:bg-blue-600 text-white cursor-pointer rounded-full'
                                                : 'shadow-lg bg-gray-300 text-gray-500 cursor-not-allowed rounded-full'
                                        }`}
                                        title="Undo last move"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={redoTeachingMove}
                                        disabled={teachingModeRedoHistory.length === 0}
                                        className={`w-10 h-10 rounded-sm flex items-center justify-center transition-all border ${
                                            isNeoEditorUi
                                                ? teachingModeRedoHistory.length > 0
                                                    ? 'bg-[#3d3a36] hover:bg-[#454240] text-[#bababa] border-[#4d4a46] cursor-pointer'
                                                    : 'bg-[#262421] text-[#5c5a57] border-[#3d3a36] cursor-not-allowed'
                                                : teachingModeRedoHistory.length > 0
                                                ? 'shadow-lg bg-green-500 hover:bg-green-600 text-white cursor-pointer rounded-full'
                                                : 'shadow-lg bg-gray-300 text-gray-500 cursor-not-allowed rounded-full'
                                        }`}
                                        title="Redo last undone move"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => setBoardOrientation(boardOrientation === 'white' ? 'black' : 'white')}
                                        className={`w-10 h-10 rounded-sm flex items-center justify-center transition-all border cursor-pointer ${
                                            isNeoEditorUi
                                                ? 'bg-[#3d3a36] hover:bg-[#454240] text-[#bababa] border-[#4d4a46]'
                                                : 'rounded-full shadow-lg bg-indigo-500 hover:bg-indigo-600 text-white'
                                        }`}
                                        title="Flip board orientation"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={() => setShowGuide(true)}
                                        className={`w-10 h-10 rounded-sm flex items-center justify-center transition-all border ${
                                            isNeoEditorUi
                                                ? 'bg-[#3d3a36] hover:bg-[#454240] text-[#bababa] border-[#4d4a46] font-bold text-lg'
                                                : 'rounded-full shadow-lg bg-blue-500 hover:bg-blue-600 text-white font-bold text-lg'
                                        }`}
                                        title="Show help guide"
                                    >
                                        ?
                                    </button>
                                </div>
                            )}
                            
                            {/* Hidden Piece Tray - Not shown */}
                            {false && mode === 'teaching' && (
                                <div></div>
                            )}
                                <div 
                                    className="relative flex-shrink-0"
                                    style={{
                                        width: boardWidthPx || 900,
                                        height: boardWidthPx || 900,
                                        maxWidth: 'calc(100% - 2rem)',
                                        maxHeight: 'calc(100% - 2rem)',
                                        aspectRatio: '1 / 1'
                                    }}
                                        onDragOver={(e) => {
                                            // Allow drop from piece tray
                                            if ((window as any).draggedPieceFromTray) {
                                                e.preventDefault();
                                                e.dataTransfer.dropEffect = 'copy';
                                            }
                                        }}
                                        onDrop={(e) => {
                                            // Handle drop from piece tray
                                            if (mode === TEACHING_MODE && (window as any).draggedPieceFromTray) {
                                                e.preventDefault();
                                                const draggedPiece = (window as any).draggedPieceFromTray;
                                                const square = getSquareFromMouseEvent(e, boardWidthPx);
                                                if (square && draggedPiece) {
                                                    const pieceColor = draggedPiece.charAt(0) === 'w' ? 'w' : 'b';
                                                    const pieceType = draggedPiece.charAt(1).toLowerCase();
                                                    const currentFen = game.fen();
                                                    
                                                    // If validations are enforced, check if placement is valid
                                                    if (enforceValidations) {
                                                        // Check if placing this piece would create an invalid position
                                                        // For now, we'll allow placement but validate the final position
                                                        try {
                                                            const testGame = new Chess(game.fen());
                                                            // Try to place the piece
                                                            testGame.put({ type: pieceType as any, color: pieceColor as any }, square as any);
                                                            // If successful, use it
                                                            setTeachingModeHistory([...teachingModeHistory, currentFen]);
                setTeachingModeRedoHistory([]); // Clear redo history when making a new move
                                                            setGame(testGame);
                                                            const pieceName = {
                                                                'k': 'King', 'q': 'Queen', 'r': 'Rook',
                                                                'b': 'Bishop', 'n': 'Knight', 'p': 'Pawn'
                                                            }[pieceType] || pieceType;
                                                            toast.success(`✅ Placed ${pieceColor === 'w' ? 'White' : 'Black'} ${pieceName} on ${square.toUpperCase()}`);
                                                        } catch (error: any) {
                                                            toast.error(`Invalid placement: ${error.message || 'Position violates chess rules'}`);
                                                            return;
                                                        }
                                                    } else {
                                                        // No validation: free placement
                                                        try {
                                                            setTeachingModeHistory([...teachingModeHistory, currentFen]);
                setTeachingModeRedoHistory([]); // Clear redo history when making a new move
                                                            const updatedGame = buildBoardState(game, square, {
                                                                type: pieceType,
                                                                color: pieceColor
                                                            });
                                                            setGame(updatedGame);
                                                            const pieceName = {
                                                                'k': 'King', 'q': 'Queen', 'r': 'Rook',
                                                                'b': 'Bishop', 'n': 'Knight', 'p': 'Pawn'
                                                            }[pieceType] || pieceType;
                                                            toast.success(`✅ Placed ${pieceColor === 'w' ? 'White' : 'Black'} ${pieceName} on ${square.toUpperCase()}`);
                                                        } catch (error: any) {
                                                            console.error('Error placing piece:', error);
                                                            toast.error(`Error: ${error.message || 'Could not place piece'}`);
                                                        }
                                                    }
                                                }
                                                (window as any).draggedPieceFromTray = null;
                                            }
                                    }}
                                        onMouseDown={(e) => {
                                            // Check if clicking on a piece first - if so, allow normal drag behavior
                                            const target = e.target as HTMLElement;
                                            const isPiece = target.closest('img[src*="pieces"]') || target.closest('[class*="piece"]') || target.closest('svg');
                                            
                                            // If clicking on a piece, don't interfere with dragging
                                            if (isPiece) {
                                                return; // Allow piece dragging to work normally
                                            }
                                            
                                            // Pen mode: left-click drag (button === 0) - simpler drawing
                                            // Arrow mode: right-click drag (button === 2) - original method
                                            // Only if drawing mode is active and no piece is selected from tray
                                            const isPenClick = drawingMode === 'pen' && e.button === 0 && !selectedTrayPiece;
                                            const isArrowClick = drawingMode === 'arrow' && e.button === 2 && !selectedTrayPiece;
                                            
                                            if ((isPenClick || isArrowClick) && (mode === TEACHING_MODE || mode === PUZZLE_MODE)) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                const square = getSquareFromMouseEvent(e, boardWidthPx);
                                                if (square) {
                                                    onSquareMouseDown(square);
                                                }
                                            }
                                        }}
                                        onMouseMove={(e) => {
                                            // Don't interfere if user is dragging a piece (buttons === 1 but not in drawing mode)
                                            if (e.buttons === 1 && !isDrawingArrow) {
                                                return; // Allow piece dragging
                                            }
                                            
                                            // Pen mode: left-click drag (buttons === 1)
                                            // Arrow mode: right-click drag (buttons === 2)
                                            const isPenDrag = drawingMode === 'pen' && isDrawingArrow && e.buttons === 1;
                                            const isArrowDrag = drawingMode === 'arrow' && isDrawingArrow && e.buttons === 2;
                                            
                                            if (isPenDrag || isArrowDrag) {
                                                e.preventDefault();
                                                const square = getSquareFromMouseEvent(e, boardWidthPx);
                                                if (square && square !== arrowEnd) {
                                                    onSquareMouseEnter(square);
                                                }
                                            }
                                        }}
                                        onMouseUp={(e) => {
                                            // Pen mode: left-click release (button === 0)
                                            // Arrow mode: right-click release (button === 2)
                                            const isPenRelease = drawingMode === 'pen' && isDrawingArrow && e.button === 0;
                                            const isArrowRelease = drawingMode === 'arrow' && isDrawingArrow && e.button === 2;
                                            
                                            if (isPenRelease || isArrowRelease) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                // Use arrowEnd which matches the preview, don't recalculate
                                                onSquareMouseUp(null);
                                            }
                                        }}
                                        onMouseLeave={() => {
                                            // Complete drawing if mouse leaves the board
                                            if ((drawingMode === 'pen' || drawingMode === 'arrow') && isDrawingArrow) {
                                                if (arrowStart && arrowEnd && arrowStart !== arrowEnd) {
                                                    // Complete the arrow if we have a valid start and end - use rgba format
                                                    const arrowColor = {
                                                        'green': 'rgba(16, 185, 129, 0.8)',
                                                        'red': 'rgba(239, 68, 68, 0.8)',
                                                        'blue': 'rgba(59, 130, 246, 0.8)',
                                                        'yellow': 'rgba(234, 179, 8, 0.8)'
                                                    }[drawingColor];
                                                    const arrowExists = arrows.some(arr => arr[0] === arrowStart && arr[1] === arrowEnd);
                                                    if (!arrowExists) {
                                                        setArrows([...arrows, [arrowStart, arrowEnd, arrowColor]]);
                                                    }
                                                }
                                                setArrowStart(null);
                                                setIsDrawingArrow(false);
                                                setIsPenMode(false);
                                                setArrowEnd(null);
                                            }
                                        }}
                                        onContextMenu={(e) => {
                                            // Prevent context menu for highlight mode (right-click highlights)
                                            if (drawingMode === 'highlight' && (mode === TEACHING_MODE || mode === PUZZLE_MODE)) {
                                                e.preventDefault();
                                            }
                                            // Prevent context menu when drawing arrows
                                            if (drawingMode === 'arrow' && (mode === TEACHING_MODE || mode === PUZZLE_MODE)) {
                                                e.preventDefault();
                                            }
                                        }}
                                    >
                                    <Chessboard
                                        position={game.fen()}
                                        onPieceDrop={mode === TEACHING_MODE ? onDropTeaching : onDrop}
                                            boardWidth={boardWidthPx}
                                            boardOrientation={boardOrientation}
                                            animationDuration={isNeoEditorUi ? 0 : 300}
                                            {...(isNeoTeachingBoard ? {
                                                showBoardNotation: true,
                                                customNotationStyle: {
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    color: LICHESS_TEXT_MUTED
                                                }
                                            } : {})}
                                            arePiecesDraggable={mode === TEACHING_MODE ? true : !(userRole === 'student' && shareMode === 'view')}
                                            isDraggablePiece={({ piece }) => mode === TEACHING_MODE ? true : !(userRole === 'student' && shareMode === 'view')}
                                        customArrows={(() => {
                                            // Stockfish best-move arrows (NEO local engine – up to 3 lines)
                                            const stockfishArrow: Array<[string,string,string]> = isNeoEditorUi && showNeoAnalysis
                                                ? sfEngine.lines
                                                    .filter((l) => l.from && l.to)
                                                    .map((l, i) => [l.from, l.to, i === 0 ? 'rgba(129,193,75,0.85)' : 'rgba(129,193,75,0.35)'] as [string,string,string])
                                                : (isNeoEditorUi && neoAnalysisBestMoveArrow
                                                    ? [[neoAnalysisBestMoveArrow[0], neoAnalysisBestMoveArrow[1], 'rgba(129,193,75,0.82)']]
                                                    : []);
                                            // Only show guide arrow while actively drawing, not the final arrows
                                            if (drawingMode === 'arrow' && isDrawingArrow && arrowStart && arrowEnd && arrowStart !== arrowEnd) {
                                                // Show only the guide arrow while drawing
                                                const arrowColor = {
                                                    'green': 'rgba(16, 185, 129, 0.8)',
                                                    'red': 'rgba(239, 68, 68, 0.8)',
                                                    'blue': 'rgba(59, 130, 246, 0.8)',
                                                    'yellow': 'rgba(234, 179, 8, 0.8)'
                                                }[drawingColor];
                                                return [[arrowStart, arrowEnd, arrowColor], ...hintArrows, ...stockfishArrow] as any;
                                            }
                                            // When not drawing, show final arrows + hints + stockfish best move
                                            return [...arrows, ...hintArrows, ...stockfishArrow] as any;
                                        })()}
                                        customSquareStyles={(() => {
                                            const styles: any = { ...rightClickedSquares };
                                            selectedSquares.forEach((square) => {
                                                if (!styles[square]) {
                                                    // Always use blue for highlights
                                                    styles[square] = { backgroundColor: 'rgba(59, 130, 246, 0.4)' };
                                                }
                                            });
                                            return styles;
                                        })()}
                                        onSquareRightClick={onSquareRightClick}
                                        onSquareClick={(square) => {
                                            // Don't handle clicks if we're actively drawing arrows (right-click drag in progress)
                                            // But allow clicks when arrow mode is selected but not actively drawing
                                            if (drawingMode === 'arrow' && isDrawingArrow) {
                                                return;
                                            }
                                            
                                            // Handle square clicks for piece placement/removal/selection in teaching/puzzle mode
                                            // Only allow piece placement in teaching mode, not puzzle mode
                                            if (mode === TEACHING_MODE) {
                                                const currentFen = game.fen();
                                                
                                                // Check if there's an existing piece on this square by reading the board directly
                                                let existingPiece: any = null;
                                                try {
                                                    const currentBoard = game.board();
                                                    const squareRank = 8 - parseInt(square[1]);
                                                    const squareFile = square.charCodeAt(0) - 97;
                                                    if (currentBoard[squareRank] && currentBoard[squareRank][squareFile]) {
                                                        existingPiece = currentBoard[squareRank][squareFile];
                                                    }
                                                } catch (e) {
                                                    // If board() fails, try using get()
                                                    try {
                                                        existingPiece = game.get(square as any);
                                                    } catch (e2) {
                                                        // No piece on this square
                                                        existingPiece = null;
                                                    }
                                                }
                                                
                                                if (selectedTrayPiece) {
                                                    // Lichess-style: Click piece from tray, then click square to place/replace
                                                    console.log('Placing piece:', selectedTrayPiece, 'on square:', square);
                                                    
                                                    const pieceType = selectedTrayPiece.piece.toLowerCase();
                                                        const pieceName = {
                                                            'k': 'King', 'q': 'Queen', 'r': 'Rook',
                                                            'b': 'Bishop', 'n': 'Knight', 'p': 'Pawn'
                                                        }[pieceType] || pieceType;
                                                        
                                                    // If validations are enforced, check if placement is valid
                                                    if (enforceValidations) {
                                                        try {
                                                            const testGame = new Chess(game.fen());
                                                            // Try to place the piece
                                                            testGame.put({ type: pieceType as any, color: selectedTrayPiece.color as any }, square as any);
                                                            // If successful, use it
                                                            setTeachingModeHistory([...teachingModeHistory, currentFen]);
                setTeachingModeRedoHistory([]); // Clear redo history when making a new move
                                                            setGame(testGame);
                                                        toast.success(`✅ Placed ${selectedTrayPiece.color === 'w' ? 'White' : 'Black'} ${pieceName} on ${square.toUpperCase()}`);
                                                        } catch (error: any) {
                                                            toast.error(`Invalid placement: ${error.message || 'Position violates chess rules'}`);
                                                            return;
                                                        }
                                                    } else {
                                                        // No validation: free placement
                                                        try {
                                                            setTeachingModeHistory([...teachingModeHistory, currentFen]);
                setTeachingModeRedoHistory([]); // Clear redo history when making a new move
                                                            const updatedGame = buildBoardState(game, square, {
                                                                type: pieceType,
                                                                color: selectedTrayPiece.color
                                                            });
                                                            setGame(updatedGame);
                                                            toast.success(`✅ Placed ${selectedTrayPiece.color === 'w' ? 'White' : 'Black'} ${pieceName} on ${square.toUpperCase()}`);
                                                    } catch (error: any) {
                                                        console.error('Error placing piece:', error, error.message);
                                                        toast.error(`Error: ${error.message || 'Could not place piece'}`);
                                                        }
                                                    }
                                                    
                                                    // Keep piece selected for multiple placements (Lichess behavior)
                                                    // User can click another square to place again, or click tray to deselect
                                                } else if (existingPiece && !selectedTrayPiece) {
                                                    // Lichess-style: Click on existing piece to remove it (only if not in arrow drawing mode)
                                                    // Allow removal even in arrow mode, but not while actively drawing
                                                    try {
                                                        setTeachingModeHistory([...teachingModeHistory, currentFen]);
                setTeachingModeRedoHistory([]); // Clear redo history when making a new move
                                                        
                                                        // Use manual board building to remove piece
                                                        const updatedGame = buildBoardState(game, square, null);
                                                        setGame(updatedGame);
                                                        
                                                        const pieceName = {
                                                            'k': 'King', 'q': 'Queen', 'r': 'Rook',
                                                            'b': 'Bishop', 'n': 'Knight', 'p': 'Pawn'
                                                        }[existingPiece.type] || existingPiece.type;
                                                        toast.info(`🗑️ Removed ${existingPiece.color === 'w' ? 'White' : 'Black'} ${pieceName} from ${square.toUpperCase()}`);
                                                    } catch (error: any) {
                                                        console.error('Error removing piece:', error);
                                                        toast.error('Could not remove piece');
                                                    }
                                                } else if (interactionMode === 'select' && existingPiece) {
                                                    // Select mode - highlight selected pieces
                                                    const newSelectedSquares = new Set(selectedSquares);
                                                    if (newSelectedSquares.has(square)) {
                                                        newSelectedSquares.delete(square);
                                                        setSelectedSquares(newSelectedSquares);
                                                        // Remove highlight
                                                        const newHighlights = { ...rightClickedSquares };
                                                        delete newHighlights[square];
                                                        setRightClickedSquares(newHighlights);
                                                        toast.info('Selection cleared');
                                                    } else {
                                                        newSelectedSquares.add(square);
                                                        setSelectedSquares(newSelectedSquares);
                                                        // Add highlight with blue color (always blue for highlights)
                                                        setRightClickedSquares({
                                                            ...rightClickedSquares,
                                                            [square]: { backgroundColor: 'rgba(59, 130, 246, 0.4)' }
                                                        });
                                                        const pieceName = {
                                                            'k': 'King', 'q': 'Queen', 'r': 'Rook',
                                                            'b': 'Bishop', 'n': 'Knight', 'p': 'Pawn'
                                                        }[existingPiece.type] || existingPiece.type;
                                                        toast.success(`✓ Selected ${existingPiece.color === 'w' ? 'White' : 'Black'} ${pieceName} on ${square.toUpperCase()}`);
                                                    }
                                                } else if (interactionMode === 'delete' && existingPiece) {
                                                    // Delete mode - remove piece
                                                    try {
                                                        setTeachingModeHistory([...teachingModeHistory, currentFen]);
                setTeachingModeRedoHistory([]); // Clear redo history when making a new move
                                                        // Use buildBoardState to remove piece
                                                        const updatedGame = buildBoardState(game, square, null);
                                                        setGame(updatedGame);
                                                        // Also remove from selected squares if it was selected
                                                        const newSelectedSquares = new Set(selectedSquares);
                                                        newSelectedSquares.delete(square);
                                                        setSelectedSquares(newSelectedSquares);
                                                        const newHighlights = { ...rightClickedSquares };
                                                        delete newHighlights[square];
                                                        setRightClickedSquares(newHighlights);
                                                        const pieceName = {
                                                            'k': 'King', 'q': 'Queen', 'r': 'Rook',
                                                            'b': 'Bishop', 'n': 'Knight', 'p': 'Pawn'
                                                        }[existingPiece.type] || existingPiece.type;
                                                        toast.info(`🗑️ Removed ${existingPiece.color === 'w' ? 'White' : 'Black'} ${pieceName} from ${square.toUpperCase()}`);
                                                    } catch (error: any) {
                                                        console.error('Error removing piece:', error);
                                                        toast.error('Could not remove piece');
                                                    }
                                                } else if (interactionMode === 'move') {
                                                    // Move mode - do nothing on click, dragging handles moves
                                                    // This is handled by onPieceDrop
                                                }
                                            }
                                        }}
                                customBoardStyle={
                                    isNeoEditorUi
                                        ? {
                                              borderRadius: 2,
                                              boxShadow: 'none',
                                              border: `1px solid ${LICHESS_PANEL_BORDER}`
                                          }
                                        : {
                                              borderRadius: '12px',
                                              boxShadow:
                                                  '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                                          }
                                }
                                customDarkSquareStyle={{
                                    backgroundColor: isNeoEditorUi
                                        ? LICHESS_BOARD_DARK
                                        : mode === TEACHING_MODE
                                          ? '#769656'
                                          : '#b58863'
                                }}
                                customLightSquareStyle={{
                                    backgroundColor: isNeoEditorUi
                                        ? LICHESS_BOARD_LIGHT
                                        : mode === TEACHING_MODE
                                          ? '#eeeed2'
                                          : '#f0d9b5'
                                }}
                                customDropSquareStyle={{
                                    boxShadow: isNeoEditorUi
                                        ? 'inset 0 0 1px 6px rgba(129, 193, 75, 0.5)'
                                        : 'inset 0 0 1px 6px rgba(59, 130, 246, 0.5)'
                                }}
                            />
                                </div>
                                    </div>
                                </div>
                                {isNeoEditorUi && (
                                    <div
                                        className="spare flex w-full shrink-0 justify-center px-0.5"
                                        style={{ maxWidth: boardWidthPx > 0 ? boardWidthPx : undefined }}
                                    >
                                        {lichessSpareRow(boardOrientation === 'white' ? 'w' : 'b')}
                                    </div>
                                )}
                                </div>
                            {isNeoEditorUi && (() => {
                                // Build PGN string from move log
                                const pgn = neoMoveLog.length === 0
                                    ? ''
                                    : Array.from(
                                        { length: Math.ceil(neoMoveLog.length / 2) },
                                        (_, i) => {
                                            const w = neoMoveLog[i * 2];
                                            const b = neoMoveLog[i * 2 + 1];
                                            return `${i + 1}. ${w}${b ? ' ' + b : ''}`;
                                        }
                                    ).join(' ');

                                return (
                                    <>
                                        {/* FEN + PGN inputs — match Lichess board-editor__inputs placement */}
                                        <div className="board-editor__inputs w-full flex flex-col items-center px-1 pt-2 pb-1 gap-2">
                                            <div
                                                className="w-full rounded-sm border overflow-hidden"
                                                style={{ maxWidth: boardWidthPx || undefined, borderColor: LICHESS_PANEL_BORDER }}
                                            >
                                                {/* ── FEN row ── */}
                                                <div
                                                    className="flex items-center gap-0 border-b"
                                                    style={{ borderColor: LICHESS_PANEL_BORDER }}
                                                >
                                                    <span
                                                        className="shrink-0 px-2 text-[10px] font-bold w-10 text-center select-none border-r"
                                                        style={{ color: LICHESS_TEXT_MUTED, borderColor: LICHESS_PANEL_BORDER, backgroundColor: LICHESS_PAGE_BG }}
                                                    >
                                                        FEN
                                                    </span>
                                                    <input
                                                        type="text"
                                                        spellCheck={false}
                                                        enterKeyHint="done"
                                                        className="flex-1 font-mono text-[11px] px-2 py-1.5 outline-none min-w-0"
                                                        style={{
                                                            background: LICHESS_PANEL_BG,
                                                            color: LICHESS_TEXT,
                                                        }}
                                                        value={neoFenField}
                                                        onChange={(e) => setNeoFenField(e.target.value)}
                                                        onBlur={() => {
                                                            try {
                                                                const g = new Chess(neoFenField.trim());
                                                                setTeachingModeHistory([...teachingModeHistory, game.fen()]);
                                                                setTeachingModeRedoHistory([]);
                                                                setGame(g);
                                                            } catch {
                                                                setNeoFenField(game.fen());
                                                                toast.error('Invalid FEN');
                                                            }
                                                        }}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="shrink-0 px-2 py-1.5 text-[10px] font-semibold border-l"
                                                        style={{
                                                            background: LICHESS_PAGE_BG,
                                                            color: LICHESS_ACCENT,
                                                            borderColor: LICHESS_PANEL_BORDER
                                                        }}
                                                        onClick={async () => {
                                                            try {
                                                                await navigator.clipboard.writeText(game.fen());
                                                                toast.success('FEN copied');
                                                            } catch {
                                                                toast.error('Copy failed');
                                                            }
                                                        }}
                                                        title="Copy FEN"
                                                    >
                                                        Copy
                                                    </button>
                                                </div>

                                                {/* ── PGN row ── */}
                                                <div className="flex items-stretch gap-0">
                                                    <span
                                                        className="shrink-0 px-2 text-[10px] font-bold w-10 text-center select-none border-r flex items-center justify-center"
                                                        style={{ color: LICHESS_TEXT_MUTED, borderColor: LICHESS_PANEL_BORDER, backgroundColor: LICHESS_PAGE_BG }}
                                                    >
                                                        PGN
                                                    </span>
                                                    <textarea
                                                        readOnly
                                                        rows={2}
                                                        spellCheck={false}
                                                        className="flex-1 font-mono text-[11px] px-2 py-1.5 outline-none resize-none min-w-0 leading-snug"
                                                        style={{
                                                            background: LICHESS_PANEL_BG,
                                                            color: pgn ? LICHESS_TEXT : LICHESS_TEXT_MUTED,
                                                        }}
                                                        value={pgn || 'No moves yet'}
                                                    />
                                                    <button
                                                        type="button"
                                                        disabled={!pgn}
                                                        className="shrink-0 px-2 text-[10px] font-semibold border-l disabled:opacity-40"
                                                        style={{
                                                            background: LICHESS_PAGE_BG,
                                                            color: LICHESS_ACCENT,
                                                            borderColor: LICHESS_PANEL_BORDER
                                                        }}
                                                        onClick={async () => {
                                                            try {
                                                                await navigator.clipboard.writeText(pgn);
                                                                toast.success('PGN copied');
                                                            } catch {
                                                                toast.error('Copy failed');
                                                            }
                                                        }}
                                                        title="Copy PGN"
                                                    >
                                                        Copy
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Screenshot link */}
                                            <a
                                                href={`https://lichess1.org/export/fen.gif?fen=${encodeURIComponent(game.fen())}&color=white&theme=brown&piece=cburnett`}
                                                download
                                                className="self-start text-xs font-semibold"
                                                style={{ color: LICHESS_ACCENT }}
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                ↓ Screenshot
                                            </a>
                                        </div>
                                    </>
                                );
                            })()}
                            </>
                        )}
                            </div>
                                    </div>
                                    
                {/* Right Sidebar - Controls and Annotations */}
                <div
                    className={`flex-shrink-0 overflow-y-auto ${
                        isNeoEditorUi
                            ? 'w-full sm:w-[300px] border rounded-sm p-2'
                            : 'w-56 bg-white rounded-xl shadow-lg p-4'
                    }`}
                    style={
                        isNeoEditorUi
                            ? {
                                  backgroundColor: LICHESS_PANEL_BG,
                                  borderColor: LICHESS_PANEL_BORDER
                              }
                            : undefined
                    }
                >
                    <div className="space-y-4">
                        {/* ── NEO move list (sidebar) ── */}
                        {isNeoEditorUi && (() => {
                            const pgn = neoMoveLog.length === 0
                                ? ''
                                : Array.from(
                                    { length: Math.ceil(neoMoveLog.length / 2) },
                                    (_, i) => {
                                        const w = neoMoveLog[i * 2];
                                        const b = neoMoveLog[i * 2 + 1];
                                        return `${i + 1}. ${w}${b ? ' ' + b : ''}`;
                                    }
                                ).join(' ');

                            return (
                                <div
                                    className="rounded-sm border overflow-hidden"
                                    style={{ borderColor: LICHESS_PANEL_BORDER }}
                                >
                                    {/* header */}
                                    <div
                                        className="flex items-center justify-between px-2 py-1 text-[10px] border-b"
                                        style={{ backgroundColor: LICHESS_PAGE_BG, borderColor: LICHESS_PANEL_BORDER }}
                                    >
                                        <span className="font-semibold text-[11px]" style={{ color: LICHESS_TEXT }}>Moves</span>
                                        <div className="flex items-center gap-2">
                                            {pgn && (
                                                <button
                                                    type="button"
                                                    onClick={async () => {
                                                        try {
                                                            await navigator.clipboard.writeText(pgn);
                                                            toast.success('PGN copied');
                                                        } catch {
                                                            toast.error('Copy failed');
                                                        }
                                                    }}
                                                    className="text-[9px] font-semibold"
                                                    style={{ color: LICHESS_ACCENT }}
                                                    title="Copy PGN"
                                                >
                                                    Copy PGN
                                                </button>
                                            )}
                                            {neoMoveLog.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => { setNeoMoveLog([]); setNeoMoveUndoneLog([]); }}
                                                    className="text-[9px]"
                                                    style={{ color: LICHESS_TEXT_MUTED }}
                                                >
                                                    clear
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* move table — each row: move-num | white | black */}
                                    {neoMoveLog.length === 0 ? (
                                        <div className="px-2 py-2 text-[10px] italic" style={{ color: LICHESS_TEXT_MUTED, backgroundColor: LICHESS_PAGE_BG }}>
                                            No moves yet
                                        </div>
                                    ) : (
                                        <div
                                            className="max-h-48 overflow-y-auto"
                                            style={{ backgroundColor: LICHESS_PAGE_BG }}
                                        >
                                            <table className="w-full border-collapse text-[11px] font-mono">
                                                <tbody>
                                                    {Array.from({ length: Math.ceil(neoMoveLog.length / 2) }, (_, i) => {
                                                        const white = neoMoveLog[i * 2];
                                                        const black = neoMoveLog[i * 2 + 1];
                                                        const isLastRow = i === Math.ceil(neoMoveLog.length / 2) - 1;
                                                        return (
                                                            <tr
                                                                key={i}
                                                                style={{
                                                                    backgroundColor: i % 2 === 0
                                                                        ? 'transparent'
                                                                        : 'rgba(255,255,255,0.03)'
                                                                }}
                                                            >
                                                                <td
                                                                    className="pl-2 pr-1 py-0.5 select-none text-[9px] text-right w-6"
                                                                    style={{ color: LICHESS_TEXT_MUTED }}
                                                                >
                                                                    {i + 1}.
                                                                </td>
                                                                <td
                                                                    className="px-1 py-0.5 w-1/2"
                                                                    style={{
                                                                        color: LICHESS_TEXT,
                                                                        fontWeight: isLastRow && !black ? 600 : undefined,
                                                                        backgroundColor: isLastRow && !black
                                                                            ? 'rgba(129,193,75,0.18)'
                                                                            : undefined,
                                                                    }}
                                                                >
                                                                    {white}
                                                                </td>
                                                                <td
                                                                    className="px-1 py-0.5 w-1/2"
                                                                    style={{
                                                                        color: LICHESS_TEXT,
                                                                        fontWeight: isLastRow && black ? 600 : undefined,
                                                                        backgroundColor: isLastRow && black
                                                                            ? 'rgba(129,193,75,0.18)'
                                                                            : undefined,
                                                                    }}
                                                                >
                                                                    {black ?? ''}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* Puzzle List - Show for all users in puzzle mode, but hide if initialPuzzle is provided (modal mode) */}
                        {mode === PUZZLE_MODE && !initialPuzzle && (
                            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-4 border-2 border-purple-200">
                                <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                                    <span>📚</span>
                                    Puzzles ({loadingPuzzles ? '...' : filteredPuzzles.length})
                                    {loadingPuzzles && (
                                        <span className="ml-auto">
                                            <svg className="animate-spin h-4 w-4 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                        </span>
                                    )}
                                </h3>
                                
                                {/* Search and Filters */}
                                <div className="mb-3 space-y-2">
                                    <input
                                        type="text"
                                        placeholder="Search puzzles..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        disabled={loadingPuzzles}
                                        className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                    />
                                    <div className="flex gap-2">
                                        <select
                                            value={selectedComplexity}
                                            onChange={(e) => setSelectedComplexity(e.target.value as any)}
                                            disabled={loadingPuzzles}
                                            className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        >
                                            <option value="all">All Levels</option>
                                            <option value="beginner">Beginner</option>
                                            <option value="intermediate">Intermediate</option>
                                            <option value="advanced">Advanced</option>
                                        </select>
                                        <select
                                            value={selectedCategory}
                                            onChange={(e) => setSelectedCategory(e.target.value as any)}
                                            disabled={loadingPuzzles}
                                            className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                                        >
                                            <option value="all">All Categories</option>
                                            <option value="tactics">Tactics</option>
                                            <option value="endgame">Endgame</option>
                                            <option value="opening">Opening</option>
                                            <option value="middlegame">Middlegame</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Active Collaborative Sessions */}
                                {Object.keys(activeSessions).length > 0 && (
                                    <div className="mb-4 p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border-2 border-purple-200">
                                        <h4 className="text-xs font-bold text-gray-900 mb-2 flex items-center gap-2">
                                            <span>🤝</span>
                                            Active Collaborative Sessions ({Object.keys(activeSessions).length})
                                        </h4>
                                        <div className="space-y-2 max-h-32 overflow-y-auto">
                                            {Object.values(activeSessions).map((session) => {
                                                const participantCount = Object.values(session.participants || {}).filter(p => p.isActive).length;
                                                const isParticipant = session.participants?.[loggedInUserId]?.isActive;
                                                return (
                                                    <div
                                                        key={session.id}
                                                        className="p-2 bg-white rounded border border-purple-200 flex items-center justify-between gap-2"
                                                    >
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-[10px] font-semibold text-gray-900 truncate">
                                                                {session.puzzle.description || 'Untitled Puzzle'}
                                                            </p>
                                                            <p className="text-[9px] text-gray-600">
                                                                {participantCount} {participantCount === 1 ? 'participant' : 'participants'} • Started by {session.createdByName}
                                                            </p>
                                                        </div>
                                                        {isParticipant ? (
                                                            <button
                                                                onClick={async (e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    setCollaborativeSessionId(session.id);
                                                                    setCurrentPuzzle(session.puzzle);
                                                                    setShowCollaborativeView(true);
                                                                }}
                                                                className="px-2 py-1 text-[10px] rounded bg-purple-500 text-white hover:bg-purple-600 font-medium"
                                                            >
                                                                Rejoin
                                                            </button>
                                                        ) : (
                                                            <button
                                                                onClick={async (e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    try {
                                                                        await joinCollaborativeSession(
                                                                            session.id,
                                                                            activeSubscriptionId,
                                                                            loggedInUserId,
                                                                            loggedInUserName,
                                                                            userRole || 'student'
                                                                        );
                                                                        setCollaborativeSessionId(session.id);
                                                                        setCurrentPuzzle(session.puzzle);
                                                                        setShowCollaborativeView(true);
                                                                        toast.success('Joined collaborative session!');
                                                                    } catch (error) {
                                                                        console.error('Error joining session:', error);
                                                                        toast.error('Failed to join session');
                                                                    }
                                                                }}
                                                                className="px-2 py-1 text-[10px] rounded bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600 font-medium"
                                                            >
                                                                Join
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Puzzle List */}
                                {loadingPuzzles ? (
                                    <div className="text-center py-8">
                                        <div className="flex flex-col items-center gap-3">
                                            <svg className="animate-spin h-8 w-8 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            <p className="text-xs text-gray-600 font-medium">Loading puzzles from class puzzles...</p>
                                            <p className="text-[10px] text-gray-500">This may take a few seconds</p>
                                        </div>
                                    </div>
                                ) : filteredPuzzles.length === 0 ? (
                                    <div className="text-center py-4 text-gray-500 text-xs">
                                        <p className="mb-2">No puzzles found in class puzzles.</p>
                                        <p className="text-yellow-600 font-semibold">Add puzzles in Chequered to share with students.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-2 max-h-96 overflow-y-auto">
                                        {filteredPuzzles.map((puzzle) => {
                                            const isTaught = (puzzle as any).isTaught || false;
                                            return (
                                                <div
                                                    key={puzzle.id}
                                                    className={`w-full p-3 rounded-lg border-2 transition-all ${
                                                        currentPuzzle?.id === puzzle.id
                                                            ? 'border-purple-500 bg-purple-50 shadow-md'
                                                            : isTaught
                                                            ? 'border-blue-300 bg-blue-50'
                                                            : 'border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50'
                                                    }`}
                                                >
                                                    <div className="flex items-start justify-between gap-2">
                                                        <button
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                // Always load the clicked puzzle
                                                                // Ensure we pass the correct puzzle object from the map closure
                                                                loadPuzzle(puzzle);
                                                            }}
                                                            className="flex-1 min-w-0 text-left"
                                                        >
                                                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                                <p className="text-xs font-semibold text-gray-900 break-words flex-1 min-w-0">
                                                                    {puzzle.description || 'Untitled Puzzle'}
                                                                </p>
                                                                {isTaught && (
                                                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium flex-shrink-0">
                                                                        ✓ Taught
                                                                    </span>
                                                                )}
                                                                {currentPuzzle?.id === puzzle.id && (
                                                                    <span className="text-purple-600 text-xs flex-shrink-0">●</span>
                                                                )}
                                                            </div>
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                                                    puzzle.complexity === 'beginner' ? 'bg-green-100 text-green-700' :
                                                                    puzzle.complexity === 'intermediate' ? 'bg-yellow-100 text-yellow-700' :
                                                                    'bg-red-100 text-red-700'
                                                                }`}>
                                                                    {puzzle.complexity || 'intermediate'}
                                                                </span>
                                                                <span className="text-[10px] text-gray-600">
                                                                    Rating: {puzzle.rating}
                                                                </span>
                                                                {puzzle.category && (
                                                                    <span className="text-[10px] text-gray-500">
                                                                        {puzzle.category}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </button>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleMarkAsTaught(puzzle, isTaught);
                                                            }}
                                                            className={`ml-2 px-2 py-1 text-[10px] rounded transition-colors ${
                                                                isTaught
                                                                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                            }`}
                                                            title={isTaught ? 'Mark as not taught' : 'Mark as taught'}
                                                        >
                                                            {isTaught ? '✓' : '○'}
                                                        </button>
                                                    </div>
                                                    <div className="mt-2 flex gap-1">
                                                        <button
                                                            onClick={async (e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                try {
                                                                    const sessionId = await createCollaborativeSession(
                                                                        puzzle,
                                                                        activeSubscriptionId,
                                                                        loggedInUserId,
                                                                        loggedInUserName,
                                                                        'teacher' // Always teacher when clicking from class puzzles
                                                                    );
                                                                    // Navigate to collaborative session page with role=teacher
                                                                    window.location.href = `/chequered/collaborative/${sessionId}?role=teacher`;
                                                                    toast.success('Collaborative session started! Others can join now.');
                                                                } catch (error) {
                                                                    console.error('Error creating collaborative session:', error);
                                                                    toast.error('Failed to start collaborative session');
                                                                }
                                                            }}
                                                            className="flex-1 px-2 py-1.5 text-[10px] rounded transition-colors bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600 font-medium"
                                                            title="Start collaborative solving session"
                                                        >
                                                            🤝 Collaborative Solve
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Puzzle Progress - Compact */}
                        {mode === PUZZLE_MODE && currentPuzzle && (
                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                    <span>📊</span>
                                    Progress
                                </h3>
                                {(() => {
                                    const totalMoves = currentPuzzle.moves.split(' ').length;
                                    const completedMoves = puzzleProgress.length;
                                    if (totalMoves > 1) {
                                        return (
                                    <div className="space-y-2">
                                                <div className="flex items-center justify-between text-xs">
                                                    <span className="text-gray-600">Moves</span>
                                                    <span className="font-semibold text-gray-900">{completedMoves}/{totalMoves}</span>
                                    </div>
                                                <div className="flex items-center gap-1">
                                                    {Array.from({ length: totalMoves }, (_, i) => (
                                                        <div
                                                            key={i}
                                                            className={`flex-1 h-1.5 rounded-full ${
                                                                i < completedMoves
                                                                    ? 'bg-emerald-500'
                                                                    : i === completedMoves
                                                                    ? 'bg-yellow-400'
                                                                    : 'bg-gray-200'
                                                            }`}
                                                        />
                                        ))}
                                    </div>
                                </div>
                                        );
                                    }
                                    return <p className="text-xs text-gray-500">Single move puzzle</p>;
                                })()}
                            </div>
                        )}

                        {/* Position tools (NEO) — Lichess editor–style panel */}
                        {mode === TEACHING_MODE && isNeoTeachingBoard && (
                            <div
                                className="rounded-sm p-3 border space-y-3"
                                style={{ backgroundColor: LICHESS_PAGE_BG, borderColor: LICHESS_PANEL_BORDER }}
                            >
                                <h3
                                    className="text-xs font-bold uppercase tracking-wider mb-2"
                                    style={{ color: LICHESS_TEXT_MUTED }}
                                >
                                    Board editor
                                </h3>

                                {/* lila `controls()` metadata — FEN/URL live under the board in copyables */}
                                <div
                                    className="rounded-sm p-3 mb-3 space-y-3"
                                    style={{ background: LICHESS_PANEL_BG, border: `1px solid ${LICHESS_PANEL_BORDER}` }}
                                >
                                    <div className="color">
                                        <select
                                            className="w-full text-sm rounded-sm px-2 py-2 border cursor-pointer"
                                            style={{
                                                background: LICHESS_PAGE_BG,
                                                borderColor: LICHESS_PANEL_BORDER,
                                                color: LICHESS_TEXT
                                            }}
                                            value={game.fen().split(/\s+/)[1] === 'b' ? 'black' : 'white'}
                                            onChange={(e) => {
                                                const v = e.target.value === 'black' ? 'b' : 'w';
                                                try {
                                                    const nextFen = withFenSideToMove(game.fen(), v);
                                                    const g = new Chess(nextFen);
                                                    setTeachingModeHistory([...teachingModeHistory, game.fen()]);
                                                    setTeachingModeRedoHistory([]);
                                                    setGame(g);
                                                } catch {
                                                    toast.error('Could not change side to move');
                                                }
                                            }}
                                        >
                                            <option value="white">White to play</option>
                                            <option value="black">Black to play</option>
                                        </select>
                                    </div>

                                    <div className="castling space-y-2">
                                        <strong className="text-sm" style={{ color: LICHESS_TEXT }}>
                                            Castling rights
                                        </strong>
                                        {/* 2-column grid: K-side (O-O) | Q-side (O-O-O) */}
                                        <div className="grid grid-cols-[auto_1fr_1fr] gap-x-3 gap-y-1 text-[11px] items-center" style={{ color: LICHESS_TEXT }}>
                                            {/* header row */}
                                            <span />
                                            <span className="text-center font-semibold opacity-70">O-O</span>
                                            <span className="text-center font-semibold opacity-70">O-O-O</span>
                                            {/* White row */}
                                            <span className="flex items-center gap-1 whitespace-nowrap font-medium">
                                                <span style={{ fontSize: '1rem' }}>♔</span> White
                                            </span>
                                            {(['K', 'Q'] as const).map((key) => {
                                                const flags = parseCastlingFlags(game.fen());
                                                const on = flags[key];
                                                return (
                                                    <label key={key} className="flex justify-center cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={on}
                                                            title={`White ${key === 'K' ? 'kingside (O-O)' : 'queenside (O-O-O)'} castling`}
                                                            onChange={() => {
                                                                try {
                                                                    const nextFen = withFenCastling(game.fen(), { ...flags, [key]: !on });
                                                                    const g = new Chess(nextFen);
                                                                    setTeachingModeHistory([...teachingModeHistory, game.fen()]);
                                                                    setTeachingModeRedoHistory([]);
                                                                    setGame(g);
                                                                } catch {
                                                                    toast.error('Invalid castling rights');
                                                                }
                                                            }}
                                                            className="w-4 h-4 rounded cursor-pointer"
                                                            style={{ accentColor: LICHESS_ACCENT }}
                                                        />
                                                    </label>
                                                );
                                            })}
                                            {/* Black row */}
                                            <span className="flex items-center gap-1 whitespace-nowrap font-medium">
                                                <span style={{ fontSize: '1rem' }}>♚</span> Black
                                            </span>
                                            {(['k', 'q'] as const).map((key) => {
                                                const flags = parseCastlingFlags(game.fen());
                                                const on = flags[key];
                                                return (
                                                    <label key={key} className="flex justify-center cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={on}
                                                            title={`Black ${key === 'k' ? 'kingside (O-O)' : 'queenside (O-O-O)'} castling`}
                                                            onChange={() => {
                                                                try {
                                                                    const nextFen = withFenCastling(game.fen(), { ...flags, [key]: !on });
                                                                    const g = new Chess(nextFen);
                                                                    setTeachingModeHistory([...teachingModeHistory, game.fen()]);
                                                                    setTeachingModeRedoHistory([]);
                                                                    setGame(g);
                                                                } catch {
                                                                    toast.error('Invalid castling rights');
                                                                }
                                                            }}
                                                            className="w-4 h-4 rounded cursor-pointer"
                                                            style={{ accentColor: LICHESS_ACCENT }}
                                                        />
                                                    </label>
                                                );
                                            })}
                                        </div>
                                        <p className="text-[10px] opacity-50 leading-snug" style={{ color: LICHESS_TEXT }}>
                                            O-O = kingside&nbsp;&nbsp;·&nbsp;&nbsp;O-O-O = queenside
                                        </p>
                                    </div>

                                    <div
                                        className="enpassant flex justify-between items-center gap-2 flex-wrap"
                                        style={{ color: LICHESS_TEXT }}
                                    >
                                        <label className="font-bold text-sm" htmlFor="neo-ep-select">
                                            En passant
                                        </label>
                                        <select
                                            id="neo-ep-select"
                                            className="text-sm rounded-sm px-2 py-1 border cursor-pointer"
                                            style={{
                                                width: '9ch',
                                                background: LICHESS_PAGE_BG,
                                                borderColor: LICHESS_PANEL_BORDER,
                                                color: LICHESS_TEXT
                                            }}
                                            value={(() => {
                                                const ep = game.fen().split(/\s+/)[3] || '-';
                                                return ep === '-' ? '' : ep;
                                            })()}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                try {
                                                    const nextFen = withFenEnPassant(game.fen(), v);
                                                    const g = new Chess(nextFen);
                                                    setTeachingModeHistory([...teachingModeHistory, game.fen()]);
                                                    setTeachingModeRedoHistory([]);
                                                    setGame(g);
                                                } catch {
                                                    toast.error('Invalid en passant square');
                                                }
                                            }}
                                        >
                                            {enPassantFileOptions(
                                                game.fen().split(/\s+/)[1] === 'b' ? 'b' : 'w'
                                            ).map((sq) => (
                                                <option key={sq || '-'} value={sq}>
                                                    {sq || '-'}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div>
                                        <label className="text-xs font-semibold block mb-1" style={{ color: LICHESS_TEXT_MUTED }}>
                                            Variant
                                        </label>
                                        <select
                                            className="w-full text-sm rounded-sm px-2 py-2 border"
                                            style={{
                                                background: LICHESS_PAGE_BG,
                                                borderColor: LICHESS_PANEL_BORDER,
                                                color: LICHESS_TEXT
                                            }}
                                            disabled
                                            value="standard"
                                        >
                                            <option value="standard">Variant | Standard</option>
                                        </select>
                                    </div>
                                </div>

                                <label
                                    className="flex items-start gap-2 cursor-pointer border-t pt-3"
                                    style={{ borderColor: LICHESS_PANEL_BORDER }}
                                >
                                    <input
                                        type="checkbox"
                                        checked={enforceValidations}
                                        onChange={(e) => setEnforceValidations(e.target.checked)}
                                        className="mt-0.5 rounded border"
                                        style={{ borderColor: LICHESS_PANEL_BORDER }}
                                    />
                                    <span className="text-[11px] leading-snug" style={{ color: LICHESS_TEXT }}>
                                        <span className="font-semibold">Enforce legal position</span>
                                        <span style={{ color: LICHESS_TEXT_MUTED }}>
                                            {' '}
                                            — when off, free placement like the Lichess editor
                                        </span>
                                    </span>
                                </label>

                                <div className="flex flex-col gap-2 border-t pt-3" style={{ borderColor: LICHESS_PANEL_BORDER }}>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            try {
                                                const initialGame = new Chess();
                                                setTeachingModeHistory([...teachingModeHistory, game.fen()]);
                                                setTeachingModeRedoHistory([]);
                                                setGame(initialGame);
                                                setSelectedTrayPiece(null);
                                                setArrows([]);
                                                setRightClickedSquares({});
                                                toast.success('Starting position');
                                            } catch (error) {
                                                console.error(error);
                                                toast.error('Failed to load');
                                            }
                                        }}
                                        className="w-full py-2 rounded-sm text-xs font-semibold"
                                        style={{ backgroundColor: LICHESS_ACCENT, color: '#262421' }}
                                    >
                                        Starting position
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            try {
                                                let clearedGame: Chess;
                                                try {
                                                    clearedGame = new Chess('8/8/8/8/8/8/8/8 w - - 0 1');
                                                } catch {
                                                    clearedGame = new Chess('k7/8/8/8/8/8/8/7K w - - 0 1');
                                                    const allSquares = [
                                                        'a1', 'b1', 'c1', 'd1', 'e1', 'f1', 'g1', 'h1',
                                                        'a2', 'b2', 'c2', 'd2', 'e2', 'f2', 'g2', 'h2',
                                                        'a3', 'b3', 'c3', 'd3', 'e3', 'f3', 'g3', 'h3',
                                                        'a4', 'b4', 'c4', 'd4', 'e4', 'f4', 'g4', 'h4',
                                                        'a5', 'b5', 'c5', 'd5', 'e5', 'f5', 'g5', 'h5',
                                                        'a6', 'b6', 'c6', 'd6', 'e6', 'f6', 'g6', 'h6',
                                                        'a7', 'b7', 'c7', 'd7', 'e7', 'f7', 'g7', 'h7',
                                                        'a8', 'b8', 'c8', 'd8', 'e8', 'f8', 'g8', 'h8'
                                                    ];
                                                    let g = clearedGame;
                                                    allSquares.forEach((sq) => {
                                                        g = buildBoardState(g, sq, null);
                                                    });
                                                    clearedGame = g;
                                                }
                                                setTeachingModeHistory([...teachingModeHistory, game.fen()]);
                                                setTeachingModeRedoHistory([]);
                                                setGame(clearedGame);
                                                setSelectedTrayPiece(null);
                                                setArrows([]);
                                                setRightClickedSquares({});
                                                toast.success('Board cleared');
                                            } catch (error) {
                                                console.error('Error clearing board:', error);
                                                toast.error('Failed to clear board');
                                            }
                                        }}
                                        className="w-full py-2 rounded-sm text-xs font-semibold border"
                                        style={{
                                            backgroundColor: LICHESS_PANEL_BG,
                                            color: LICHESS_TEXT,
                                            borderColor: LICHESS_PANEL_BORDER
                                        }}
                                    >
                                        Clear board
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setBoardOrientation(boardOrientation === 'white' ? 'black' : 'white')
                                        }
                                        className="w-full py-2 rounded-sm text-xs font-semibold text-left px-2 border flex items-center gap-2"
                                        style={{
                                            backgroundColor: LICHESS_PANEL_BG,
                                            color: LICHESS_TEXT,
                                            borderColor: LICHESS_PANEL_BORDER
                                        }}
                                    >
                                        <span aria-hidden>↻</span> Flip board
                                    </button>
                                    {/* ── Stockfish engine toggle ── */}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (showNeoAnalysis) {
                                                sfEngine.stopAnalysis();
                                                setShowNeoAnalysis(false);
                                            } else {
                                                setShowNeoAnalysis(true);
                                                sfEngine.startAnalysis(game.fen());
                                            }
                                        }}
                                        className="w-full py-2 rounded-sm text-xs font-semibold text-left px-2 border flex items-center gap-2"
                                        style={{
                                            backgroundColor: showNeoAnalysis ? LICHESS_ACCENT : LICHESS_PANEL_BG,
                                            color: showNeoAnalysis ? '#262421' : LICHESS_TEXT,
                                            borderColor: LICHESS_PANEL_BORDER
                                        }}
                                    >
                                        <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                        {showNeoAnalysis ? 'Engine ON' : 'Analysis board'}
                                        {sfEngine.analyzing && (
                                            <span className="ml-auto flex items-center gap-1 text-[10px]" style={{ color: showNeoAnalysis ? '#262421' : LICHESS_TEXT_MUTED }}>
                                                <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                                                </svg>
                                                d{sfEngine.depth}
                                            </span>
                                        )}
                                        {!sfEngine.analyzing && showNeoAnalysis && sfEngine.depth > 0 && (
                                            <span className="ml-auto text-[10px]" style={{ color: '#262421' }}>d{sfEngine.depth} ✓</span>
                                        )}
                                    </button>

                                    {/* ── Inline Stockfish panel (Lichess-style) ── */}
                                    {showNeoAnalysis && (
                                        <div
                                            className="rounded-sm border space-y-0 overflow-hidden"
                                            style={{ backgroundColor: LICHESS_PAGE_BG, borderColor: LICHESS_PANEL_BORDER }}
                                        >
                                            {/* Header: engine info + controls */}
                                            <div
                                                className="flex items-center justify-between px-2 py-1.5 text-[10px] border-b"
                                                style={{ borderColor: LICHESS_PANEL_BORDER, color: LICHESS_TEXT_MUTED }}
                                            >
                                                <span className="font-semibold" style={{ color: LICHESS_TEXT }}>Stockfish 10</span>
                                                <div className="flex items-center gap-2">
                                                    {/* Lines selector */}
                                                    <span>Lines</span>
                                                    {[1, 2, 3].map((n) => (
                                                        <button
                                                            key={n}
                                                            type="button"
                                                            onClick={() => {
                                                                sfEngine.setMultiPV(n);
                                                                sfEngine.startAnalysis(game.fen());
                                                            }}
                                                            className="w-5 h-5 rounded-sm text-[10px] font-bold border"
                                                            style={{
                                                                backgroundColor: sfEngine.multiPV === n ? LICHESS_ACCENT : LICHESS_PANEL_BG,
                                                                color: sfEngine.multiPV === n ? '#262421' : LICHESS_TEXT,
                                                                borderColor: LICHESS_PANEL_BORDER
                                                            }}
                                                        >
                                                            {n}
                                                        </button>
                                                    ))}
                                                    {/* Threads */}
                                                    <span className="ml-1">CPU</span>
                                                    {[1, 2, 4].filter(t => t <= navigator.hardwareConcurrency).map((t) => (
                                                        <button
                                                            key={t}
                                                            type="button"
                                                            onClick={() => sfEngine.setThreads(t)}
                                                            className="w-5 h-5 rounded-sm text-[10px] font-bold border"
                                                            style={{
                                                                backgroundColor: sfEngine.threads === t ? LICHESS_ACCENT : LICHESS_PANEL_BG,
                                                                color: sfEngine.threads === t ? '#262421' : LICHESS_TEXT,
                                                                borderColor: LICHESS_PANEL_BORDER
                                                            }}
                                                        >
                                                            {t}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Waiting for engine to boot */}
                                            {!sfEngine.ready && (
                                                <div className="px-2 py-2 text-[10px] flex items-center gap-2" style={{ color: LICHESS_TEXT_MUTED }}>
                                                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                                                    </svg>
                                                    Loading engine…
                                                </div>
                                            )}

                                            {/* Principal variation lines */}
                                            {sfEngine.ready && sfEngine.lines.length > 0 && (() => {
                                                const topLine = sfEngine.lines[0];
                                                const ev = topLine.isMate
                                                    ? (topLine.mateIn! > 0 ? 999 : -999)
                                                    : topLine.evaluation;
                                                const capped = Math.max(-6, Math.min(6, ev));
                                                const whitePct = Math.round((capped + 6) / 12 * 100);
                                                const evalLabel = topLine.isMate
                                                    ? (topLine.mateIn! > 0 ? `M${topLine.mateIn}` : `−M${Math.abs(topLine.mateIn!)}`)
                                                    : (ev === 0 ? '0.00' : ev > 0 ? `+${ev.toFixed(2)}` : ev.toFixed(2));

                                                return (
                                                    <>
                                                        {/* Eval bar + score */}
                                                        <div className="px-2 pt-2 pb-1 space-y-1">
                                                            <div className="flex items-center gap-2">
                                                                <span
                                                                    className="font-mono font-bold text-sm w-14 shrink-0 text-right"
                                                                    style={{ color: ev >= 0 ? '#d4e8c2' : '#ef9a9a' }}
                                                                >
                                                                    {evalLabel}
                                                                </span>
                                                                <div className="flex-1 h-3 rounded-sm overflow-hidden flex" style={{ border: `1px solid ${LICHESS_PANEL_BORDER}` }}>
                                                                    <div style={{ width: `${whitePct}%`, background: '#f0d9b5', transition: 'width 0.3s ease' }} />
                                                                    <div style={{ flex: 1, background: '#b58863' }} />
                                                                </div>
                                                                <span className="text-[9px] w-8 text-right shrink-0" style={{ color: LICHESS_TEXT_MUTED }}>
                                                                    {sfEngine.analyzing ? (
                                                                        <span className="animate-pulse">d{sfEngine.depth}</span>
                                                                    ) : (
                                                                        `d${sfEngine.depth}`
                                                                    )}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* PV lines */}
                                                        <div className="divide-y" style={{ borderColor: LICHESS_PANEL_BORDER }}>
                                                            {sfEngine.lines.map((line, idx) => {
                                                                const lineEv = line.isMate
                                                                    ? (line.mateIn! > 0 ? `M${line.mateIn}` : `−M${Math.abs(line.mateIn!)}`)
                                                                    : (line.evaluation === 0 ? '0.00' : line.evaluation > 0 ? `+${line.evaluation.toFixed(2)}` : line.evaluation.toFixed(2));
                                                                return (
                                                                    <div
                                                                        key={line.multipv}
                                                                        className="flex items-start gap-2 px-2 py-1.5"
                                                                        style={{ backgroundColor: idx === 0 ? 'rgba(129,193,75,0.07)' : 'transparent' }}
                                                                    >
                                                                        <span
                                                                            className="font-mono font-bold text-[11px] w-12 shrink-0 pt-px"
                                                                            style={{ color: idx === 0 ? 'rgba(129,193,75,0.9)' : LICHESS_TEXT_MUTED }}
                                                                        >
                                                                            {lineEv}
                                                                        </span>
                                                                        <div className="flex flex-wrap gap-x-1 gap-y-0.5 min-w-0">
                                                                            {line.pvSan.map((san, i) => (
                                                                                <span
                                                                                    key={i}
                                                                                    className="font-mono text-[10px]"
                                                                                    style={{ color: i === 0 ? LICHESS_TEXT : LICHESS_TEXT_MUTED }}
                                                                                >
                                                                                    {san}
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>

                                                        {/* Re-analyse on new position */}
                                                        <div className="px-2 py-1.5 flex gap-1 border-t" style={{ borderColor: LICHESS_PANEL_BORDER }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => sfEngine.startAnalysis(game.fen())}
                                                                className="flex-1 py-1 rounded-sm text-[10px] font-semibold border"
                                                                style={{ backgroundColor: LICHESS_PANEL_BG, color: LICHESS_TEXT, borderColor: LICHESS_PANEL_BORDER }}
                                                            >
                                                                Re-analyse
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => { sfEngine.stopAnalysis(); setShowNeoAnalysis(false); }}
                                                                className="flex-1 py-1 rounded-sm text-[10px] font-semibold border"
                                                                style={{ backgroundColor: LICHESS_PANEL_BG, color: LICHESS_TEXT, borderColor: LICHESS_PANEL_BORDER }}
                                                            >
                                                                Close
                                                            </button>
                                                        </div>
                                                    </>
                                                );
                                            })()}

                                            {/* No lines yet but engine is running */}
                                            {sfEngine.ready && sfEngine.analyzing && sfEngine.lines.length === 0 && (
                                                <div className="px-2 py-2 text-[10px] flex items-center gap-2" style={{ color: LICHESS_TEXT_MUTED }}>
                                                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                                                    </svg>
                                                    Analysing…
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            disabled={teachingModeHistory.length === 0}
                                            onClick={undoTeachingMove}
                                            className="flex-1 py-2 rounded-sm text-[11px] font-semibold border disabled:opacity-40"
                                            style={{
                                                backgroundColor: LICHESS_PANEL_BG,
                                                color: LICHESS_TEXT,
                                                borderColor: LICHESS_PANEL_BORDER
                                            }}
                                        >
                                            Undo
                                        </button>
                                        <button
                                            type="button"
                                            disabled={teachingModeRedoHistory.length === 0}
                                            onClick={redoTeachingMove}
                                            className="flex-1 py-2 rounded-sm text-[11px] font-semibold border disabled:opacity-40"
                                            style={{
                                                backgroundColor: LICHESS_PANEL_BG,
                                                color: LICHESS_TEXT,
                                                borderColor: LICHESS_PANEL_BORDER
                                            }}
                                        >
                                            Redo
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Piece Tray - Teaching Mode Only (standard layout; NEO uses spare row under board) */}
                        {mode === TEACHING_MODE && !isNeoTeachingBoard && (
                            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 border-2 border-blue-200">
                                <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                                    <span>🎨</span>
                                    Piece Tray
                                </h3>
                                
                                {/* Validation Checkbox */}
                                <div className="mb-3 p-2 bg-white rounded-lg border border-gray-200">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enforceValidations}
                                            onChange={(e) => setEnforceValidations(e.target.checked)}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-xs font-semibold text-gray-700">
                                            Enforce Validations
                                        </span>
                                    </label>
                                    <p className="text-xs text-gray-500 mt-1 ml-6">
                                        {enforceValidations 
                                            ? 'Chess rules enforced (no multiple kings, etc.)' 
                                            : 'Free placement mode (allows any position)'}
                                    </p>
                                </div>
                                
                                <p className="text-xs text-gray-600 mb-3">Drag piece to board or click piece → click square</p>
                                
                                {selectedTrayPiece && (
                                    <div className="mb-3 p-2 bg-green-100 border border-green-300 rounded-lg">
                                        <p className="text-xs font-semibold text-green-800 mb-1">
                                            Selected: {selectedTrayPiece.color === 'w' ? '⚪' : '⚫'} {
                                                {'K': 'King', 'Q': 'Queen', 'R': 'Rook', 'B': 'Bishop', 'N': 'Knight', 'P': 'Pawn'}[selectedTrayPiece.piece]
                                            }
                                        </p>
                                        <button
                                            onClick={() => setSelectedTrayPiece(null)}
                                            className="text-xs px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded"
                                        >
                                            ✖ Deselect
                                        </button>
                                    </div>
                                )}
                                
                                {/* White Pieces */}
                                <div className="mb-3 bg-white rounded-lg p-2 border border-gray-200">
                                    <p className="text-xs font-bold text-gray-700 mb-2 text-center">⚪ White</p>
                                    <div className="grid grid-cols-3 gap-1.5">
                                        {[
                                            { piece: 'K', symbol: '♔', name: 'King' },
                                            { piece: 'Q', symbol: '♕', name: 'Queen' },
                                            { piece: 'R', symbol: '♖', name: 'Rook' },
                                            { piece: 'B', symbol: '♗', name: 'Bishop' },
                                            { piece: 'N', symbol: '♘', name: 'Knight' },
                                            { piece: 'P', symbol: '♙', name: 'Pawn' }
                                        ].map(({ piece, symbol, name }) => (
                                            <div
                                                key={`w-${piece}`}
                                                draggable
                                                onDragStart={(e) => {
                                                    (window as any).draggedPieceFromTray = `w${piece}`;
                                                    e.dataTransfer.setData('piece', `w${piece}`);
                                                    e.dataTransfer.effectAllowed = 'copy';
                                                }}
                                                onDragEnd={() => {
                                                    (window as any).draggedPieceFromTray = null;
                                                }}
                                                onClick={() => {
                                                    // Lichess behavior: Click same piece to deselect, different piece to select
                                                    if (selectedTrayPiece?.piece === piece && selectedTrayPiece?.color === 'w') {
                                                        setSelectedTrayPiece(null);
                                                        toast.info(`Deselected White ${name}`);
                                                    } else {
                                                    setSelectedTrayPiece({ piece, color: 'w' });
                                                        toast.success(`Selected White ${name} - Click board to place`);
                                                    }
                                                }}
                                                className={`w-10 h-10 flex items-center justify-center border-2 rounded text-2xl transition-all cursor-move ${
                                                    selectedTrayPiece?.piece === piece && selectedTrayPiece?.color === 'w'
                                                        ? 'border-green-500 ring-2 ring-green-400 bg-green-50'
                                                        : 'border-gray-300 hover:border-blue-500 bg-white'
                                                }`}
                                                title={`Drag ${name} to board or click to select`}
                                            >
                                                {symbol}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Black Pieces */}
                                <div className="mb-3 bg-gray-800 rounded-lg p-2 border border-gray-600">
                                    <p className="text-xs font-bold text-white mb-2 text-center">⚫ Black</p>
                                    <div className="grid grid-cols-3 gap-1.5">
                                        {[
                                            { piece: 'K', symbol: '♚', name: 'King' },
                                            { piece: 'Q', symbol: '♛', name: 'Queen' },
                                            { piece: 'R', symbol: '♜', name: 'Rook' },
                                            { piece: 'B', symbol: '♝', name: 'Bishop' },
                                            { piece: 'N', symbol: '♞', name: 'Knight' },
                                            { piece: 'P', symbol: '♟', name: 'Pawn' }
                                        ].map(({ piece, symbol, name }) => (
                                            <div
                                                key={`b-${piece}`}
                                                draggable
                                                onDragStart={(e) => {
                                                    (window as any).draggedPieceFromTray = `b${piece}`;
                                                    e.dataTransfer.setData('piece', `b${piece}`);
                                                    e.dataTransfer.effectAllowed = 'copy';
                                                }}
                                                onDragEnd={() => {
                                                    (window as any).draggedPieceFromTray = null;
                                                }}
                                                onClick={() => {
                                                    // Lichess behavior: Click same piece to deselect, different piece to select
                                                    if (selectedTrayPiece?.piece === piece && selectedTrayPiece?.color === 'b') {
                                                        setSelectedTrayPiece(null);
                                                        toast.info(`Deselected Black ${name}`);
                                                    } else {
                                                    setSelectedTrayPiece({ piece, color: 'b' });
                                                        toast.success(`Selected Black ${name} - Click board to place`);
                                                    }
                                                }}
                                                className={`w-10 h-10 flex items-center justify-center border-2 rounded text-2xl transition-all cursor-move ${
                                                    selectedTrayPiece?.piece === piece && selectedTrayPiece?.color === 'b'
                                                        ? 'border-yellow-400 ring-2 ring-yellow-300 bg-yellow-50'
                                                        : 'border-gray-400 hover:border-yellow-400 bg-white'
                                                }`}
                                                title={`Drag ${name} to board or click to select`}
                                            >
                                                {symbol}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                
                                {/* Board Control Buttons */}
                                <div className="space-y-2">
                                    {/* Initial Position Button */}
                                <button
                                    onClick={() => {
                                        try {
                                                // Set board to standard starting position
                                                const initialGame = new Chess();
                                                setGame(initialGame);
                                            setTeachingModeHistory([]);
                                                setSelectedTrayPiece(null); // Clear any selected piece
                                                setArrows([]); // Clear any arrows
                                                setRightClickedSquares({}); // Clear any highlights
                                                toast.success('Initial position loaded!');
                                        } catch (error) {
                                                console.error('Error loading initial position:', error);
                                                toast.error('Failed to load initial position');
                                            }
                                        }}
                                        className="w-full px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-2"
                                    >
                                        ♟️ Initial Position
                                    </button>
                                    
                                    {/* Clear Board Button */}
                                    <button
                                        onClick={() => {
                                            try {
                                                // Create a truly empty board by using buildBoardState with empty board array
                                                // Start with a minimal game and use buildBoardState to create empty board
                                                const emptyBoardArray: (any | null)[][] = Array(8).fill(null).map(() => Array(8).fill(null));
                                                
                                                // Create a new game with empty FEN
                                                let clearedGame: Chess;
                                                try {
                                                    // Try to create with empty FEN
                                                    clearedGame = new Chess('8/8/8/8/8/8/8/8 w - - 0 1');
                                                } catch (e) {
                                                    // If empty FEN fails, create with minimal kings and remove all pieces
                                                    clearedGame = new Chess('k7/8/8/8/8/8/8/7K w - - 0 1');
                                                    // Remove all pieces using buildBoardState
                                                    const allSquares = ['a1', 'b1', 'c1', 'd1', 'e1', 'f1', 'g1', 'h1',
                                                                      'a2', 'b2', 'c2', 'd2', 'e2', 'f2', 'g2', 'h2',
                                                                      'a3', 'b3', 'c3', 'd3', 'e3', 'f3', 'g3', 'h3',
                                                                      'a4', 'b4', 'c4', 'd4', 'e4', 'f4', 'g4', 'h4',
                                                                      'a5', 'b5', 'c5', 'd5', 'e5', 'f5', 'g5', 'h5',
                                                                      'a6', 'b6', 'c6', 'd6', 'e6', 'f6', 'g6', 'h6',
                                                                      'a7', 'b7', 'c7', 'd7', 'e7', 'f7', 'g7', 'h7',
                                                                      'a8', 'b8', 'c8', 'd8', 'e8', 'f8', 'g8', 'h8'];
                                                    allSquares.forEach(sq => {
                                                        clearedGame = buildBoardState(clearedGame, sq, null);
                                                    });
                                                }
                                                
                                                setGame(clearedGame);
                                            setTeachingModeHistory([]);
                                                setSelectedTrayPiece(null); // Clear any selected piece
                                                setArrows([]); // Clear any arrows
                                                setRightClickedSquares({}); // Clear any highlights
                                            toast.success('Board cleared!');
                                            } catch (error) {
                                                console.error('Error clearing board:', error);
                                                toast.error('Failed to clear board');
                                        }
                                    }}
                                    className="w-full px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-2"
                                >
                                    🗑️ Clear Board
                                </button>
                                </div>
                            </div>
                        )}


                        {/* Annotations Controls - Right Side */}
                        {(mode === PUZZLE_MODE || mode === TEACHING_MODE) && !isNeoEditorUi && (
                            <div
                                className={isNeoEditorUi ? 'rounded-sm p-3 border' : 'bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-4 border-2 border-purple-200'}
                                style={
                                    isNeoEditorUi
                                        ? { backgroundColor: LICHESS_PAGE_BG, borderColor: LICHESS_PANEL_BORDER }
                                        : undefined
                                }
                            >
                                <h3
                                    className={`text-sm font-bold mb-3 flex items-center gap-2 ${isNeoEditorUi ? '' : 'text-gray-900'}`}
                                    style={isNeoEditorUi ? { color: LICHESS_TEXT } : undefined}
                                >
                                    <span>✏️</span>
                                    Annotations
                                </h3>
                                <div className="space-y-3">
                                    {mode === TEACHING_MODE && isNeoTeachingBoard && (
                                        <div>
                                            <p className="text-xs font-semibold text-gray-700 mb-2">Interaction</p>
                                            <div className="grid grid-cols-3 gap-2">
                                                <button
                                                    onClick={() => setInteractionMode('move')}
                                                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                                        interactionMode === 'move'
                                                            ? 'bg-slate-800 text-white shadow-md'
                                                            : 'bg-white text-gray-700 hover:bg-slate-50 border border-gray-200'
                                                    }`}
                                                >
                                                    ✋ Move
                                                </button>
                                                <button
                                                    onClick={() => setInteractionMode('select')}
                                                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                                        interactionMode === 'select'
                                                            ? 'bg-slate-800 text-white shadow-md'
                                                            : 'bg-white text-gray-700 hover:bg-slate-50 border border-gray-200'
                                                    }`}
                                                >
                                                    👀 Select
                                                </button>
                                                <button
                                                    onClick={() => setInteractionMode('delete')}
                                                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                                        interactionMode === 'delete'
                                                            ? 'bg-slate-800 text-white shadow-md'
                                                            : 'bg-white text-gray-700 hover:bg-slate-50 border border-gray-200'
                                                    }`}
                                                >
                                                    🗑️ Delete
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Mode Selection */}
                                    <div>
                                        <p className="text-xs font-semibold text-gray-700 mb-2">Mode</p>
                                        <div className={`gap-2 ${mode === TEACHING_MODE && isNeoTeachingBoard ? 'grid grid-cols-3' : 'grid grid-cols-2'}`}>
                                            <button
                                                onClick={() => {
                                                    setDrawingMode('highlight');
                                                    setArrowStart(null);
                                                    setIsDrawingArrow(false);
                                                }}
                                                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                                    drawingMode === 'highlight'
                                                        ? 'bg-purple-600 text-white shadow-md'
                                                        : 'bg-white text-gray-700 hover:bg-purple-100 border border-gray-200'
                                                }`}
                                            >
                                                ⬛ Highlight
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setDrawingMode('arrow');
                                                    setArrowStart(null);
                                                    setIsDrawingArrow(false);
                                                }}
                                                className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                                    drawingMode === 'arrow'
                                                        ? 'bg-purple-600 text-white shadow-md'
                                                        : 'bg-white text-gray-700 hover:bg-purple-100 border border-gray-200'
                                                }`}
                                                title="Right-click, hold and drag to draw arrows"
                                            >
                                                ➡️ Arrow
                                            </button>

                                            {mode === TEACHING_MODE && isNeoTeachingBoard && (
                                                <button
                                                    onClick={() => {
                                                        setDrawingMode('pen');
                                                        setArrowStart(null);
                                                        setIsDrawingArrow(false);
                                                    }}
                                                    className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                                        drawingMode === 'pen'
                                                            ? 'bg-purple-600 text-white shadow-md'
                                                            : 'bg-white text-gray-700 hover:bg-purple-100 border border-gray-200'
                                                    }`}
                                                    title="Left-click and drag to draw (pen)"
                                                >
                                                    🖊️ Pen
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Color selection (NEO only) */}
                                    {mode === TEACHING_MODE && isNeoTeachingBoard && (
                                        <div>
                                            <p className="text-xs font-semibold text-gray-700 mb-2">Color</p>
                                            <div className="grid grid-cols-4 gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setDrawingColor('green')}
                                                    className={`px-2 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                                                        drawingColor === 'green'
                                                            ? 'bg-green-600 text-white shadow-md border-transparent'
                                                            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200'
                                                    }`}
                                                >
                                                    🟢
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setDrawingColor('red')}
                                                    className={`px-2 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                                                        drawingColor === 'red'
                                                            ? 'bg-red-600 text-white shadow-md border-transparent'
                                                            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200'
                                                    }`}
                                                >
                                                    🔴
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setDrawingColor('blue')}
                                                    className={`px-2 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                                                        drawingColor === 'blue'
                                                            ? 'bg-blue-600 text-white shadow-md border-transparent'
                                                            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200'
                                                    }`}
                                                >
                                                    🔵
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setDrawingColor('yellow')}
                                                    className={`px-2 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                                                        drawingColor === 'yellow'
                                                            ? 'bg-yellow-500 text-gray-900 shadow-md border-transparent'
                                                            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-200'
                                                    }`}
                                                >
                                                    🟡
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Tool tips */}
                                    {mode === TEACHING_MODE && isNeoTeachingBoard ? (
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-2 min-h-[48px]">
                                            <p className="text-xs text-blue-800 font-medium">
                                                {drawingMode === 'pen' && '💡 Tip: Left-click and drag to draw.'}
                                                {drawingMode === 'arrow' && '💡 Tip: Right-click, hold and drag to draw arrows.'}
                                                {drawingMode === 'highlight' && '💡 Tip: Right-click squares to highlight.'}
                                            </p>
                                        </div>
                                    ) : (
                                        <div className={`bg-blue-50 border border-blue-200 rounded-lg p-2 mb-2 min-h-[48px] transition-opacity ${drawingMode === 'arrow' ? 'opacity-100' : 'opacity-0'}`}>
                                            <p className="text-xs text-blue-800 font-medium">
                                                💡 <strong>Tip:</strong> Right-click, hold and drag on the board to draw arrows in the selected color.
                                            </p>
                                        </div>
                                    )}

                                    
                                    {/* Clear Button */}
                                    {(Object.keys(rightClickedSquares).length > 0 || arrows.length > 0) && (
                                                <button
                                            onClick={clearAnnotations}
                                            className="w-full px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-2"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                            Clear All
                                                </button>
                                    )}
                                    </div>
                                </div>
                            )}
                        </div>
                        </div>
            </div>
        </div>

        {/* Help Guide Modal */}
        {showGuide && (
            <div 
                className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
                onClick={() => setShowGuide(false)}
            >
                <div 
                    className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 rounded-t-xl flex items-center justify-between">
                        <h2 className="text-2xl font-bold flex items-center gap-2">
                            📚 Chess Board Guide
                        </h2>
                        <button
                            onClick={() => setShowGuide(false)}
                            className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="p-6 space-y-6">
                        {/* Teaching Mode */}
                        <section className="pb-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                🎓 Teaching Mode
                            </h3>
                            <div className="space-y-4">
                                <div className="bg-blue-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-blue-900 mb-2">Moving Pieces</h4>
                                    <p className="text-gray-700 mb-2">
                                        <strong>Click, hold, and drag</strong> any piece to move it freely on the board. 
                                        Pieces can be moved anywhere - perfect for teaching positions and concepts!
                                    </p>
                                    <div className="flex items-center gap-2 text-sm text-blue-700">
                                        <span className="bg-blue-200 px-2 py-1 rounded">💡 Tip:</span>
                                        <span>Uncheck "Enforce Chess Rules" for completely free movement</span>
                                    </div>
                                </div>

                                <div className="bg-purple-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-purple-900 mb-2">Drawing Tools</h4>
                                    <ul className="space-y-2 text-gray-700">
                                        <li>
                                            <strong>🖊️ Pen Mode:</strong> Left-click and drag to draw arrows on the board
                                        </li>
                                        <li>
                                            <strong>➡️ Arrow Mode:</strong> Right-click and drag to draw arrows
                                        </li>
                                        <li>
                                            <strong>🟡 Highlight Mode:</strong> Right-click squares to highlight them in different colors
                                        </li>
                                        <li>
                                            <strong>🎨 Colors:</strong> Choose from Green, Red, Blue, or Yellow
                                        </li>
                                    </ul>
                                </div>

                                <div className="bg-green-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-green-900 mb-2">Piece Tray</h4>
                                    <p className="text-gray-700 mb-2">
                                        Drag pieces from the tray onto the board to set up custom positions. 
                                        Perfect for demonstrating specific scenarios!
                                    </p>
                                </div>
                            </div>
                        </section>

                        {/* Quick Tips */}
                        <section className="bg-gradient-to-r from-purple-100 to-blue-100 p-6 rounded-lg">
                            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                💡 Quick Tips
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white p-4 rounded-lg">
                                    <strong className="text-purple-700">Drawing & Annotations:</strong>
                                    <ul className="mt-2 space-y-1 text-sm text-gray-700">
                                        <li>• Right-click squares to highlight</li>
                                        <li>• Left-click drag (Pen) or right-click drag (Arrow) to draw</li>
                                        <li>• Use different colors for different concepts</li>
                                        <li>• Clear all annotations with one button</li>
                                    </ul>
                                </div>
                                <div className="bg-white p-4 rounded-lg">
                                    <strong className="text-blue-700">Board Controls:</strong>
                                    <ul className="mt-2 space-y-1 text-sm text-gray-700">
                                        <li>• Flip board to see from opponent's view</li>
                                        <li>• Use zoom controls for better visibility</li>
                                        <li>• Drag pieces freely in Teaching Mode</li>
                                        <li>• Use piece tray to set up custom positions</li>
                                    </ul>
                                </div>
                            </div>
                        </section>
                    </div>

                    <div className="sticky bottom-0 bg-gray-50 p-4 rounded-b-xl border-t flex justify-end">
                        <button
                            onClick={() => setShowGuide(false)}
                            className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700 transition-colors"
                        >
                            Got it!
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default ChequeredChess;
